import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import NavBar from '../components/navbar';
import { FaUser, FaGift } from 'react-icons/fa';
import Auth from './oauth';
import AuthModal from '../components/AuthModal';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUnpublishedChanges } from '../context/UnpublishedChangesContext';
import '../styles/enhanced-tabs.css';
import dataService from '../services/dataService';
import apiClient from '../services/apiClient';
import { ACCOUNT_TYPE_SEATS, FEATURE_FLAGS } from '../constants';
import { API_CONFIG } from '../config/config';
import taskContextData from '../data/TaskContext.json';
import TeamNotificationTooltip from '../components/TeamNotificationTooltip';



// ...existing code...
// import Billing from './Billing';

// ── Unpublished-changes diff helpers ─────────────────────────────────────────
const _localDateStr = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const _normalizeTask = (t) => ({
  task_id:        t.task_id || '',
  title:          (t.title || '').trim(),
  status:         t.status || 'notStarted',
  due:            _localDateStr(t.due),
  notes:          (t.notes || '').trim(),
  action_items:   JSON.stringify((t.action_items || []).filter(Boolean).map(s => String(s).trim()).sort()),
  item_type:      t.item_type || '',
  start_datetime: (t.start_datetime || t.start || '').slice(0, 16),
  end_datetime:   (t.end_datetime || t.end || '').slice(0, 16),
});
const _computeDirtyIds = (currentTasks, publishedTasks) => {
  // Only index tasks with a valid task_id — no-ID tasks collapse to key `undefined` in a Map,
  // causing phantom DELETED entries for every no-ID baseline task.
  const pubMap = new Map(
    (publishedTasks || []).filter(t => !!t.task_id).map(t => [t.task_id, t])
  );
  const curIds = new Set();
  const dirty = new Set();
  for (const t of (currentTasks || [])) {
    const id = t.task_id;
    if (!id) continue;
    curIds.add(id);
    const pub = pubMap.get(id);
    if (!pub || JSON.stringify(_normalizeTask(t)) !== JSON.stringify(_normalizeTask(pub))) dirty.add(id);
  }
  for (const id of pubMap.keys()) { if (!curIds.has(id)) dirty.add(id); }
  return dirty;
};

// ── Calendar modal helpers ────────────────────────────────────────────────────
const calIsSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const calIsSameMonth = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
const calAddDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
const calAddMonths = (date, n) => { const d = new Date(date); d.setMonth(d.getMonth() + n); return d; };
const calStartOfWeek = (date) => { const d = new Date(date); d.setDate(d.getDate() - d.getDay()); d.setHours(0,0,0,0); return d; };
const calGetMonthGrid = (year, month) => {
  const start = new Date(year, month, 1 - new Date(year, month, 1).getDay());
  return Array.from({ length: 42 }, (_, i) => calAddDays(start, i));
};
const calFormatMonthYear = (d) => d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const calFormatWeekRange = (date) => {
  const s = calStartOfWeek(date), e = calAddDays(s, 6);
  if (s.getMonth() === e.getMonth())
    return `${s.toLocaleDateString('en-US', { month: 'long' })} ${s.getDate()}–${e.getDate()}, ${s.getFullYear()}`;
  return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
};
const calFormatDayFull = (d) => d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
const CAL_DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const CAL_MINI_LABELS = ['S','M','T','W','T','F','S'];
const CAL_MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const CAL_HOURS = Array.from({ length: 24 }, (_, i) => i);
const CAL_PROPERTY_COLORS = [
  '#039be5','#e67c73','#33b679','#f6bf26','#8e24aa',
  '#f4511e','#0b8043','#3f51b5','#7986cb','#d50000','#795548','#616161',
];

const _etFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric', month: 'short', day: 'numeric',
  hour: 'numeric', minute: '2-digit', hour12: true,
});
const _parseCompactTs = (ts) => {
  const m = String(ts).match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z` : null;
};
const formatPublishedAt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const date = isNaN(d.getTime()) ? new Date(_parseCompactTs(iso)) : d;
  if (isNaN(date.getTime())) return null;
  try { return _etFormatter.format(date) + ' ET'; } catch { return null; }
};

const CalendarConnection = ({ onProfileClick, onBillingClick, onSeatsUpdate }) => {
  const { user, isMember, isAdmin, canManageUsersTeams, checkAndEnforcePaymentMethod, isImpersonating, impersonatedUser } = useAuth();
  const navigate = useNavigate();

  // Lead Agents with can_manage_teams are allowed on this page to view/edit their teams
  const isLeadAgentManager = !!(user?.can_manage_teams && user?.role === 'member');

  // The workspace this user actually manages/owns — for a delegated Admin this is the
  // Account Owner they're scoped to (owner_email), never the Admin's own email. Used to
  // scope Owner-level controls (e.g. deleting a team) to teams this specific user actually
  // owns/administers, rather than to every team card shown on this page (which can include
  // teams the user merely participates in as a Member/Lead Agent under another owner).
  const effectiveManagerEmail = ((isAdmin && canManageUsersTeams && user?.owner_email)
    ? user.owner_email
    : (user?.email || '')).toLowerCase();

  // Whether this user is the real Account Owner/Admin of a SPECIFIC team. Checked two ways:
  // (1) the team's `owner` object names them (per effectiveManagerEmail), which is the normal
  // case; or (2) for legacy connections that never had `owner` populated, their own membership
  // record on THIS team carries a primary-owner marker (role 'primary' or is_connection_owner).
  // Deliberately excludes role 'team_lead' from this fallback — a delegated Lead Agent must
  // never be treated as the Owner, even though they may independently own a separate workspace
  // elsewhere. This check is scoped entirely to the team's own stored data (its `owner` object
  // and the caller's own membership row), so removing some OTHER member from `team.members`
  // can never affect the result — ownership is never derived from another member's presence.
  const isRealOwnerOfTeam = (team) => {
    if (team?.owner && (team.owner.email || '').toLowerCase() === effectiveManagerEmail) return true;
    // effectiveManagerEmail (not the literal caller's own email) so a delegated Admin matches
    // against the Account Owner workspace they administer, not their own separate identity.
    const ownerMembership = (team?.members || []).find(m => (m.email || '').toLowerCase() === effectiveManagerEmail);
    return !!(ownerMembership && (ownerMembership.role === 'primary' || ownerMembership.is_connection_owner));
  };

  // Whether this user may open the Edit modal for a SPECIFIC team: either they own/administer
  // it (per isRealOwnerOfTeam above), or their own membership record on THIS team (not
  // their global user.can_manage_teams / user.role, which reflect only their own separate
  // workspace) grants them Lead Agent management rights. A plain Member of someone else's team
  // must never get this, even though the user's overall account may itself be an Owner.
  const canManageSpecificTeam = (team) => {
    if (isRealOwnerOfTeam(team)) return true;
    const myEmail = (user?.email || '').toLowerCase();
    const myMembership = (team?.members || []).find(m => (m.email || '').toLowerCase() === myEmail);
    return !!(myMembership && myMembership.can_manage_teams && myMembership.role === 'team_lead');
  };

  useEffect(() => {
    if (isMember && !isLeadAgentManager) navigate('/tasks', { replace: true });
  }, [isMember, isLeadAgentManager, navigate]);

  const canArchiveTaskLists   = user?.role === 'owner' || (isAdmin && canManageUsersTeams)
    || ['team_lead', 'solo_agent', 'single_agent', 'admin'].includes(user?.team_role);
  const canCreateTaskList     = user?.role === 'owner' || (isAdmin && canManageUsersTeams);
  const canManageCalendars    = user?.role === 'owner' || ['team_lead', 'solo_agent', 'single_agent', 'admin'].includes(user?.team_role);

  const [teams, setTeams] = useState([]);
  const [soloAgents, setSoloAgents] = useState([]);
  const [solos, setSolos] = useState([]);
  const [trials, setTrials] = useState([]);
  // Connections where the current user appears as a Lead Agent member under another owner
  const [memberLeadTeams, setMemberLeadTeams] = useState([]);
  const [activeTab, setActiveTab] = useState(null); // Start with null, will be set by useEffect
  
  const [hasSetInitialTab, setHasSetInitialTab] = useState(false);
  const [previousTab, setPreviousTab] = useState('teams'); // Track previous tab for restoration
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState(null);
  // Confirmation modals for deleting
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(''); // 'small_team' or 'solo'
  // Tasks modal state
  const [showTasksModal, setShowTasksModal] = useState(false);
  const [tasksModalTeam, setTasksModalTeam] = useState(null);
  // Archive/delete permissions re-evaluated per modal based on the open team's member list
  const tasksCanArchive = useMemo(() => {
    if (user?.role === 'owner') return true;
    if (isAdmin && canManageUsersTeams) return true;
    const email = user?.email?.toLowerCase();
    const m = (tasksModalTeam?.members || []).find(mb => mb.email?.toLowerCase() === email);
    if (!m) return false;
    const val = m.can_archive_task_lists ?? m.permissions?.can_archive_task_lists;
    if (val !== undefined) return Boolean(val);
    return m.role === 'team_lead';
  }, [user, tasksModalTeam, isAdmin, canManageUsersTeams]);
  const tasksCanDelete = useMemo(() => {
    if (user?.role === 'owner') return true;
    if (isAdmin && canManageUsersTeams) return true;
    const email = user?.email?.toLowerCase();
    const m = (tasksModalTeam?.members || []).find(mb => mb.email?.toLowerCase() === email);
    if (!m) return false;
    const val = m.can_delete_task_lists ?? m.permissions?.can_delete_task_lists;
    if (val !== undefined) return Boolean(val);
    return m.role === 'team_lead';
  }, [user, tasksModalTeam, isAdmin, canManageUsersTeams]);
  const [tasksModalData, setTasksModalData] = useState([]);
  const [tasksModalLoading, setTasksModalLoading] = useState(false);
  // Team-level unpublished changes modal (Actions column)
  const [teamUnpublishedModal, setTeamUnpublishedModal] = useState(null); // { team }
  const [teamUnpublishedRecords, setTeamUnpublishedRecords] = useState([]);
  const [teamUnpublishedLoading, setTeamUnpublishedLoading] = useState(false);
  const [teamUnpublishedCollapsed, setTeamUnpublishedCollapsed] = useState(new Set()); // collapsed group titles
  const [teamViewingTask, setTeamViewingTask] = useState(null); // { task, record }
  const [expandedTeamActionItems, setExpandedTeamActionItems] = useState(new Set());
  // Calendar modal state
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [calendarModalTeam, setCalendarModalTeam] = useState(null);
  const [calendarModalData, setCalendarModalData] = useState([]);
  const [calendarModalLoading, setCalendarModalLoading] = useState(false);
  const [calView, setCalView] = useState('month');
  const [calCurrentDate, setCalCurrentDate] = useState(new Date());
  const [calMiniDate, setCalMiniDate] = useState(new Date());
  const [calHiddenProperties, setCalHiddenProperties] = useState(new Set());
  const calTimeGridRef = useRef(null);
  const [editingTask, setEditingTask] = useState(null);
  // editingTask = { task, record, recordIdx, taskIdx }
  const editingTaskKeyRef = useRef(null);
  const [editTaskExpandedItems, setEditTaskExpandedItems] = useState(new Set());
  // Shared unpublished-changes tracking (synced across Tasks / Calendar / CalendarConnection)
  const { unpublishedChanges, setUnpublishedChanges, unpublishedTaskIds, setUnpublishedTaskIds } = useUnpublishedChanges();
  // Ref that always reflects the latest unpublishedChanges — used in event handlers to avoid stale closures
  const unpublishedChangesRef = useRef({});
  // Titles whose unpublished cards are currently playing the attention animation
  const [flashingTitles, setFlashingTitles] = useState(new Set());
  // DOM refs for unpublished task cards, keyed as `${title}:::${task_id}`
  const unpublishedCardRefs = useRef({});
  // In-app navigation warning: null | { onConfirm: () => void }
  const [unpublishedWarning, setUnpublishedWarning] = useState(null);
  const unpublishedInitRef = useRef(false);
  // Published snapshot per title: Task[] when known, null when unknown (pre-existing unpublished).
  const lastPublishedTasksRef = useRef({});
  const [publishingTitle, setPublishingTitle] = useState(null);
  const [publishModalRecord, setPublishModalRecord] = useState(null); // { record, eligibleUsers }
  const [publishSelectedEmails, setPublishSelectedEmails] = useState(new Set());
  // Archive / restore state
  const [tasksActiveTab, setTasksActiveTab] = useState('active'); // 'active' | 'archived'
  const [confirmArchiveRecord, setConfirmArchiveRecord] = useState(null);
  const [archivingTitle, setArchivingTitle] = useState(null);
  const [restoringTitle, setRestoringTitle] = useState(null);
  // Discard unpublished changes state
  const [confirmDiscardRecord, setConfirmDiscardRecord] = useState(null);
  const [discardingTitle, setDiscardingTitle] = useState(null);
  const [toast, setToast] = useState(null);
  // Acting user's personal calendar status — { enabled, connected, provider } or null while loading
  const [selfCalendarStatus, setSelfCalendarStatus] = useState(null);
  // Dedicated confirmation for closing the calendar modal when there are unpublished changes
  const [calendarCloseConfirm, setCalendarCloseConfirm] = useState(false);
  const [deletingTitle, setDeletingTitle] = useState(null);
  const [tasksCompletedOpenSet, setTasksCompletedOpenSet] = useState(new Set());
  const [hoveredCircle, setHoveredCircle] = useState(null);
  const [confirmDeleteRecord, setConfirmDeleteRecord] = useState(null);
  const [confirmRemoveTask, setConfirmRemoveTask] = useState(null); // { record, taskIdx, taskTitle }
  const [tasksView, setTasksView] = useState('allTime');
  const [tasksViewDate, setTasksViewDate] = useState(new Date());

  // Create-task-list modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createTitle, setCreateTitle]         = useState('');
  const [createAddress, setCreateAddress]     = useState('');
  const [isCreating, setIsCreating]           = useState(false);
  const [createError, setCreateError]         = useState('');

  // Ref lets the polling callback read current editing state without a stale closure
  const editingTaskRef = useRef(null);
  useEffect(() => { editingTaskRef.current = editingTask; }, [editingTask]);
  useEffect(() => { setExpandedTeamActionItems(new Set()); }, [teamViewingTask]);
  useEffect(() => {
    const key = editingTask
      ? `${editingTask.record?.task_list_title || ''}:${editingTask.taskIdx ?? 'new'}`
      : null;
    if (editingTaskKeyRef.current !== key) {
      editingTaskKeyRef.current = key;
      setEditTaskExpandedItems(new Set());
    }
  }, [editingTask]);
  const pickerOpenRef = useRef(false);
  const completedSectionRefs = useRef({});

  // Titles deleted this session — filter them out of every poll/reload to handle eventual consistency
  const deletedTitlesRef = useRef(new Set());
  // Titles archived this session — re-apply archive:true on every poll/reload until backend confirms
  const archivedTitlesRef = useRef(new Set());
  // Titles restored this session — re-apply archive:false on every poll so a stale poll result
  // cannot re-show the item in the Archived tab before DynamoDB catches up
  const restoredTitlesRef = useRef(new Set());

  const toDatetimeLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const p = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const toDateLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const p = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}`;
  };


  const handleCreateTaskList = async () => {
    const trimmed = createTitle.trim();
    if (!trimmed || isCreating) return;
    setIsCreating(true);
    setCreateError('');
    const result = await dataService.createTaskList(trimmed, createAddress.trim(), '', tasksModalTeam?.connection_id || '');
    setIsCreating(false);
    if (!result || result.error) {
      setCreateError(result?.error || 'Failed to create task list. Please try again.');
      return;
    }
    setTasksModalData(prev => [result, ...prev]);
    setTasksView('allTime');
    setShowCreateModal(false);
    setCreateTitle('');
    setCreateAddress('');
  };

  useEffect(() => {
    if (document.getElementById('unpublished-pop-anim')) return;
    const s = document.createElement('style');
    s.id = 'unpublished-pop-anim';
    s.textContent = '@keyframes unpublishedPop { 0%{transform:scale(1);} 40%{transform:scale(1.025);box-shadow:0 0 0 4px rgba(251,146,60,0.28),0 4px 16px rgba(251,146,60,0.18);} 100%{transform:scale(1);} }';
    document.head.appendChild(s);
  }, []);

  const handleFlashUnpublished = (title) => {
    const doFlash = () => {
      const cards = Object.entries(unpublishedCardRefs.current)
        .filter(([k]) => k.startsWith(`${title}:::`))
        .map(([, el]) => el)
        .filter(Boolean);
      if (cards.length > 0) cards[0].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setFlashingTitles(prev => new Set([...prev, title]));
      setTimeout(() => setFlashingTitles(prev => { const n = new Set(prev); n.delete(title); return n; }), 800);
    };
    const unpubIds = unpublishedTaskIds[title] || new Set();
    const record = tasksModalData.find(r => r.task_list_title === title);
    const hasCompletedUnpublished = (record?.tasks || []).some(
      t => unpubIds.has(t.task_id) && t.status === 'completed'
    );
    if (hasCompletedUnpublished) {
      setTasksCompletedOpenSet(prev => new Set([...prev, title]));
      setTimeout(doFlash, 50);
    } else {
      doFlash();
    }
  };

  const handleFlashCalUnpublished = (title) => {
    const doFlash = () => {
      const cards = Object.entries(unpublishedCardRefs.current)
        .filter(([k]) => k.startsWith(`${title}:::`))
        .map(([, el]) => el)
        .filter(Boolean);
      if (cards.length > 0) cards[0].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setFlashingTitles(prev => new Set([...prev, title]));
      setTimeout(() => setFlashingTitles(prev => { const n = new Set(prev); n.delete(title); return n; }), 800);
    };

    // Navigate to the first unpublished task's due date if it's out of the current view
    const unpubIds = unpublishedTaskIds[title];
    const record = calendarModalData.find(r => r.task_list_title === title);
    const firstDirtyTask = unpubIds && record
      ? (record.tasks || []).find(t => t.task_id && unpubIds.has(t.task_id) && t.due)
      : null;

    if (firstDirtyTask?.due) {
      setCalCurrentDate(new Date(firstDirtyTask.due));
      setTimeout(doFlash, 150);
    } else {
      doFlash();
    }
  };

  const handleAddTask = (record) => {
    const blankTask = { title: '', status: 'notStarted', due: null, action_items: [] };
    setEditingTask({ task: { ...blankTask }, originalTask: { ...blankTask }, record, recordIdx: null, taskIdx: null, isNewTask: true });
  };

  const handleRemoveTask = (record, taskIdx) => {
    const title = record.task_list_title;
    const newTasks = (record.tasks || []).filter((_, ti) => ti !== taskIdx);
    const updater = prev => prev.map(rec =>
      rec.task_list_title !== title ? rec : { ...rec, tasks: newTasks }
    );
    setTasksModalData(updater);
    setCalendarModalData(updater);
    const dirty = _computeDirtyIds(newTasks, lastPublishedTasksRef.current[title] ?? []);
    setUnpublishedTaskIds(prev => ({ ...prev, [title]: dirty }));
    setUnpublishedChanges(prev => dirty.size === 0
      ? (() => { const n = { ...prev }; delete n[title]; return n; })()
      : { ...prev, [title]: { count: dirty.size, lastEditedAt: new Date().toISOString() } }
    );
    dataService.replaceTasksDraft(title, newTasks, dirty.size > 0, dirty.size).then(ok => {
      if (!ok) console.warn('[CalendarConnection] Remove task draft save may have failed');
    });
  };

  const handleToggleComplete = useCallback((task, record, taskIdx) => {
    const title = record.task_list_title;
    if (deletedTitlesRef.current.has(title)) return;
    const newStatus = task.status === 'completed' ? 'notStarted' : 'completed';
    const stampedId = task.task_id || `tid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const updatedTask = {
      ...task,
      task_id: stampedId,
      status: newStatus,
      update_timestamp: new Date().toISOString(),
    };
    // If we just assigned a task_id, register it in the baseline so the diff can detect the change
    if (!task.task_id) {
      const base = lastPublishedTasksRef.current[title];
      if (Array.isArray(base) && taskIdx >= 0 && taskIdx < base.length) {
        lastPublishedTasksRef.current[title] = base.map((bt, bi) =>
          bi === taskIdx ? { ...bt, task_id: stampedId } : bt
        );
      }
    }
    const updatedTasks = (record.tasks || []).map((t, ti) => ti === taskIdx ? updatedTask : t);
    const updater = prev => prev.map(rec =>
      rec.task_list_title !== title ? rec : { ...rec, tasks: updatedTasks }
    );
    setTasksModalData(updater);
    setCalendarModalData(updater);
    const dirty = _computeDirtyIds(updatedTasks, lastPublishedTasksRef.current[title] ?? []);
    setUnpublishedTaskIds(prev => ({ ...prev, [title]: dirty }));
    setUnpublishedChanges(prev => dirty.size === 0
      ? (() => { const n = { ...prev }; delete n[title]; return n; })()
      : { ...prev, [title]: { count: dirty.size, lastEditedAt: new Date().toISOString() } }
    );
    dataService.updateTaskItemDraft(title, taskIdx, updatedTask, dirty.size > 0, dirty.size).then(ok => {
      if (!ok) console.warn('[CalendarConnection] Toggle completion draft save may have failed');
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTaskSave = () => {
    const { task, taskIdx, record, isCalendarModal, isNewTask, isNewEvent } = editingTask;
    const title = record.task_list_title;

    const isScheduled = isCalendarModal && task.item_type === 'scheduled_event';
    const savedTask = isCalendarModal
      ? {
          ...task,
          due: task.start || task.due,
          action_items: (task.action_items || []).filter(s => s.trim()),
          ...(isScheduled ? { start_datetime: task.start, end_datetime: task.end } : {}),
        }
      : task;
    const stampedTask = {
      ...savedTask,
      task_id: savedTask.task_id || `tid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      update_timestamp: new Date().toISOString(),
    };

    // Compute the full updated task list synchronously (React state not yet flushed)
    const currentRecord = tasksModalData.find(r => r.task_list_title === title);
    const updatedTasks = (isNewTask || isNewEvent)
      ? [...(currentRecord?.tasks || []), stampedTask]
      : (currentRecord?.tasks || []).map((t, ti) => ti === taskIdx ? stampedTask : t);

    setEditingTask(null);

    // When a previously no-ID task gets an ID assigned, update the baseline at the same index
    // so the diff can track it going forward instead of treating it as a perpetual NEW task.
    if (!isNewTask && !isNewEvent && !savedTask.task_id && stampedTask.task_id) {
      const base = lastPublishedTasksRef.current[title];
      if (Array.isArray(base) && taskIdx >= 0 && taskIdx < base.length) {
        lastPublishedTasksRef.current[title] = base.map((bt, bi) =>
          bi === taskIdx ? { ...bt, task_id: stampedTask.task_id } : bt
        );
      }
    }

    const dirty = _computeDirtyIds(updatedTasks, lastPublishedTasksRef.current[title] ?? []);
    setUnpublishedTaskIds(prev => ({ ...prev, [title]: dirty }));
    setUnpublishedChanges(prev => dirty.size === 0
      ? (() => { const n = { ...prev }; delete n[title]; return n; })()
      : { ...prev, [title]: { count: dirty.size, lastEditedAt: new Date().toISOString() } }
    );

    const updater = prev => prev.map(rec =>
      rec.task_list_title !== title ? rec : { ...rec, tasks: updatedTasks }
    );
    setTasksModalData(updater);
    if (isCalendarModal) setCalendarModalData(updater);
    if (isNewTask || isNewEvent) {
      dataService.replaceTasksDraft(title, updatedTasks, dirty.size > 0, dirty.size).then(ok => {
        if (!ok) console.warn('[CalendarConnection] New task draft save may have failed');
      });
    } else {
      dataService.updateTaskItemDraft(title, taskIdx, stampedTask, dirty.size > 0, dirty.size).then(ok => {
        if (!ok) console.warn('[CalendarConnection] Draft save to DynamoDB may have failed');
      });
    }
  };

  const handleAddCalEvent = (record, defaultDate = null) => {
    const base = defaultDate || new Date();
    const startISO = base.toISOString();
    const endISO = new Date(base.getTime() + 60 * 60 * 1000).toISOString();
    const blank = {
      title: '', start: startISO, end: endISO, due: startISO,
      all_day: false, location: '', description: '',
      status: 'notStarted', action_items: [],
      item_type: 'deadline',
      task_id: `tid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      update_timestamp: startISO,
    };
    setEditingTask({ task: { ...blank }, originalTask: { ...blank }, record, isNewEvent: true, isCalendarModal: true });
  };

  const openTeamUnpublishedModal = async (team) => {
    const memberEmails = (team.members || []).map(m => m.email).filter(Boolean);
    setTeamUnpublishedModal({ team });
    setTeamUnpublishedLoading(true);
    setTeamUnpublishedRecords([]);
    setTeamUnpublishedCollapsed(new Set());
    // During impersonation of a paid Account Owner, scope the API call to the impersonated
    // user's own account so we don't return the JWT caller's (actual owner's) data.
    const targetEmail = (isImpersonating && user?.role === 'owner') ? (user?.email ?? null) : null;
    try {
      const result = await dataService.getPendingChanges(targetEmail);
      if (result) {
        const scopedSet = new Set(memberEmails.map(e => e.toLowerCase()));
        const pending = (result.tasks ?? []).filter(
          r => r.has_unpublished_changes === true && r.active !== false && !r.archive
            && scopedSet.has((r.id || '').toLowerCase())
        );
        setTeamUnpublishedRecords(pending);
      }
    } catch (err) {
      console.error('[TeamUnpublished] load error:', err);
    } finally {
      setTeamUnpublishedLoading(false);
    }
  };

  const openPublishModal = (record) => {
    const allConnections = [...teams, ...solos, ...soloAgents, ...trials];
    const recordEmail = record.id?.toLowerCase();
    const currentEmail = user?.email?.toLowerCase();
    const userRole = user?.role;
    const userTeamRole = user?.team_role;
    const isAdminActingAsOwner = isAdmin && canManageUsersTeams;

    const connection =
      (record.connection_id && allConnections.find(conn => conn.connection_id === record.connection_id)) ||
      allConnections.find(conn =>
        (conn.members || []).some(m => m.email?.toLowerCase() === recordEmail)
      ) ||
      allConnections.find(conn => {
        const ownerEmailLower = (conn.owner?.email || '').toLowerCase();
        return ownerEmailLower === currentEmail ||
          (isAdminActingAsOwner && ownerEmailLower === (user?.owner_email || '').toLowerCase());
      }) ||
      (isAdminActingAsOwner
        ? allConnections.find(conn =>
            (conn.owner?.email || '').toLowerCase() === recordEmail
          ) || tasksModalTeam
        : null) ||
      allConnections.find(conn =>
        (conn.members || []).some(m => m.email?.toLowerCase() === currentEmail)
      ) ||
      tasksModalTeam;
    const allMembers = connection?.members?.length
      ? connection.members
      : [{ email: record.id, role: 'team_lead' }];
    const teamLead = allMembers.find(m => m.role === 'team_lead') || allMembers[0];

    let eligible;
    if (userRole === 'owner' || userTeamRole === 'team_lead' || isAdminActingAsOwner) {
      // Owner/Lead Agent/Admin-as-owner: owner entry + other members as selectable recipients.
      // For Admin, use the delegated owner's email (user.owner_email) so the owner's calendar is included.
      const adminOwnerEmail = isAdminActingAsOwner ? (user?.owner_email || '').toLowerCase() : null;
      const selfEntry = isAdminActingAsOwner
        ? (allMembers.find(m => m.email?.toLowerCase() === adminOwnerEmail) || { email: adminOwnerEmail, role: 'owner', first_name: user.owner_name, last_name: user.owner_last_name })
        : (allMembers.find(m => m.email?.toLowerCase() === currentEmail) || { email: currentEmail });
      const otherMembers = allMembers.filter(m => m.email?.toLowerCase() !== selfEntry.email?.toLowerCase());
      eligible = [selfEntry, ...otherMembers];
    } else {
      // Regular member: team lead is required (locked); self is publisher.
      const selfMemberObj = allMembers.find(m => m.email?.toLowerCase() === currentEmail) || { email: currentEmail };
      const lockedLead = teamLead && teamLead.email?.toLowerCase() !== currentEmail
        ? { ...teamLead, lockedInfo: true }
        : null;
      eligible = [selfMemberObj, ...(lockedLead ? [lockedLead] : [])];
    }
    if (!eligible.length) eligible = [{ email: record.id, role: 'team_lead' }];

    // Pre-select ALL entries — locked entries are forced-checked in the UI and must also be
    // included in the set so the backend's selected_recipients filter doesn't exclude them.
    const initialSelected = new Set(
      eligible.map(m => m.email?.toLowerCase()).filter(Boolean)
    );
    setPublishSelectedEmails(initialSelected);
    setPublishModalRecord({ record, eligibleUsers: eligible, isCurrentUserMember: userRole !== 'owner' && userTeamRole !== 'team_lead' && !isAdminActingAsOwner });
  };

  const handlePublishConfirm = async () => {
    if (!publishModalRecord) return;
    const { record } = publishModalRecord;
    const title = record.task_list_title;
    const currentRecord = tasksModalData.find(r => r.task_list_title === title)
      || calendarModalData.find(r => r.task_list_title === title);
    setPublishModalRecord(null);
    setPublishingTitle(title);

    // Push the editor's latest tasks to all team members' TaskContext records
    if (currentRecord?.tasks) {
      await dataService.propagateTasks(title, currentRecord.tasks);
    }

    // Publish to all selected recipients in a single backend call.
    // Admin users act on behalf of the owner — use the record's owner email so the
    // backend can locate the task context records (which are keyed to the owner, not the admin).
    const currentEmail = user?.email?.toLowerCase();
    const publishEmail = (isAdmin && canManageUsersTeams) ? (record.id?.toLowerCase() || currentEmail) : currentEmail;
    await dataService.publishTasks(publishEmail, title, record.connection_id || '', [...publishSelectedEmails]);

    // Clear unpublished badge and card highlights for this task list
    setUnpublishedChanges(prev => { const n = { ...prev }; delete n[title]; return n; });
    setUnpublishedTaskIds(prev => { const n = { ...prev }; delete n[title]; return n; });
    setFlashingTitles(prev => { const n = new Set(prev); n.delete(title); return n; });
    // Snapshot published state so future edits diff against what was just published
    lastPublishedTasksRef.current[title] = (currentRecord?.tasks || []).map(t => ({ ...t }));
    setPublishingTitle(null);
  };

  const handleDelete = (record) => {
    setConfirmDeleteRecord(record);
  };

  const handleConfirmDelete = async () => {
    const record = confirmDeleteRecord;
    setConfirmDeleteRecord(null);
    const title = record.task_list_title;
    setDeletingTitle(title);
    const ok = await dataService.deleteTaskList(record.id, title);
    setDeletingTitle(null);
    if (ok) {
      deletedTitlesRef.current.add(title);
      setTasksModalData(prev => prev.filter(r => !deletedTitlesRef.current.has(r.task_list_title)));
      setCalendarModalData(prev => prev.filter(r => !deletedTitlesRef.current.has(r.task_list_title)));
    } else console.warn('[CalendarConnection] Delete task list may have failed');
  };

  const showToast = useCallback((message) => {
    setToast({ message });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handleDiscard = (record) => setConfirmDiscardRecord(record);

  const handleConfirmDiscard = async () => {
    const record = confirmDiscardRecord;
    setConfirmDiscardRecord(null);
    const title = record.task_list_title;
    setDiscardingTitle(title);

    const publishedBase = lastPublishedTasksRef.current[title];
    const result = await dataService.discardDraft(
      title,
      (publishedBase !== null && publishedBase !== undefined) ? publishedBase : undefined,
    );

    if (!result.success) {
      setDiscardingTitle(null);
      showToast('Failed to discard changes. Please try again.');
      return;
    }

    const restoredTasks = result.tasks;
    lastPublishedTasksRef.current[title] = restoredTasks.map(t => ({ ...t }));
    const updater = prev => prev.map(rec =>
      rec.task_list_title !== title ? rec : { ...rec, tasks: restoredTasks }
    );
    setTasksModalData(updater);
    setCalendarModalData(updater);
    setUnpublishedChanges(prev => { const n = { ...prev }; delete n[title]; return n; });
    setUnpublishedTaskIds(prev => { const n = { ...prev }; delete n[title]; return n; });
    setFlashingTitles(prev => { const n = new Set(prev); n.delete(title); return n; });
    setDiscardingTitle(null);
    showToast('Unshared changes discarded.');
  };

  const handleArchive = (record) => setConfirmArchiveRecord(record);

  const handleConfirmArchive = async () => {
    const record = confirmArchiveRecord;
    setConfirmArchiveRecord(null);
    const title = record.task_list_title;
    setArchivingTitle(title);
    const ok = await dataService.archiveTaskList(record.id, title);
    setArchivingTitle(null);
    if (ok) {
      archivedTitlesRef.current.add(title);
      setTasksModalData(prev => prev.map(r =>
        r.task_list_title === title ? { ...r, archive: true } : r
      ));
      setCalendarModalData(prev => prev.map(r =>
        r.task_list_title === title ? { ...r, archive: true } : r
      ));
      showToast('Task list archived.');
    } else console.warn('[CalendarConnection] Archive task list may have failed');
  };

  const handleRestore = async (record) => {
    const title = record.task_list_title;
    setRestoringTitle(title);
    const ok = await dataService.restoreTaskList(record.id, title);
    setRestoringTitle(null);
    if (ok) {
      archivedTitlesRef.current.delete(title);
      restoredTitlesRef.current.add(title);
      setTasksModalData(prev => prev.map(r =>
        r.task_list_title === title ? { ...r, archive: false } : r
      ));
      setCalendarModalData(prev => prev.map(r =>
        r.task_list_title === title ? { ...r, archive: false } : r
      ));
      setTasksActiveTab('active');
      showToast('Task list restored to Active.');
      // Silent re-fetch to guarantee UI is in sync with the backend
      if (tasksModalTeam) {
        const emails = (tasksModalTeam.members || []).map(m => m.email).filter(Boolean);
        const cid = tasksModalTeam.connection_id || '';
        try {
          const fresh = await dataService.getTasksByMembers(emails, cid);
          if (fresh !== null) {
            setTasksModalData(fresh.tasks
              .filter(r => !cid || r.connection_id === cid)
              .filter(r => !deletedTitlesRef.current.has(r.task_list_title))
              .map(r => {
                if (archivedTitlesRef.current.has(r.task_list_title)) return { ...r, archive: true };
                if (restoredTitlesRef.current.has(r.task_list_title)) return { ...r, archive: false };
                return r;
              }));
          }
        } catch (e) {
          console.warn('[CalendarConnection] Silent re-fetch after restore failed:', e);
        }
      }
    } else console.warn('[CalendarConnection] Restore task list may have failed');
  };
  // Auth component state
  const [accountType, setAccountType] = useState("");
  const [members, setMembers] = useState([{
    email: '',
    first_name: '',
    last_name: '',
    ...(FEATURE_FLAGS.PHONE_FEATURE_ENABLED && {
      phone: '',
      displayPhone: ''
    })
  }]);
  const [provider, setProvider] = useState('google');
  // Phone slots state - only if phone feature is enabled
  const [, setUserPhoneSlots] = useState([]);
  const [, setTotalPhoneSlots] = useState(0);
  const [, setRegisteredPhoneNumbers] = useState(0);

  // Ref to Auth component for triggering phone data refresh
  const authRef = useRef();

  // Handle phone slots update callback from navbar - only if phone feature is enabled
  const handlePhoneSlotsUpdate = () => {
    if (!FEATURE_FLAGS.PHONE_FEATURE_ENABLED) return;
    
    console.log('[DEBUG] Phone slots updated, refreshing Auth component phone data...');
    if (authRef.current && authRef.current.refreshPhoneData) {
      authRef.current.refreshPhoneData();
    }
  };


  // Redirect to home if not logged in and check payment method for paid users
  useEffect(() => {
    if (!user) {
      navigate('/', { replace: true });
    } else {
      // Check payment method requirement for paid users
      checkAndEnforcePaymentMethod();
    }
  }, [user, navigate, checkAndEnforcePaymentMethod]);

  const effectiveAccountType = user?.role === 'admin' ? (user?.owner_account_type || user?.accountType) : user?.accountType;
  const normalizedAccountType = effectiveAccountType?.toLowerCase();

  // Extra team tabs derived from connections where this user is a Lead Agent member under another owner
  const memberLeadAgentTabs = useMemo(() => {
    return [...new Set(
      memberLeadTeams
        .filter(t => t.status !== 'suspended')
        .map(t => (t.team_type || t.account_type || '').toLowerCase())
        .filter(type => ['small_team', 'medium_team', 'large_team'].includes(type))
    )];
  }, [memberLeadTeams]);

  const getVisibleTabs = useMemo(() => {
    if (isLeadAgentManager) {
      // Derive tabs from the loaded teams — only show tabs for team sizes the Lead Agent actually leads.
      // Fall back to all three while data is loading (teams is still empty).
      const teamTypes = new Set(
        teams.map(t => (t.team_type || t.account_type || '').toLowerCase())
      );
      const leadTabs = ['large_team', 'medium_team', 'small_team'].filter(t => teamTypes.has(t));
      return leadTabs.length > 0 ? leadTabs : ['large_team', 'medium_team', 'small_team'];
    }
    if (!normalizedAccountType) {
      return ['teams', 'soloAgents', 'solos']; // Show all if no account type
    }
    if (['small_brokerage', 'medium_brokerage', 'large_brokerage', 'brokerage'].includes(normalizedAccountType)) {
      return ['large_team', 'medium_team', 'small_team', 'soloAgents', 'solos']; // Show all tabs for brokerage users
    }
    if (normalizedAccountType === 'solo') {
      return ['solos', ...memberLeadAgentTabs];
    }
    if (normalizedAccountType === 'trial') {
      return ['trial', ...memberLeadAgentTabs];
    }
    if (normalizedAccountType === 'solo_assistant') {
      return ['soloAgents', ...memberLeadAgentTabs];
    }
    if (normalizedAccountType === 'small_team') {
      // Also show other team-type tabs if user leads them as a member under another owner
      const extraTabs = memberLeadAgentTabs.filter(t => t !== 'small_team');
      return ['small_team', ...extraTabs];
    }
    if (normalizedAccountType === 'medium_team') {
      const extraTabs = memberLeadAgentTabs.filter(t => t !== 'medium_team');
      return ['medium_team', ...extraTabs];
    }
    if (normalizedAccountType === 'large_team') {
      const extraTabs = memberLeadAgentTabs.filter(t => t !== 'large_team');
      return ['large_team', ...extraTabs];
    }
    if (normalizedAccountType === 'super') {
      return ['large_team', 'medium_team', 'small_team', 'soloAgents', 'solos']; // Show all tabs for super users
    }
    // For any other/unknown type, show no tabs (but still show Lead Agent team tabs if any)
    return [...memberLeadAgentTabs];
  }, [normalizedAccountType, isLeadAgentManager, teams, memberLeadAgentTabs]);

  // --- FILTER HELPERS (must be above seat logic) ---
  const getFilteredTeams = () => {
    // For Lead Agent managers, loadData already pre-filtered teams to only those where
    // this user is the assigned team_lead. Skip the owner/accountType matching below.
    if (isLeadAgentManager) {
      return teams.map(team => ({
        ...team,
        _isOwnedByCurrentUser: isRealOwnerOfTeam(team),
        _canManageThisTeam: canManageSpecificTeam(team)
      }));
    }
    // A delegated Admin's own Users record carries whatever accountType they had before being
    // made Admin (often unrelated, e.g. 'admin' or empty) — the type that actually determines
    // which teams they administer is the delegated owner's, in owner_account_type.
    const scopedAccountType = (isAdmin && canManageUsersTeams && user?.owner_account_type)
      ? user.owner_account_type
      : user?.accountType;
    if (!user || !scopedAccountType) {
      return teams; // Show all if no account type
    }
    const normalized = scopedAccountType.toLowerCase().replace(/ /g, '_');
    console.log('[DEBUG] getFilteredTeams: user.email =', user.email);
    console.log('[DEBUG] getFilteredTeams: scopedAccountType =', scopedAccountType, 'normalized =', normalized);
    console.log('[DEBUG] getFilteredTeams: teams =', teams);
    
    // Debug each team structure
    teams.forEach((team, index) => {
      console.log(`[DEBUG] Team ${index}:`, {
        team_type: team.team_type,
        account_type: team.account_type,
        connection_type: team.connection_type,
        owner: team.owner,
        members: team.members?.length || 0,
        is_migrated: team.is_migrated,
        original_type: team.original_type,
        upgrade_date: team.upgrade_date
      });
    });
    
    // Brokerage owners (and Admins delegated into a brokerage workspace) manage teams of every
    // size, same as 'super' — they must route into the owner-managed branch below instead of
    // falling through to the unfiltered `default` case, which never sets _isOwnedByCurrentUser /
    // _canManageThisTeam and so hides Edit/Remove for every team regardless of any member change.
    const isBrokerageType = ['small_brokerage', 'medium_brokerage', 'large_brokerage', 'brokerage'].includes(normalized);

    switch (normalized) {
      case 'small_team':
      case 'medium_team':
      case 'large_team':
      case 'super':
      case 'small_brokerage':
      case 'medium_brokerage':
      case 'large_brokerage':
      case 'brokerage':
        // Only show teams where the user is the owner
        const filteredTeams = teams.filter(team => {
          // Exclude suspended connections
          if (team.status === 'suspended') {
            console.log(`[DEBUG] Excluding suspended team connection: ${team.team_name || 'Unnamed'}`);
            return false;
          }
          
          // Compared against effectiveManagerEmail (not the raw logged-in user.email) so a
          // delegated Admin — whose own email never appears as `owner` — still matches against
          // the Account Owner workspace they administer.
          const hasOwner = team.owner && (team.owner.email || '').toLowerCase() === effectiveManagerEmail;

          // Fallback: Check if the managed workspace's owner is in members array with owner/admin role
          let isMemberOwner = false;
          if (!hasOwner && team.members) {
            isMemberOwner = team.members.some(member =>
              (member.email || '').toLowerCase() === effectiveManagerEmail &&
              (member.role === 'team_lead' || member.role === 'primary' || member.is_connection_owner)
            );
          }
          
          const isOwner = hasOwner || isMemberOwner;
          
          // Enhanced logic for migrated connections
          // A migrated connection should appear in the tab corresponding to its NEW account type
          // regardless of its original type
          let shouldShowInTab = false;
          
          if (team.is_migrated) {
            // For migrated connections, use the current account_type (after migration)
            const teamAccountType = team.account_type?.toLowerCase();
            const teamConnectionType = team.connection_type?.toLowerCase();
            
            console.log(`[DEBUG] Migrated team check:`, {
              teamAccountType,
              teamConnectionType,
              userAccountType: normalized,
              originalType: team.original_type
            });
            
            // Map connection types to user account types for proper tab placement
            if (normalized === 'small_team' && 
                (teamConnectionType === 'small_team' || teamAccountType === 'small_team')) {
              shouldShowInTab = true;
            } else if (normalized === 'medium_team' && 
                      (teamConnectionType === 'medium_team' || teamAccountType === 'medium_team')) {
              shouldShowInTab = true;
            } else if (normalized === 'large_team' && 
                      (teamConnectionType === 'large_team' || teamAccountType === 'large_team')) {
              shouldShowInTab = true;
            } else if (normalized === 'super' || isBrokerageType) {
              // Super users and Brokerage owners/Admins see all team types
              shouldShowInTab = true;
            }
          } else {
            // For non-migrated connections, use the original logic
            const teamType = team.team_type?.toLowerCase();
            const teamAccountType = team.account_type?.toLowerCase();
            
            if (normalized === 'small_team' && 
                (teamType === 'small_team' || teamAccountType === 'small_team')) {
              shouldShowInTab = true;
            } else if (normalized === 'medium_team' && 
                      (teamType === 'medium_team' || teamAccountType === 'medium_team')) {
              shouldShowInTab = true;
            } else if (normalized === 'large_team' && 
                      (teamType === 'large_team' || teamAccountType === 'large_team')) {
              shouldShowInTab = true;
            } else if (normalized === 'super' || isBrokerageType) {
              // Super users and Brokerage owners/Admins see all team types
              shouldShowInTab = true;
            }
          }
          
          const finalResult = isOwner && shouldShowInTab;
          
          console.log(`[DEBUG] Team ownership check for ${team.team_name || 'Unnamed'}:`, {
            team_type: team.team_type,
            account_type: team.account_type,
            connection_type: team.connection_type,
            has_owner_field: !!team.owner,
            owner_email: team.owner?.email,
            user_email: user.email,
            direct_owner_match: hasOwner,
            member_owner_match: isMemberOwner,
            is_owner: isOwner,
            should_show_in_tab: shouldShowInTab,
            is_migrated: team.is_migrated,
            original_type: team.original_type,
            final_result: finalResult
          });
          
          return finalResult;
        }).map(team => ({
          // Owner-level controls (e.g. deleting the team) must only ever be scoped to
          // teams this specific user actually manages — a direct check against the
          // team's real owner, independent of the isOwner/isMemberOwner fallback above
          // (which intentionally also matches Lead Agents for tab-inclusion purposes).
          // _canManageThisTeam additionally allows a genuine per-team Lead Agent to Edit
          // (but never Remove) — scoped to THIS team's own membership record, not the
          // user's global role/can_manage_teams (which describe their own separate workspace).
          ...team,
          _isOwnedByCurrentUser: isRealOwnerOfTeam(team),
          _canManageThisTeam: canManageSpecificTeam(team)
        }));
        console.log('[DEBUG] Filtered teams for owner:', filteredTeams);
        // Also include any teams where this user is a Lead Agent member under another owner
        if (memberLeadTeams.length > 0) {
          const ownedIds = new Set(filteredTeams.map(t => t.connection_id));
          const extra = memberLeadTeams
            .filter(t => !ownedIds.has(t.connection_id) && t.status !== 'suspended')
            .map(t => ({
              ...t,
              _isOwnedByCurrentUser: isRealOwnerOfTeam(t),
              _canManageThisTeam: canManageSpecificTeam(t)
            }));
          return [...filteredTeams, ...extra];
        }
        return filteredTeams;
      case 'trial':
      case 'solo':
      case 'solo_assistant':
        // These account types don't own team connections, but user may be a Lead Agent in one
        if (memberLeadTeams.length > 0) {
          console.log('[DEBUG] Non-team user has Lead Agent team connections:', memberLeadTeams.length);
          return memberLeadTeams
            .filter(t => t.status !== 'suspended')
            .map(t => ({
              ...t,
              _isOwnedByCurrentUser: isRealOwnerOfTeam(t),
              _canManageThisTeam: canManageSpecificTeam(t)
            }));
        }
        return [];
      default:
        return teams; // Default to all teams (for brokerage or unknown types)
    }
  };

  const getFilteredSoloAgents = () => {
    if (!user || !user.accountType) {
      return soloAgents; // Show all if no account type
    }
    const normalized = user.accountType.toLowerCase().replace(/ /g, '_');
    switch (normalized) {
      case 'solo_assistant':
        // Solo+Assistant users can only see connections they own or are members of
        const userSoloAgents = soloAgents.filter(solo => {
          // Exclude suspended connections
          if (solo.status === 'suspended') {
            console.log(`[DEBUG] Excluding suspended solo+assistant connection: ${solo.name || 'Unnamed'}`);
            return false;
          }
          
          const isOwner = (solo.owner && solo.owner.email === user.email) ||
                         (solo.members && solo.members.some(member => member.email === user.email));
          
          // Handle migrated connections - show if connection_type or account_type matches
          let shouldShow = false;
          if (solo.is_migrated) {
            const connectionType = solo.connection_type?.toLowerCase();
            const accountType = solo.account_type?.toLowerCase();
            shouldShow = (connectionType === 'solo_assistant' || accountType === 'solo_assistant');
          } else {
            // For non-migrated, use original logic
            shouldShow = true; // Already filtered by ownership above
          }
          
          console.log(`[DEBUG] Solo agent filter check:`, {
            solo_name: solo.name || 'Unnamed',
            is_owner: isOwner,
            is_migrated: solo.is_migrated,
            connection_type: solo.connection_type,
            account_type: solo.account_type,
            original_type: solo.original_type,
            should_show: shouldShow && isOwner
          });
          
          return isOwner && shouldShow;
        });
        console.log(`Solo+Assistant filtering: ${userSoloAgents.length} of ${soloAgents.length} connections for ${user.email}`);
        return userSoloAgents;
      case 'super':
        return soloAgents; // Super users can see all solo+assistant connections
      default:
        return soloAgents; // Default to all solo+assistant connections
    }
  };

  // --- SEAT LOGIC (must be after all hooks/state above and after getFilteredTeams) ---
  const normalizedAccountTypeKey = (effectiveAccountType || '').toLowerCase().replace(/ /g, '_');
  const defaultSeatLimit = ACCOUNT_TYPE_SEATS[normalizedAccountTypeKey] || 1;
  
  // State for billing-based seat limit
  const [billingSeats, setBillingSeats] = useState(defaultSeatLimit);
  
  // State for billing status information
  const [billingStatus, setBillingStatus] = useState(null); // 'trial', 'active', etc.
  const [, setAccountStatus] = useState('active'); // account status from billing API
  
  // Function to reload billing seats (can be called externally)
  const loadBillingSeats = async () => {
    // Try different possible ID field names (same as Seats.js)
    const userId = user?.id || user?.userId || user?.user_id;
    // Admin users load billing from the owner's record
    const billingUserId = user?.role === 'admin' ? (user?.owner_user_id || userId) : userId;

    if (billingUserId) {
      try {
        console.log('[CalendarConnection] Loading billing seats for user:', billingUserId);
        const response = await apiClient.get(`/api/billing/${billingUserId}`);
        
        console.log('[CalendarConnection] Billing API response:', response.data);
        
        if (response.data.success && response.data.data) {
          const billingData = response.data.data;
          
          // Update seat count
          if (billingData.currentPlan?.seatCount) {
            const newSeatCount = billingData.currentPlan.seatCount;
            console.log('[CalendarConnection] Setting billing seats to:', newSeatCount);
            setBillingSeats(newSeatCount);
          } else {
            setBillingSeats(defaultSeatLimit);
          }
          
          // Update billing status information
          const billingStatusFromAPI = billingData.billing_status || billingData.plan_type || 'trial';
          const accountStatusFromAPI = billingData.account_status || 'active';
          
          console.log('[CalendarConnection] Setting billing status:', { billingStatusFromAPI, accountStatusFromAPI });
          setBillingStatus(billingStatusFromAPI);
          setAccountStatus(accountStatusFromAPI);
        } else {
          // User not found in billing data (404) or no plan data - use defaults
          console.log('[CalendarConnection] No billing data found, using defaults');
          setBillingSeats(defaultSeatLimit);
          setBillingStatus('trial'); // Default for new Solo users
          setAccountStatus('active');
        }
      } catch (err) {
        console.error('[CalendarConnection] Failed to load billing seats:', err);
        setBillingSeats(defaultSeatLimit);
        setBillingStatus('trial'); // Default for new Solo users
        setAccountStatus('active');
      }
    } else {
      console.log('[CalendarConnection] No user ID available for loading billing seats - user object:', user);
    }
  };
  
  // Load user's purchased seats from billing data on mount and user change
  useEffect(() => {
    loadBillingSeats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, defaultSeatLimit]);
  
  // Listen for seat updates from parent component
  useEffect(() => {
    if (onSeatsUpdate) {
      // Store the refresh function in a way the parent can call it
      window.refreshCalendarSeats = loadBillingSeats;
    }
    
    // Also listen for custom events for seat updates
    const handleSeatUpdate = (event) => {
      console.log('[CalendarConnection] Received seat update event:', event.detail);
      loadBillingSeats();
    };
    
    window.addEventListener('seatsUpdated', handleSeatUpdate);
    
    // For debugging - expose force refresh function and debug info
    window.forceRefreshSeats = () => {
      console.log('[CalendarConnection] Force refreshing seats...');
      loadBillingSeats();
    };
    
    // Debug function to show current state
    window.debugSeats = () => {
      const userId = user?.id || user?.userId || user?.user_id;
      console.log('[DEBUG] Current seat state:', {
        billingSeats,
        seatLimit,
        usedSeats,
        availableSeats,
        userId: userId,
        userObject: user,
        accountType: normalizedAccountTypeKey,
        defaultSeatLimit
      });
    };
    
    return () => {
      // Clean up the global reference
      if (window.refreshCalendarSeats) {
        delete window.refreshCalendarSeats;
      }
      if (window.forceRefreshSeats) {
        delete window.forceRefreshSeats;
      }
      window.removeEventListener('seatsUpdated', handleSeatUpdate);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSeatsUpdate]);
  
  const seatLimit = billingSeats;
  const usedSeats = useMemo(() => {
    if (!user) return 0;
    if (["small_team", "medium_team", "large_team", "small_brokerage", "medium_brokerage", "large_brokerage", "brokerage", "super"].includes(normalizedAccountTypeKey)) {
      const myTeams = getFilteredTeams();
      console.log('[DEBUG usedSeats] myTeams:', myTeams);
      return myTeams.reduce((sum, team) => {
        // Simply count members - the members array includes all people (admin + members)
        const teamSeats = team.members ? team.members.length : 0;
        
        console.log(`[DEBUG usedSeats] Team: members=${team.members?.length}, teamSeats=${teamSeats}`);
        console.log(`[DEBUG usedSeats] sum before: ${sum}, adding: ${teamSeats}, sum after: ${sum + teamSeats}`);
        return sum + teamSeats;
      }, 0);
    }
    if (["solo", "solo_assistant"].includes(normalizedAccountTypeKey)) {
      if (normalizedAccountTypeKey === "solo") {
        const mySolo = solos.find(solo => solo.owner && solo.owner.email === user.email);
        return mySolo && mySolo.members ? mySolo.members.length : 0;
      } else {
        const mySolo = soloAgents.find(sa => sa.owner && sa.owner.email === user.email);
        return mySolo && mySolo.members ? mySolo.members.length : 0;
      }
    }
    if (normalizedAccountTypeKey === "trial") {
      console.log(`[DEBUG] Trial seat calculation - user.email: ${user.email}`);
      console.log(`[DEBUG] Trial seat calculation - trials array:`, trials);
      const myTrial = trials.find(trial => trial.owner && trial.owner.email === user.email);
      console.log(`[DEBUG] Trial seat calculation - myTrial:`, myTrial);
      const memberCount = myTrial && myTrial.members ? myTrial.members.length : 0;
      console.log(`[DEBUG] Trial seat calculation - member count:`, memberCount);
      return memberCount;
    }
    return 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, teams, solos, soloAgents, trials, normalizedAccountTypeKey]);
  
  // Recalculate available seats whenever seatLimit or usedSeats changes
  const availableSeats = useMemo(() => {
    return Math.max(seatLimit - usedSeats, 0);
  }, [seatLimit, usedSeats]);
  
  // Debug logging for seat calculations
  useEffect(() => {
    console.log(`[CalendarConnection] Seat calculations [${new Date().toLocaleTimeString()}]:`, {
      seatLimit,
      billingSeats,
      usedSeats,
      availableSeats,
      normalizedAccountTypeKey,
      defaultSeatLimit
    });
  }, [seatLimit, billingSeats, usedSeats, availableSeats, normalizedAccountTypeKey, defaultSeatLimit]);

  // Helper function to determine which connection types are available for selection
  const getAvailableConnectionTypes = () => {
    if (!user || !user.accountType) {
      // Show all if no account type (legacy users)
      return ['Solo', 'Solo + Assistant', 'Small Team', 'Large Team'];
    }
    const normalized = user.accountType.toLowerCase().replace(/ /g, '_');
    switch (normalized) {
      case 'solo':
        return ['Solo'];
      case 'trial':
        return ['Trial'];
      case 'solo_assistant':
        return ['Solo + Assistant'];
      case 'small_team':
        return ['Small Team'];
      case 'medium_team':
        return ['Medium Team'];
      case 'large_team':
        return ['Large Team'];
      case 'small_brokerage':
      case 'medium_brokerage':
      case 'large_brokerage':
      case 'brokerage':
        // Brokerage users can create all types of connections
        return ['Solo', 'Solo + Assistant', 'Small Team', 'Medium Team', 'Large Team'];
      case 'super':
        // Super users can create all types
        return ['Solo', 'Solo + Assistant', 'Small Team', 'Medium Team', 'Large Team'];
      default:
        // If the account type is not recognized, do not show Solo for any team type
        if (normalized.includes('team')) {
          if (normalized.includes('large')) return ['Large Team'];
          if (normalized.includes('medium')) return ['Medium Team'];
          if (normalized.includes('small')) return ['Small Team'];
          return ['Small Team', 'Medium Team', 'Large Team'];
        }
        return ['Solo'];
    }
  };

  // Helper function to get the best default tab for brokerage users
  const getBestDefaultTab = () => {
    // Check which tabs have content and return the first one
    // Note: This runs before data is loaded, so we default to large_team
    // The actual tab selection will work once data loads
    try {
      const filteredTeams = getFilteredTeams();
      const filteredSoloAgents = getFilteredSoloAgents();
      // Filter solos the same way as in the render
      const filteredSolos = solos.filter(solo => {
        if (!user || !user.email) return false;
        const userEmail = (user.email || '').toLowerCase().trim();
        const ownerEmail = ((solo.owner && solo.owner.email) || '').toLowerCase().trim();
        let matchesUser = ownerEmail === userEmail;

        // Handle migrated connections
        let shouldShow = true;
        if (solo.is_migrated) {
          const connectionType = solo.connection_type?.toLowerCase();
          const accountType = solo.account_type?.toLowerCase();
          shouldShow = (connectionType === 'solo' || accountType === 'solo');
        }
        
        return matchesUser && shouldShow;
      });
      
      if (filteredTeams.length > 0) return 'large_team';
      if (filteredSoloAgents.length > 0) return 'soloAgents';
      if (filteredSolos.length > 0) return 'solos';
    } catch (error) {
      // If data isn't loaded yet, just default to large_team
      console.log('[DEBUG] getBestDefaultTab: Data not loaded yet, defaulting to large_team');
    }
    
    // If no content or data not loaded, default to large_team as it's the most common for brokerages
    return 'large_team';
  };

  // Set initial tab based on user's account type - ONLY ONCE WHEN USER IS AVAILABLE
  useEffect(() => {
    // Only run once when user becomes available and we haven't set the initial tab yet
    if (user && user.accountType && !hasSetInitialTab) {
      const normalized = user.accountType.toLowerCase().replace(/ /g, '_');
      
      // For brokerage users, check if there's a saved tab in sessionStorage first
      if (['broker', 'small_brokerage', 'medium_brokerage', 'large_brokerage', 'brokerage', 'super'].includes(normalized)) {
        const savedTab = sessionStorage.getItem('brokerageActiveTab');
        if (savedTab && ['large_team', 'medium_team', 'small_team', 'soloAgents', 'solos'].includes(savedTab)) {
          console.log('[DEBUG] Restoring saved brokerage tab:', savedTab);
          setActiveTab(savedTab);
          setPreviousTab(savedTab);
          setHasSetInitialTab(true);
          return;
        }
      }
      
      let initialTab;
      switch (normalized) {
        case 'solo':
          initialTab = 'solos';
          break;
        case 'trial':
          initialTab = 'trial';
          break;
        case 'solo_assistant':
          initialTab = 'soloAgents';
          break;
        case 'small_team':
          initialTab = 'small_team';
          break;
        case 'medium_team':
          initialTab = 'medium_team';
          break;
        case 'large_team':
          initialTab = 'large_team';
          break;
        case 'small_brokerage':
        case 'medium_brokerage':
        case 'large_brokerage':
        case 'broker':
        case 'brokerage':
        case 'super':
          // For brokerage users, set a smart default
          initialTab = getBestDefaultTab();
          break;
        default:
          // Fallback: if unknown account type, try to infer from visible tabs
          const visibleTabs = getVisibleTabs;
          initialTab = visibleTabs.length > 0 ? visibleTabs[0] : 'large_team';
      }
      
      console.log('[DEBUG] Setting initial tab:', initialTab, 'for account type:', normalized);
      setActiveTab(initialTab);
      setPreviousTab(initialTab); // Also set as previous tab
      setHasSetInitialTab(true); // Mark as set - will never run again
    } else if (user && !hasSetInitialTab) {
      // Fallback: if user exists but no accountType, set a safe default
      console.warn('[DEBUG] User exists but no accountType, setting default tab');
      const visibleTabs = getVisibleTabs;
      const fallbackTab = visibleTabs.length > 0 ? visibleTabs[0] : 'large_team';
      setActiveTab(fallbackTab);
      setPreviousTab(fallbackTab);
      setHasSetInitialTab(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.accountType, hasSetInitialTab, getVisibleTabs]); // Include getVisibleTabs for fallback logic

  // For Lead Agents: once data loads, visible tabs narrow from 3 → actual types.
  // If the currently-active tab is no longer in the visible set, jump to the first visible one.
  useEffect(() => {
    if (!isLeadAgentManager) return;
    if (!activeTab) return;
    if (getVisibleTabs.length === 0) return;
    if (!getVisibleTabs.includes(activeTab)) {
      const corrected = getVisibleTabs[0];
      setActiveTab(corrected);
      setPreviousTab(corrected);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getVisibleTabs]); // activeTab intentionally omitted — only fires when tab list changes

  // _doTabChange is declared first so handleTabChange can reference it without TDZ issues
  const _doTabChange = (newTab) => {
    setPreviousTab(activeTab);
    setActiveTab(newTab);
    if (user && user.accountType) {
      const normalized = user.accountType.toLowerCase().replace(/ /g, '_');
      if (['broker', 'small_brokerage', 'medium_brokerage', 'large_brokerage', 'brokerage', 'super'].includes(normalized)) {
        sessionStorage.setItem('brokerageActiveTab', newTab);
        console.log('[DEBUG] Saved brokerage tab to sessionStorage:', newTab);
      }
    }
  };

  // Helper function to handle tab changes — reads from ref to avoid stale closure
  const handleTabChange = (newTab) => {
    const hasAny = Object.values(unpublishedChangesRef.current).some(v => v.count > 0);
    if (hasAny) {
      setUnpublishedWarning({
        onConfirm: () => {
          setUnpublishedChanges({});
          setUnpublishedTaskIds({});
          unpublishedChangesRef.current = {};
          _doTabChange(newTab);
        },
      });
      return;
    }
    _doTabChange(newTab);
  };

  const [appleId, setAppleId] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [authText, setAuthText] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [message, setMessage] = useState(null);
  // Enhanced loading states
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Cache for connection data to avoid refetching using refs
  const dataCacheRef = useRef({});
  const lastFetchTimeRef = useRef(0);
  const CACHE_DURATION = 30000; // 30 seconds cache

  // Load data from service with optimizations
  const loadData = useCallback(async (forceRefresh = false) => {
    console.log('[DEBUG] loadData: Starting to load connections...');
    
    // Check cache first (unless force refresh)
    const now = Date.now();
    const currentUserEmail = user?.email || '';
    if (!forceRefresh && dataCacheRef.current.teams && dataCacheRef.current.forEmail === currentUserEmail && (now - lastFetchTimeRef.current < CACHE_DURATION)) {
      console.log('[DEBUG] loadData: Using cached data');
      setTeams(dataCacheRef.current.teams || []);
      setSoloAgents(dataCacheRef.current.soloAgents || []);
      setSolos(dataCacheRef.current.solos || []);
      setTrials(dataCacheRef.current.trials || []);
      setMemberLeadTeams(dataCacheRef.current.memberLeadTeams || []);
      return;
    }

    setLoading(true);
    setApiError(null);

    // Lead Agent managers only see connections where they are the team_lead
    if (isLeadAgentManager) {
      try {
        let data;
        if (isImpersonating && impersonatedUser?.email) {
          // During impersonation the JWT is the owner's, so getMemberTeamConnections()
          // would return teams where the OWNER is a member — wrong scope.
          // Instead, load the owner's own connections and filter client-side to only
          // teams where the impersonated user is assigned as team_lead.
          const impersonatedEmail = impersonatedUser.email.toLowerCase();
          const results = await Promise.allSettled([
            dataService.getConnections('small_team'),
            dataService.getConnections('medium_team'),
            dataService.getConnections('large_team'),
          ]);
          const allTeams = results.flatMap(r => r.status === 'fulfilled' ? r.value || [] : []);
          data = allTeams.filter(team =>
            team.status !== 'suspended' &&
            team.members?.some(m => (m.email || '').toLowerCase() === impersonatedEmail && m.role === 'team_lead')
          );
        } else {
          data = await dataService.getMemberTeamConnections();
        }
        setTeams(data);
        dataCacheRef.current = { teams: data, forEmail: currentUserEmail };
        lastFetchTimeRef.current = Date.now();
      } catch (err) {
        console.error('[loadData] isLeadAgentManager data load failed:', err);
        setTeams([]);
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      // Optimize: Only load data types the user can actually see
      const visibleTabs = getVisibleTabs;
      const loadPromises = [];

      // Always fetch connections where this user appears as a Lead Agent member (under another owner).
      // This runs regardless of visible tabs so existing paid users invited as Lead Agent can see those teams.
      const memberConnectionsPromise = dataService.getMemberTeamConnections().catch(err => {
        console.error('[loadData] supplemental getMemberTeamConnections failed:', err);
        return [];
      });
      
      // Load team data only if user can see team tabs
      if (visibleTabs.some(tab => ['large_team', 'medium_team', 'small_team'].includes(tab))) {
        loadPromises.push(
          Promise.allSettled([
            dataService.getConnections('small_team'),
            dataService.getConnections('medium_team'),
            dataService.getConnections('large_team')
          ]).then(results => ({
            type: 'teams',
            data: results.flatMap(result => result.status === 'fulfilled' ? result.value || [] : [])
          }))
        );
      }

      // Load solo agents only if visible
      if (visibleTabs.includes('soloAgents')) {
        loadPromises.push(
          dataService.getConnections('solo_assistant')
            .then(data => ({ type: 'soloAgents', data: data || [] }))
            .catch(() => ({ type: 'soloAgents', data: [] }))
        );
      }

      // Load solos only if visible
      if (visibleTabs.includes('solos')) {
        loadPromises.push(
          dataService.getConnections('solo')
            .then(data => ({ type: 'solos', data: data || [] }))
            .catch(() => ({ type: 'solos', data: [] }))
        );
      }

      // Load trials only if visible
      if (visibleTabs.includes('trial')) {
        loadPromises.push(
          dataService.getConnections('trial')
            .then(data => ({ type: 'trials', data: data || [] }))
            .catch(() => ({ type: 'trials', data: [] }))
        );
      }

      // Wait for all relevant data to load, plus the supplemental member-connections fetch
      const [results, fetchedMemberConnections] = await Promise.all([
        Promise.all(loadPromises),
        memberConnectionsPromise,
      ]);

      // Connections where user appears as a member (Lead Agent) under another owner
      const userEmail = (user?.email || '').toLowerCase();
      const newMemberLeadTeams = fetchedMemberConnections.filter(conn =>
        conn.members?.some(m => (m.email || '').toLowerCase() === userEmail)
      );
      setMemberLeadTeams(newMemberLeadTeams);

      // Process results
      const newCache = { memberLeadTeams: newMemberLeadTeams };
      results.forEach(result => {
        switch (result.type) {
          case 'teams':
            setTeams(result.data);
            newCache.teams = result.data;
            break;
          case 'soloAgents':
            setSoloAgents(result.data);
            newCache.soloAgents = result.data;
            break;
          case 'solos':
            setSolos(result.data);
            newCache.solos = result.data;
            break;
          case 'trials':
            setTrials(result.data);
            newCache.trials = result.data;
            break;
          default:
            break;
        }
      });

      // Update cache
      dataCacheRef.current = { ...dataCacheRef.current, ...newCache, forEmail: currentUserEmail };
      lastFetchTimeRef.current = now;
      
      console.log('[DEBUG] Main connection data loaded, hiding loading indicator');
      setLoading(false);
      
      // Load phone slots data in background (don't wait for it)
      if (FEATURE_FLAGS.PHONE_FEATURE_ENABLED) {
        loadPhoneSlotsData().catch(err => {
          console.error('[DEBUG] Phone slots loading failed:', err);
        });
      }
      
    } catch (error) {
      console.error('[DEBUG] Error loading data:', error);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getVisibleTabs, isLeadAgentManager, user?.email]); // user.email needed for memberLeadTeams filtering
  
  // Load phone slots data from billing - only if phone feature is enabled
  const loadPhoneSlotsData = async () => {
    if (!FEATURE_FLAGS.PHONE_FEATURE_ENABLED) return;
    
    try {
      const userId = user?.id || user?.userId || user?.user_id;
      if (!userId) return;
      
      const response = await apiClient.get(`/api/billing/${userId}`);
      
      if (response.data.success && response.data.data && typeof response.data.data.phoneSlots === 'number') {
        setTotalPhoneSlots(response.data.data.phoneSlots);
        
        // Load actual phone registrations from connections data to count registered numbers
        // We'll use a simpler approach for now - just count from the current team members
        const registeredCount = members.filter(member => 
          member.phone && member.phone.toString().replace(/\D/g, '').length === 10
        ).length;
        setRegisteredPhoneNumbers(registeredCount);
      } else {
        // User not found in billing data - use defaults
        setUserPhoneSlots([]);
        setTotalPhoneSlots(0);
        setRegisteredPhoneNumbers(0);
      }
    } catch (error) {
      console.error('Error loading phone slots data:', error);
      setUserPhoneSlots([]);
      setTotalPhoneSlots(0);
      setRegisteredPhoneNumbers(0);
    }
  };

  // Initialize data loading when component mounts or user changes
  useEffect(() => {
    const loadInitialData = async () => {
      if (user) {
        setInitialLoad(true);
        await loadData();
        setInitialLoad(false);
      }
    };
    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Remove loadData from dependencies to prevent infinite loop

  // Listen for subscription changes (cancellations, upgrades) to auto-refresh connections
  useEffect(() => {
    const handleSubscriptionChange = async (event) => {
      console.log('🔔 [EVENT] Subscription change detected, refreshing connections...', event.detail);
      console.log('🔔 [EVENT] Event type:', event.detail?.action, 'Plan:', event.detail?.planType);
      // Small delay to account for DynamoDB eventual consistency
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('🔔 [EVENT] Calling loadData to refresh connections...');
      await loadData(true); // Force refresh with cache invalidation
      console.log('🔔 [EVENT] loadData completed');
    };

    const handleConnectionDeleted = async (event) => {
      console.log('🗑️ Connection deleted, refreshing connections...', event.detail);
      // Small delay to account for DynamoDB eventual consistency
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadData(true); // Force refresh with cache invalidation
    };

    const handleConnectionReactivated = async (event) => {
      console.log('🔄 Connection reactivated, refreshing connections...', event.detail);
      // Delay for DynamoDB eventual consistency
      await new Promise(resolve => setTimeout(resolve, 1000));
      await loadData(true); // Force refresh with cache invalidation
    };

    console.log('🎧 [INIT] Setting up event listeners for subscription-changed, connection-deleted, connection-reactivated');
    window.addEventListener('subscription-changed', handleSubscriptionChange);
    window.addEventListener('connection-deleted', handleConnectionDeleted);
    window.addEventListener('connection-reactivated', handleConnectionReactivated);
    
    return () => {
      console.log('🔇 [CLEANUP] Removing event listeners');
      window.removeEventListener('subscription-changed', handleSubscriptionChange);
      window.removeEventListener('connection-deleted', handleConnectionDeleted);
      window.removeEventListener('connection-reactivated', handleConnectionReactivated);
    };
  }, [loadData]);

  // Handle data updates after successful authorization
  const handleDataUpdate = async () => {
    console.log('🔄 [SAVE] handleDataUpdate called - Data updated, refreshing...');
    // Force refresh of data with cache invalidation
    await loadData(true); 
    console.log('🔄 [SAVE] Data loaded');
    setTimeout(() => {
      console.log('🔄 [SAVE] Closing modal');
      handleClose();
    }, 200);
  };

  // Check if Add Connection button should be disabled
  const isAddConnectionDisabled = () => {
    if (!user || !user.accountType) return false;
    const normalized = user.accountType.toLowerCase().replace(/ /g, '_');
  // no 'broker' type

    // Solo+Assistant users can only have one connection
    if (normalized === 'solo_assistant') {
      const userSoloAgents = getFilteredSoloAgents();
      return userSoloAgents.length > 0;
    }

    // Solo users can only have one solo connection
    if (['solo'].includes(normalized)) {
      const userEmail = (user.email || '').toLowerCase().trim();
      const userSolos = solos.filter(solo => {
        let memberMatch = false;
        if (solo.members && solo.members.length > 0) {
          const memberEmail = (solo.members[0].email || '').toLowerCase().trim();
          memberMatch = memberEmail === userEmail;
        }
        let ownerMatch = false;
        if (solo.owner && solo.owner.email) {
          const ownerEmail = (solo.owner.email || '').toLowerCase().trim();
          ownerMatch = ownerEmail === userEmail;
        }
        
        // Handle migrated connections
        let shouldShow = true;
        if (solo.is_migrated) {
          const connectionType = solo.connection_type?.toLowerCase();
          const accountType = solo.account_type?.toLowerCase();
          shouldShow = (connectionType === 'solo' || accountType === 'solo');
        }
        
        return (memberMatch || ownerMatch) && shouldShow;
      });
      return userSolos.length > 0;
    }

    // Team users (small_team, medium_team, large_team) can only have one team connection
    if (['small_team', 'medium_team', 'large_team'].includes(normalized)) {
      const userTeams = getFilteredTeams();
      return userTeams.length > 0;
    }

    // Trial users can only have one trial connection
    if (normalized === 'trial' || user?.is_trial === 'true' || user?.trial_status === 'active') {
      const userEmail = (user.email || '').toLowerCase().trim();
      const userTrials = trials.filter(trial => {
        const ownerEmail = ((trial.owner && trial.owner.email) || '').toLowerCase().trim();
        let matchesUser = ownerEmail === userEmail;

        // Fallback: check if user email matches first member (for legacy connections)
        if (!matchesUser && (!trial.owner || !trial.owner.email) && trial.members && trial.members.length > 0) {
          const firstMemberEmail = (trial.members[0].email || '').toLowerCase().trim();
          matchesUser = firstMemberEmail === userEmail;
        }

        return matchesUser;
      });
      return userTrials.length > 0;
    }

    return false;
  };

  const handleAddClick = () => {
    // Don't allow if button is disabled
    if (isAddConnectionDisabled()) {
      return;
    }
    
    // Save current tab state before starting modal flow (for restoration on cancel)
    setPreviousTab(activeTab);
    
    // For Team accounts, check if they already have a team
    if (user && user.accountType === 'Team') {
      const userTeams = teams.filter(team => 
        team.members && team.members.some(member => member.email === user.email)
      );
      
      if (userTeams.length > 0) {
        setMessage({
          type: 'warning',
          text: 'Team accounts can only create one team connection. You already have a team.'
        });
        setTimeout(() => setMessage(null), 5000);
        return;
      }
    }
    
    // Reset all form data before showing the selection modal
    resetFormData();
    
    // Show selection modal
    setShowSelectionModal(true);
  };

  const handleSelectionClose = () => {
    setShowSelectionModal(false);
    
    // Restore the previous tab when canceling from account type selection
    if (user && user.accountType) {
      const normalized = user.accountType.toLowerCase().replace(/ /g, '_');
      if (['broker', 'brokerage', 'super'].includes(normalized)) {
        setActiveTab(previousTab); // Restore previously selected tab for Brokers and Super
      }
    }
  };

  const handleTypeSelection = (type) => {
    console.log('Selected account type:', type);
    
    // Check if user already has a connection and is restricted
    if (user && user.accountType) {
      if (user.accountType === 'Solo_Assistant' && type === "Solo + Assistant") {
        // Check if they already have a Solo+Assistant connection
        const existingConnections = getFilteredSoloAgents();
        if (existingConnections.length > 0) {
          alert('Solo + Assistant accounts can only create one Solo + Assistant connection. You already have an existing connection.');
          return;
        }
      } else if (user.accountType === 'Team' && type === "Team") {
        // Check if they already have a Team connection
        const existingTeams = getFilteredTeams();
        if (existingTeams.length > 0) {
          alert('Team accounts can only create one team connection. You already have an existing team.');
          return;
        }
      }
    }
    
    // Convert display names to internal accountType values
    let internalAccountType = type;
    if (type === "Solo") {
      internalAccountType = "solo";
    } else if (type === "Trial") {
      internalAccountType = "trial";
    } else if (type === "Solo + Assistant") {
      internalAccountType = "solo_assistant";
    } else if (type === "Small Team") {
      internalAccountType = "small_team";
    } else if (type === "Medium Team") {
      internalAccountType = "medium_team";
    } else if (type === "Large Team") {
      internalAccountType = "large_team";
    }
    setAccountType(internalAccountType);
    setShowSelectionModal(false);
    
    // Switch to appropriate tab based on selection
    if (type === "Solo") {
      setActiveTab('solos');
    } else if (type === "Trial") {
      setActiveTab('trial'); // Trial users get their own tab
    } else if (type === "Solo + Assistant") {
      setActiveTab('soloAgents');
    } else if (type === "Small Team" || type === "Medium Team" || type === "Large Team") {
      setActiveTab('teams');
    }
    
    // Initialize members array with proper structure based on type
    if (type === "Solo") {
      setMembers([{
        email: '', first_name: '', last_name: '',
        phone: '', displayPhone: '',
        provider: 'google', appleId: '', appPassword: ''
      }]);
    } else if (type === "Trial") {
      setMembers([{
        email: '', first_name: '', last_name: '',
        phone: '', displayPhone: '',
        provider: 'google', appleId: '', appPassword: ''
      }]);
    } else if (type === "Solo + Assistant") {
      // For solo + assistant, initialize with agent and assistant
      setMembers([
        {
          email: '', first_name: '', last_name: '',
          phone: '', displayPhone: '',
          provider: 'google', role: 'single_agent', appleId: '', appPassword: ''
        },
        {
          email: '', first_name: '', last_name: '',
          phone: '', displayPhone: '',
          provider: 'google', role: 'assistant', appleId: '', appPassword: ''
        }
      ]);
      setActiveTab('soloAgents');
    } else if (type === "Small Team" || type === "Medium Team" || type === "Large Team") {
      // For team, initialize with one primary member
      setMembers([{
        email: '', first_name: '', last_name: '',
        phone: '', displayPhone: '',
        provider: 'google', role: 'primary', appleId: '', appPassword: ''
      }]);
      setActiveTab('teams');
    }
    
    // Show the add modal
    setShowAddModal(true);
  };

  const handleClose = () => {
    setShowAddModal(false);
    setIsEditMode(false);
    setCurrentEditItem(null);
    
    // Reset form data to clean state
    resetFormData();
    
    // Always restore the previous tab when closing modal (for better UX)
    // This ensures users return to the tab they were on before opening the modal
    console.log('[DEBUG] handleClose: previousTab:', previousTab, 'activeTab:', activeTab, 'user?.accountType:', user?.accountType);
    
    if (previousTab) {
      console.log('[DEBUG] Restoring previous tab:', previousTab, 'from current:', activeTab);
      setActiveTab(previousTab);
    } else if (user && user.accountType) {
      // Fallback: only set default tab if no previous tab is available
      console.log('[DEBUG] No previousTab available, setting default based on account type');
      const normalized = user.accountType.toLowerCase().replace(/ /g, '_');
      
      // Set appropriate default tab based on account type
      switch (normalized) {
        case 'solo':
          setActiveTab('solos');
          break;
        case 'trial':
          setActiveTab('trial');
          break;
        case 'solo_assistant':
          setActiveTab('soloAgents');
          break;
        case 'small_team':
          setActiveTab('small_team');
          break;
        case 'medium_team':
          setActiveTab('medium_team');
          break;
        case 'large_team':
          setActiveTab('large_team');
          break;
        case 'broker':
        case 'brokerage':
        case 'super':
          setActiveTab('teams'); // fallback for brokerage users
          break;
        default:
          setActiveTab('teams'); // fallback
      }
    }
  };

  // Helper function to reset all form data
  const resetFormData = () => {
    setAccountType("");
    setShowSuccess(false);
    setMembers([{
      email: '', first_name: '', last_name: '',
      ...(FEATURE_FLAGS.PHONE_FEATURE_ENABLED && {
        phone: '',
        displayPhone: ''
      }),
      provider: 'google',
      appleId: '',
      appPassword: ''
    }]);
    setProvider('google');
    setAppleId('');
    setAppPassword('');
    setAuthText('');
    setAuthCode('');
    setMessage(null);
    setLoading(false);
    setIsEditMode(false);
    setCurrentEditItem(null);
  };

  const handleReset = () => {
    resetFormData();
  };


  const handleEditSolo = (solo) => {
    console.log('[DEBUG] handleEditSolo: Saving current tab as previous:', activeTab);
    setPreviousTab(activeTab);
    setIsEditMode(true);
    setCurrentEditItem(solo);
    const connectionAccountType = solo.account_type || "solo";
    setAccountType(connectionAccountType);
    const baseMembers = solo.members && solo.members.length > 0
      ? solo.members.map(member => ({
          email: member.email || '',
          originalEmail: member.email || '',
          first_name: member.first_name || '',
          last_name: member.last_name || '',
          ...(FEATURE_FLAGS.PHONE_FEATURE_ENABLED && {
            phone: member.phone || '',
            displayPhone: member.phone || ''
          }),
          provider: member.client || member.provider || 'google',
          appleId: member.apple_id || member.appleId || '',
          appPassword: member.app_password || member.appPassword || '',
          task_due_email_reminder: member.task_due_email_reminder !== false,
          personal_calendar_sync: member.personal_calendar_sync !== false,
          permissions: {
            ...(member.permissions || {}),
            can_delete_task_lists: member.permissions?.can_delete_task_lists ?? member.can_delete_task_lists ?? (member.role === 'team_lead'),
            can_archive_task_lists: member.permissions?.can_archive_task_lists ?? member.can_archive_task_lists ?? (member.role === 'team_lead'),
            can_manage_teams: member.permissions?.can_manage_teams ?? member.can_manage_teams ?? false,
            can_view_unpublished_changes: member.permissions?.can_view_unpublished_changes ?? member.can_view_unpublished_changes ?? (member.role === 'team_lead'),
          },
          role: member.role || '',
        }))
      : [{ email: '', first_name: '', last_name: '', provider: 'google', appleId: '', appPassword: '' }];
    setMembers(baseMembers);
    setShowAddModal(true);
    loadBillingSeats();
  };

  const handleEditTeam = (team) => {
    console.log('[DEBUG] handleEditTeam: Saving current tab as previous:', activeTab);
    setPreviousTab(activeTab);
    setIsEditMode(true);
    setCurrentEditItem(team);
    if (team.team_type === 'large_team') {
      setAccountType('large_team');
    } else if (team.team_type === 'medium_team') {
      setAccountType('medium_team');
    } else {
      setAccountType('small_team');
    }
    const baseMembers = team.members.map(member => ({
      email: member.email,
      originalEmail: member.email || '',
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      ...(FEATURE_FLAGS.PHONE_FEATURE_ENABLED && {
        phone: member.phone || '',
        displayPhone: member.phone || ''
      }),
      provider: member.client || member.provider || 'google',
      role: member.role || 'secondary',
      appleId: member.apple_id || member.appleId || '',
      appPassword: member.app_password || member.appPassword || '',
      task_due_email_reminder: member.task_due_email_reminder !== false,
      personal_calendar_sync: member.personal_calendar_sync !== false,
      permissions: {
        ...(member.permissions || {}),
        can_delete_task_lists: member.permissions?.can_delete_task_lists ?? member.can_delete_task_lists ?? (member.role === 'team_lead'),
        can_archive_task_lists: member.permissions?.can_archive_task_lists ?? member.can_archive_task_lists ?? (member.role === 'team_lead'),
        can_manage_teams: member.permissions?.can_manage_teams ?? member.can_manage_teams ?? false,
        can_view_unpublished_changes: member.permissions?.can_view_unpublished_changes ?? member.can_view_unpublished_changes ?? (member.role === 'team_lead'),
      },
      is_connection_owner: member.is_connection_owner === true,
      team_role: member.team_role || member.role || '',
      suspended: member.suspended === true,
      suspended_by: member.suspended_by || null,
      suspended_at: member.suspended_at || null,
    }));
    setMembers(baseMembers);
    setShowAddModal(true);
    loadBillingSeats();
  };

  // Handle editing a solo+assistant connection
  const handleEditSoloAgent = (agent) => {
    console.log('[DEBUG] handleEditSoloAgent: Saving current tab as previous:', activeTab);
    setPreviousTab(activeTab);
    setIsEditMode(true);
    setCurrentEditItem(agent);
    setAccountType("solo_assistant");
    const baseMembers = agent.members.map(member => ({
      email: member.email,
      originalEmail: member.email || '',
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      ...(FEATURE_FLAGS.PHONE_FEATURE_ENABLED && {
        phone: member.phone || '',
        displayPhone: member.phone || ''
      }),
      provider: member.client || member.provider || 'google',
      role: member.role || 'single_agent',
      appleId: member.apple_id || member.appleId || '',
      appPassword: member.app_password || member.appPassword || '',
      task_due_email_reminder: member.task_due_email_reminder !== false,
      personal_calendar_sync: member.personal_calendar_sync !== false,
      permissions: {
        ...(member.permissions || {}),
        can_delete_task_lists: member.permissions?.can_delete_task_lists ?? member.can_delete_task_lists ?? (member.role === 'team_lead'),
        can_archive_task_lists: member.permissions?.can_archive_task_lists ?? member.can_archive_task_lists ?? (member.role === 'team_lead'),
        can_manage_teams: member.permissions?.can_manage_teams ?? member.can_manage_teams ?? false,
        can_view_unpublished_changes: member.permissions?.can_view_unpublished_changes ?? member.can_view_unpublished_changes ?? (member.role === 'team_lead'),
      },
      suspended: member.suspended === true,
      suspended_by: member.suspended_by || null,
      suspended_at: member.suspended_at || null,
    }));
    setMembers(baseMembers);
    setShowAddModal(true);
    loadBillingSeats();
  };

  // Handle viewing tasks for all team members (lead + members)
  const handleViewTasks = async (team) => {
    const emails = (team.members || []).map(m => m.email).filter(Boolean);
    // Include the effective owner email so owner-scoped records are fetched.
    const effectiveOwnerEmail = (isAdmin && canManageUsersTeams && user?.owner_email)
      ? user.owner_email.toLowerCase()
      : (user?.role === 'owner' || !user?.role) && user?.email
        ? user.email.toLowerCase()
        : null;
    if (effectiveOwnerEmail && !emails.map(e => e.toLowerCase()).includes(effectiveOwnerEmail)) {
      emails.push(effectiveOwnerEmail);
    }
    // Admin: also ensure admin's own email is included so admin-created task lists
    // (stored under admin_email) are fetched even if admin is not in members[].
    if (isAdmin && canManageUsersTeams && user?.email) {
      const adminSelf = user.email.toLowerCase();
      if (!emails.map(e => e.toLowerCase()).includes(adminSelf)) {
        emails.push(adminSelf);
      }
    }
    const connectionId = team.connection_id || '';
    setTasksModalTeam(team);
    setTasksModalData([]);
    setTasksActiveTab('active');
    setTasksModalLoading(true);
    setShowTasksModal(true); // open modal immediately with spinner

    try {
      const apiRecords = await dataService.getTasksByMembers(emails, connectionId);
      if (apiRecords !== null) {
        setTasksModalData(apiRecords.tasks
          // Strict team scope: only show records that belong to this team's connection
          .filter(r => !connectionId || r.connection_id === connectionId)
          .filter(r => !deletedTitlesRef.current.has(r.task_list_title))
          .map(r => {
            if (archivedTitlesRef.current.has(r.task_list_title)) return { ...r, archive: true };
            if (restoredTitlesRef.current.has(r.task_list_title)) return { ...r, archive: false };
            return r;
          }));
      } else {
        // API unavailable — fall back to local sample data scoped to this connection
        console.warn('[Tasks] API unavailable, falling back to local TaskContext data');
        const memberEmails = new Set(emails);
        const fallback = taskContextData.filter(
          record => memberEmails.has(record.id) && record.active
            && (!connectionId || record.connection_id === connectionId)
            && !deletedTitlesRef.current.has(record.task_list_title)
        );
        setTasksModalData(fallback);
      }
    } catch (err) {
      console.error('[Tasks] handleViewTasks error:', err);
      const memberEmails = new Set(emails);
      const fallback = taskContextData.filter(
        record => memberEmails.has(record.id) && record.active
          && (!connectionId || record.connection_id === connectionId)
          && !deletedTitlesRef.current.has(record.task_list_title)
      );
      setTasksModalData(fallback);
    } finally {
      setTasksModalLoading(false);
    }
  };

  // Handle viewing calendar for all team members
  const handleViewCalendar = async (team) => {
    const emails = (team.members || []).map(m => m.email).filter(Boolean);
    const connectionId = team.connection_id || '';
    setCalendarModalTeam(team);
    setCalendarModalData([]);
    setCalendarModalLoading(true);
    setCalView('month');
    setCalCurrentDate(new Date());
    setCalMiniDate(new Date());
    setCalHiddenProperties(new Set());
    setShowCalendarModal(true);
    try {
      const apiRecords = await dataService.getTasksByMembers(emails, connectionId);
      if (apiRecords !== null) {
        setCalendarModalData(apiRecords.tasks
          .filter(r => !connectionId || r.connection_id === connectionId)
          .filter(r => !deletedTitlesRef.current.has(r.task_list_title))
          .map(r => {
            if (archivedTitlesRef.current.has(r.task_list_title)) return { ...r, archive: true };
            if (restoredTitlesRef.current.has(r.task_list_title)) return { ...r, archive: false };
            return r;
          }));
      } else {
        const memberEmails = new Set(emails);
        setCalendarModalData(taskContextData.filter(
          r => memberEmails.has(r.id) && r.active
            && (!connectionId || r.connection_id === connectionId)
            && !deletedTitlesRef.current.has(r.task_list_title)
        ));
      }
    } catch (err) {
      console.error('[Calendar] handleViewCalendar error:', err);
      const memberEmails = new Set(emails);
      setCalendarModalData(taskContextData.filter(
        r => memberEmails.has(r.id) && r.active
          && (!connectionId || r.connection_id === connectionId)
          && !deletedTitlesRef.current.has(r.task_list_title)
      ));
    } finally {
      setCalendarModalLoading(false);
    }
  };

  // SSE: instant updates when tasks are saved through the API (local dev server supports this).
  // Skipped entirely on the Lambda backend — polling handles that case and the Lambda endpoint
  // does not return CORS headers for EventSource requests.
  useEffect(() => {
    if (!showTasksModal) return;
    if (!API_CONFIG.BASE_URL.includes('localhost')) return;
    let es;
    try {
      es = new EventSource(`${API_CONFIG.BASE_URL}/api/tasks/sse`);

      es.addEventListener('task-updated', (e) => {
        try {
          const { task_list_title, task_index, task } = JSON.parse(e.data);
          setTasksModalData(prev => prev.map(rec =>
            rec.task_list_title !== task_list_title ? rec : {
              ...rec,
              tasks: rec.tasks.map((t, ti) => ti === task_index ? task : t)
            }
          ));
        } catch { /* malformed event — ignore */ }
      });

      es.onerror = () => { /* backend doesn't support SSE — polling covers this */ };
    } catch { /* EventSource blocked or unavailable */ }

    return () => { if (es) es.close(); };
  }, [showTasksModal]);

  // Background polling while the tasks modal is open: re-fetch every 10 s to pick
  // up DynamoDB changes from any external source (direct edits, other Lambda runs, etc.).
  // Skips when the edit popup is open or the browser tab is hidden.
  useEffect(() => {
    if (!showTasksModal || !tasksModalTeam) return;
    const INTERVAL = 10_000;
    let timerId = null;

    const poll = async () => {
      if (document.hidden || editingTaskRef.current) return;
      const emails = (tasksModalTeam.members || []).map(m => m.email).filter(Boolean);
      const effectiveOwnerEmailPoll = (isAdmin && canManageUsersTeams && user?.owner_email)
        ? user.owner_email.toLowerCase()
        : (user?.role === 'owner' || !user?.role) && user?.email
          ? user.email.toLowerCase()
          : null;
      if (effectiveOwnerEmailPoll && !emails.map(e => e.toLowerCase()).includes(effectiveOwnerEmailPoll)) {
        emails.push(effectiveOwnerEmailPoll);
      }
      // Admin: also ensure admin's own email is included so admin-created task lists
      // (stored under admin_email) survive the state overwrite even if admin is not in members[].
      if (isAdmin && canManageUsersTeams && user?.email) {
        const adminSelf = user.email.toLowerCase();
        if (!emails.map(e => e.toLowerCase()).includes(adminSelf)) {
          emails.push(adminSelf);
        }
      }
      const pollConnectionId = tasksModalTeam?.connection_id || '';
      try {
        const records = await dataService.getTasksByMembers(emails, pollConnectionId);
        if (records !== null) {
          const filtered = records.tasks
            .filter(r => !deletedTitlesRef.current.has(r.task_list_title))
            .map(r => {
              if (archivedTitlesRef.current.has(r.task_list_title)) return { ...r, archive: true };
              if (restoredTitlesRef.current.has(r.task_list_title)) return { ...r, archive: false };
              return r;
            });
          setTasksModalData(filtered);
          // Recompute unpublished changes so external updates (e.g. email-action completions)
          // refresh the badge count without requiring a manual page reload.
          if (filtered.length > 0 && unpublishedInitRef.current) {
            const effectiveEmail = (isImpersonating ? impersonatedUser?.email : user?.email)?.toLowerCase();
            const pollChanges = {};
            const pollTaskIds = {};
            const backfillMap = {};
            filtered.forEach(r => {
              const title = r.task_list_title;
              if (effectiveEmail && r.id && r.id.toLowerCase() !== effectiveEmail) return;
              // DB explicitly says nothing is unpublished — clear the badge regardless of
              // task timestamps (handles records where published_at is absent).
              if (r.has_unpublished_changes === false) {
                pollChanges[title] = null;
                pollTaskIds[title] = null;
                return;
              }
              const publishedAt = r.published_at ? new Date(r.published_at) : null;
              const isUnpublishedChange = (t) =>
                !!t.update_timestamp && (!publishedAt || new Date(t.update_timestamp) > publishedAt);
              let tasks = r.tasks || [];
              if (tasks.some(t => isUnpublishedChange(t) && !t.task_id)) {
                const existingBase = lastPublishedTasksRef.current[title] || [];
                tasks = tasks.map((t, ti) =>
                  (isUnpublishedChange(t) && !t.task_id)
                    ? { ...t, task_id: (existingBase[ti] || {}).task_id || `tid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` }
                    : t
                );
                backfillMap[title] = tasks;
              }
              const dirtyTasks = tasks.filter(t => t.task_id && isUnpublishedChange(t));
              pollChanges[title] = dirtyTasks.length > 0
                ? { count: dirtyTasks.length, lastEditedAt: r.last_edited_at || null }
                : null;
              pollTaskIds[title] = dirtyTasks.length > 0
                ? new Set(dirtyTasks.map(t => t.task_id))
                : null;
            });
            setUnpublishedChanges(prev => {
              const next = { ...prev };
              Object.entries(pollChanges).forEach(([t, v]) => { if (v) next[t] = v; else delete next[t]; });
              return next;
            });
            setUnpublishedTaskIds(prev => {
              const next = { ...prev };
              Object.entries(pollTaskIds).forEach(([t, v]) => { if (v) next[t] = v; else delete next[t]; });
              return next;
            });
            if (Object.keys(backfillMap).length > 0) {
              setTasksModalData(prev => prev.map(r => { const bf = backfillMap[r.task_list_title]; return bf ? { ...r, tasks: bf } : r; }));
              Object.entries(backfillMap).forEach(([title, tasks]) => {
                lastPublishedTasksRef.current[title] = tasks.map(t => ({ ...t }));
              });
            }
          }
        }
      } catch { /* network hiccup — try again next tick */ }
    };

    const onVisibility = () => {
      if (document.hidden) {
        clearInterval(timerId);
        timerId = null;
      } else {
        if (!timerId) timerId = setInterval(poll, INTERVAL);
      }
    };

    timerId = setInterval(poll, INTERVAL);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timerId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [showTasksModal, tasksModalTeam, isImpersonating, impersonatedUser, user, setUnpublishedChanges, setUnpublishedTaskIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Background polling while the calendar modal is open: re-fetch every 10 s to pick up
  // DynamoDB changes from any external source (e.g. a Lead Agent deleting a task list).
  // Skips when the edit popup is open or the browser tab is hidden.
  useEffect(() => {
    if (!showCalendarModal || !calendarModalTeam) return;
    const emails = (calendarModalTeam.members || []).map(m => m.email).filter(Boolean);
    const connectionId = calendarModalTeam.connection_id || '';
    const INTERVAL = 10_000;
    let timerId = null;

    const poll = async () => {
      if (document.hidden || editingTaskRef.current) return;
      try {
        const records = await dataService.getTasksByMembers(emails, connectionId);
        if (records !== null) {
          setCalendarModalData(records.tasks
            .filter(r => !connectionId || r.connection_id === connectionId)
            .filter(r => !deletedTitlesRef.current.has(r.task_list_title))
            .map(r => {
              if (archivedTitlesRef.current.has(r.task_list_title)) return { ...r, archive: true };
              if (restoredTitlesRef.current.has(r.task_list_title)) return { ...r, archive: false };
              return r;
            }));
        }
      } catch { /* network hiccup — try again next tick */ }
    };

    const onVisibility = () => {
      if (document.hidden) { clearInterval(timerId); timerId = null; }
      else if (!timerId) timerId = setInterval(poll, INTERVAL);
    };

    timerId = setInterval(poll, INTERVAL);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timerId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [showCalendarModal, calendarModalTeam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Background polling while the team unpublished-changes modal is open: re-fetch every 10 s.
  // Skips when the edit popup is open or the browser tab is hidden.
  useEffect(() => {
    if (!teamUnpublishedModal) return;
    const INTERVAL = 10_000;
    const memberEmails = (teamUnpublishedModal.team?.members || []).map(m => m.email).filter(Boolean);
    const scopedSet = new Set(memberEmails.map(e => e.toLowerCase()));
    const pollTargetEmail = (isImpersonating && user?.role === 'owner') ? (user?.email ?? null) : null;
    let timerId = null;

    const poll = async () => {
      if (document.hidden || editingTaskRef.current) return;
      try {
        const result = await dataService.getPendingChanges(pollTargetEmail);
        if (!result) return;
        const pending = (result.tasks ?? []).filter(
          r => r.has_unpublished_changes === true && r.active !== false && !r.archive
            && scopedSet.has((r.id || '').toLowerCase())
        );
        setTeamUnpublishedRecords(pending);
      } catch { /* network hiccup — try again next tick */ }
    };

    const onVisibility = () => {
      if (document.hidden) {
        clearInterval(timerId);
        timerId = null;
      } else {
        if (!timerId) timerId = setInterval(poll, INTERVAL);
      }
    };

    timerId = setInterval(poll, INTERVAL);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timerId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [teamUnpublishedModal, isImpersonating, user?.email, user?.role]);

  // Reset init guard and baselines whenever the tasks modal closes so the next open re-seeds fresh
  useEffect(() => {
    if (!showTasksModal) {
      unpublishedInitRef.current = false;
      lastPublishedTasksRef.current = {};
    }
  }, [showTasksModal]);

  // Initialise unpublished-changes state from the backend on each tasks modal open
  useEffect(() => {
    if (!showTasksModal || unpublishedInitRef.current || tasksModalData.length === 0) return;
    unpublishedInitRef.current = true;
    const effectiveEmail = (isImpersonating ? impersonatedUser?.email : user?.email)?.toLowerCase();
    const initial = {};
    const initialTaskIds = {};
    const backfillMap = {};
    tasksModalData.forEach(r => {
      const title = r.task_list_title;
      // Skip records that belong to other users — their update_timestamp values are not this user's edits.
      if (effectiveEmail && r.id && r.id.toLowerCase() !== effectiveEmail) return;
      // A task is genuinely unpublished only if its update_timestamp is AFTER the last publish.
      // update_timestamp is never cleared by publish, so comparing against published_at is required.
      const publishedAt = r.published_at ? new Date(r.published_at) : null;
      const isUnpublishedChange = (t) =>
        !!t.update_timestamp && (!publishedAt || new Date(t.update_timestamp) > publishedAt);
      // Backfill task_id for legacy tasks that have genuinely new unpublished changes but no task_id.
      // Reuse the stable ID already in the baseline at the same index when available — generating
      // a fresh random ID on every poll causes the previous session's ID to appear DELETED in the diff.
      let tasks = r.tasks || [];
      if (tasks.some(t => isUnpublishedChange(t) && !t.task_id)) {
        const existingBase = lastPublishedTasksRef.current[title] || [];
        tasks = tasks.map((t, ti) =>
          (isUnpublishedChange(t) && !t.task_id)
            ? { ...t, task_id: (existingBase[ti] || {}).task_id || `tid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` }
            : t
        );
        backfillMap[title] = tasks;
      }
      // Only count tasks the current user personally has pending — tasks with update_timestamp are user-specific drafts.
      // Do NOT use r.has_unpublished_changes / r.unpublished_change_count, which are shared task-list-level flags
      // that reflect any team member's changes and would bleed other users' indicators into this user's view.
      const dirtyTasks = tasks.filter(t => t.task_id && isUnpublishedChange(t));
      if (dirtyTasks.length > 0) {
        initial[title] = { count: dirtyTasks.length, lastEditedAt: r.last_edited_at || null };
        initialTaskIds[title] = new Set(dirtyTasks.map(t => t.task_id));
      }
      // Re-seed the published baseline using backfilled tasks so diff can track them by ID.
      lastPublishedTasksRef.current[title] = tasks.map(t => ({ ...t }));
    });
    // Apply backfilled task_ids to tasksModalData state so cards render with their IDs.
    if (Object.keys(backfillMap).length > 0) {
      setTasksModalData(prev => prev.map(r => {
        const bf = backfillMap[r.task_list_title];
        return bf ? { ...r, tasks: bf } : r;
      }));
      Object.entries(backfillMap).forEach(([title, tasks]) => {
        lastPublishedTasksRef.current[title] = tasks.map(t => ({ ...t }));
      });
    }
    if (Object.keys(initial).length > 0) setUnpublishedChanges(prev => ({ ...prev, ...initial }));
    if (Object.keys(initialTaskIds).length > 0) {
      setUnpublishedTaskIds(prev => {
        const next = { ...prev };
        Object.entries(initialTaskIds).forEach(([title, ids]) => {
          next[title] = ids;
        });
        return next;
      });
    }
  }, [showTasksModal, tasksModalData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Merge backend unpublished-changes into state when calendar modal data loads
  useEffect(() => {
    if (!showCalendarModal || calendarModalData.length === 0) return;
    const effectiveEmail = (isImpersonating ? impersonatedUser?.email : user?.email)?.toLowerCase();
    const newChanges = {};
    const newTaskIds = {};
    const backfillMap = {};
    calendarModalData.forEach(r => {
      // Skip records that belong to other users — their update_timestamp values are not this user's edits.
      if (effectiveEmail && r.id && r.id.toLowerCase() !== effectiveEmail) return;
      // A task is genuinely unpublished only if its update_timestamp is AFTER the last publish.
      // update_timestamp is never cleared by publish, so comparing against published_at is required.
      const publishedAt = r.published_at ? new Date(r.published_at) : null;
      const isUnpublishedChange = (t) =>
        !!t.update_timestamp && (!publishedAt || new Date(t.update_timestamp) > publishedAt);
      // Backfill task_id for legacy tasks that have genuinely new unpublished changes but no task_id.
      // Reuse the stable ID already in the baseline at the same index when available — generating
      // a fresh random ID on every poll causes the previous session's ID to appear DELETED in the diff.
      let tasks = r.tasks || [];
      if (tasks.some(t => isUnpublishedChange(t) && !t.task_id)) {
        const existingBase = lastPublishedTasksRef.current[r.task_list_title] || [];
        tasks = tasks.map((t, ti) =>
          (isUnpublishedChange(t) && !t.task_id)
            ? { ...t, task_id: (existingBase[ti] || {}).task_id || `tid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` }
            : t
        );
        backfillMap[r.task_list_title] = tasks;
      }
      // Only count tasks the current user personally has pending — tasks with update_timestamp are user-specific drafts.
      // Do NOT use r.has_unpublished_changes / r.unpublished_change_count, which are shared task-list-level flags
      // that reflect any team member's changes and would bleed other users' indicators into this user's view.
      const dirtyTasks = tasks.filter(t => t.task_id && isUnpublishedChange(t));
      if (dirtyTasks.length > 0) {
        newChanges[r.task_list_title] = { count: dirtyTasks.length, lastEditedAt: r.last_edited_at || null };
        newTaskIds[r.task_list_title] = new Set(dirtyTasks.map(t => t.task_id));
      }
    });
    // Apply backfilled task_ids to calendarModalData state so cards render with their IDs.
    if (Object.keys(backfillMap).length > 0) {
      setCalendarModalData(prev => prev.map(r => {
        const bf = backfillMap[r.task_list_title];
        return bf ? { ...r, tasks: bf } : r;
      }));
    }
    if (Object.keys(newChanges).length === 0) return;
    setUnpublishedChanges(prev => {
      const merged = { ...prev };
      let changed = false;
      Object.entries(newChanges).forEach(([title, info]) => {
        if (!merged[title]) { merged[title] = info; changed = true; }
      });
      return changed ? merged : prev;
    });
    setUnpublishedTaskIds(prev => {
      const next = { ...prev };
      let changed = false;
      Object.entries(newTaskIds).forEach(([title, ids]) => {
        if (!next[title]) { next[title] = new Set(ids); changed = true; }
      });
      return changed ? next : prev;
    });
  }, [showCalendarModal, calendarModalData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the acting user's personal calendar auth status once on login
  useEffect(() => {
    if (!user) { setSelfCalendarStatus(null); return; }
    apiClient.get('/api/calendar/auth/status').then(res => {
      setSelfCalendarStatus({
        enabled: res.data.calendar_sync_enabled !== false,
        connected: !!res.data.connected,
        provider: res.data.provider || null,
      });
    }).catch(() => {
      setSelfCalendarStatus({ enabled: false, connected: false, provider: null });
    });
  }, [user]);

  // Keep ref in sync with state so event handlers always read the latest value
  useEffect(() => {
    unpublishedChangesRef.current = unpublishedChanges;
  }, [unpublishedChanges]);

  // Warn before browser close / tab close when there are unpublished changes
  useEffect(() => {
    const hasAny = Object.values(unpublishedChanges).some(v => v.count > 0);
    const handler = (e) => {
      if (!hasAny) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [unpublishedChanges]);

  // Handle removing a team
  const handleRemoveTeam = (team) => {
    // Determine the correct team type
    let type = 'small_team'; // default
    if (team.team_type === 'large_team' || team.account_type === 'large_team') {
      type = 'large_team';
    } else if (team.team_type === 'medium_team' || team.account_type === 'medium_team') {
      type = 'medium_team';
    } else if (team.team_type === 'small_team' || team.account_type === 'small_team') {
      type = 'small_team';
    }
    setDeleteType(type);
    setItemToDelete(team);
    setShowDeleteConfirmModal(true);
  };

  // Handle removing a solo
  const handleRemoveSolo = (solo) => {
    setDeleteType('solo');
    setItemToDelete(solo);
    setShowDeleteConfirmModal(true);
  };

  // Handle removing a solo+assistant
  const handleRemoveSoloAssistant = (soloAssistant) => {
    setDeleteType('solo_assistant');
    setItemToDelete(soloAssistant);
    setShowDeleteConfirmModal(true);
  };

  // Confirm deletion
  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      let result;
      // Use connection_id as the unique identifier (not id)
      const connectionId = itemToDelete.connection_id || itemToDelete.id;
      
      if (deleteType === 'small_team' || deleteType === 'medium_team' || deleteType === 'large_team') {
        result = await dataService.removeConnection(deleteType, connectionId);
      } else if (deleteType === 'solo') {
        result = await dataService.removeConnection('solo', connectionId);
      } else if (deleteType === 'individual') {
        result = await dataService.removeConnection('individual', connectionId);
      } else if (deleteType === 'solo_assistant') {
        result = await dataService.removeConnection('solo_assistant', connectionId);
      }
      
      if (result && result.success) {
        console.log('Connection deleted successfully:', result.message);
        
        // Emit event to notify other components of connection deletion
        window.dispatchEvent(new CustomEvent('connection-deleted', {
          detail: {
            connectionId: connectionId,
            deleteType: deleteType
          }
        }));
        
        // Force refresh with cache invalidation
        await loadData(true);
      } else {
        console.error('Failed to delete connection:', result?.message);
        // Could add error handling here
      }
    } catch (error) {
      console.error('Error deleting connection:', error);
    }
    
    setShowDeleteConfirmModal(false);
    setItemToDelete(null);
    setDeleteType('');
  };

  // Cancel deletion
  const cancelDelete = () => {
    setShowDeleteConfirmModal(false);
    setItemToDelete(null);
    setDeleteType('');
  };

  // ...existing code...

  // Billing modal state is now managed globally in App.js

  // ── Tasks modal view navigation ──────────────────────────────────────────────
  const tasksMatchesPeriod = (task) => {
    if (!task.due) return false;
    const due = new Date(task.due);
    if (tasksView === 'month') return calIsSameMonth(due, tasksViewDate);
    if (tasksView === 'week') { const ws = calStartOfWeek(tasksViewDate); return due >= ws && due < calAddDays(ws, 7); }
    if (tasksView === 'day') return calIsSameDay(due, tasksViewDate);
    return false;
  };
  const effectiveTasksTab = tasksCanArchive ? tasksActiveTab : 'active';
  const tasksDisplayRecords = tasksModalData.filter(r =>
    r.active !== false &&
    (effectiveTasksTab === 'active' ? !r.archive : r.archive === true)
  );
  const tasksHasVisible = tasksView === 'allTime' || tasksDisplayRecords.some(r => (r.tasks || []).some(tasksMatchesPeriod));
  const tasksPeriodLabel = tasksView === 'month' ? calFormatMonthYear(tasksViewDate)
    : tasksView === 'week' ? calFormatWeekRange(tasksViewDate)
    : tasksView === 'day' ? calFormatDayFull(tasksViewDate) : null;

  return (
    <div className="App">
  <NavBar 
    onSignInClick={() => setShowAuthModal(true)} 
    onProfileClick={onProfileClick}
    onBillingClick={onBillingClick}
    onSeatsUpdate={onSeatsUpdate}
    {...(FEATURE_FLAGS.PHONE_FEATURE_ENABLED && { onPhoneSlotsUpdate: handlePhoneSlotsUpdate })}
  />
      <AuthModal showAuthModal={showAuthModal} setShowAuthModal={setShowAuthModal} />

      
  <header className="App-header" style={{
        background: 'linear-gradient(180deg, #eef2f0 0%, #e4ece8 100%)',
        borderBottom: '1px solid #c8d8d2',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.07)',
        borderRadius: 0,
        margin: 0,
        padding: '0 20px',
        minHeight: 42,
        flexShrink: 0,
      }}>
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', position: 'relative' }}>
          {user && (user.accountType || isLeadAgentManager) && (
            <div style={{
              position: 'absolute',
              left: 0,
              background: '#dcfce7',
              color: '#15803d',
              padding: '3px 10px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              border: '1px solid #bbf7d0',
              letterSpacing: '0.01em',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              {isLeadAgentManager ? (
                <span>Member Account</span>
              ) : (
                <>
                  <span>
                    {effectiveAccountType === 'Solo' ? 'Solo Account' :
                     effectiveAccountType === 'Solo_Assistant' ? 'Solo + Assistant' :
                     effectiveAccountType === 'Trial' ? '14-Day Free Trial' :
                     effectiveAccountType === 'Brokerage' ? 'Brokerage Account' :
                     effectiveAccountType === 'Super' ? 'Super Account' :
                     effectiveAccountType
                       ? effectiveAccountType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                       : ''}
                    {billingStatus === 'trial' && (
                      <span className="trial-badge" style={{ marginLeft: 4 }}>FREE TRIAL</span>
                    )}
                  </span>
                  <span style={{
                    background: availableSeats === 0 ? '#fee2e2' : '#d1fae5',
                    color: availableSeats === 0 ? '#dc2626' : '#15803d',
                    borderRadius: 999,
                    padding: '1px 7px',
                    fontWeight: 600,
                    fontSize: 12,
                    border: `1px solid ${availableSeats === 0 ? '#fca5a5' : '#a7f3d0'}`,
                  }}>
                    {availableSeats} seat{availableSeats !== 1 ? 's' : ''} available
                  </span>
                  <span style={{ color: '#64748b', fontSize: 11 }}>
                    (Max: {seatLimit})
                  </span>
                </>
              )}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 7,
              background: '#d1fae5', color: '#047857',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16,
            }}>👥</div>
            <h2 style={{
              margin: 0, color: '#1e3a2f',
              fontSize: 18, fontWeight: 700,
              letterSpacing: '-0.01em',
            }}>Teams</h2>
          </div>

          {!isLeadAgentManager && <Button
            variant="primary"
            onClick={handleAddClick}
            disabled={isAddConnectionDisabled()}
            style={{
              background: isAddConnectionDisabled()
                ? '#e2e8f0'
                : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              border: 0,
              color: isAddConnectionDisabled() ? '#94a3b8' : '#ffffff',
              boxShadow: isAddConnectionDisabled()
                ? 'none'
                : '0 4px 12px rgba(5, 150, 105, 0.28)',
              transition: 'all 0.2s ease',
              fontWeight: 600,
              padding: '5px 14px',
              borderRadius: 8,
              fontSize: 13,
              position: 'absolute',
              right: 0,
              cursor: isAddConnectionDisabled() ? 'not-allowed' : 'pointer',
              opacity: isAddConnectionDisabled() ? 0.55 : 1,
            }}
            onMouseOver={(e) => {
              if (!isAddConnectionDisabled()) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #047857 0%, #065f46 100%)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(5, 150, 105, 0.36)';
              }
            }}
            onMouseOut={(e) => {
              if (!isAddConnectionDisabled()) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.28)';
              }
            }}
          >
            {isAddConnectionDisabled() ? 'Connection Complete' : '+ Add Team'}
          </Button>}
        </div>
      </header>
      
      <Container className="mt-2" style={{ position: 'relative' }}>
        {/* Modal-style loading popup — overlays content area only, navbar stays visible */}
        {(loading || initialLoad) && (
          <>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.5; }
              }
            `}</style>
            {/* Backdrop over content area only */}
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.45)',
              borderRadius: '8px',
              zIndex: 500,
              minHeight: '200px'
            }} />
            {/* Floating card — fixed to viewport center but below navbar */}
            <div style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 501,
              backgroundColor: 'white',
              padding: '25px 35px',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '15px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '2px solid #007bff',
              minWidth: '280px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{
                  width: '32px', height: '32px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #007bff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>
                  {initialLoad ? 'Loading connections...' : 'Refreshing data...'}
                </span>
              </div>
              {initialLoad && (
                <div style={{ fontSize: '14px', color: '#666', textAlign: 'center', animation: 'pulse 2s infinite' }}>
                  Optimizing for your account type...
                </div>
              )}
            </div>
          </>
        )}

        {/* API Error Banner */}
        {apiError && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{
              color: '#856404',
              fontSize: '18px'
            }}>⚠️</div>
            <div style={{
              color: '#856404',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {apiError}
            </div>
          </div>
        )}
        
        <Nav variant="tabs" className="custom-tabs">
          {getVisibleTabs.includes('large_team') && (
            <Nav.Item>
              <Nav.Link
                className={`custom-tab ${activeTab === 'large_team' ? 'active' : ''}`}
                active={activeTab === 'large_team'}
                onClick={() => handleTabChange('large_team')}
              >
                <span className="tab-icon">
                  <FaUser style={{ fontSize: '14px', marginRight: '2px' }} />
                      <FaUser style={{ fontSize: '16px', marginRight: '2px' }} />
                      <FaUser style={{ fontSize: '14px' }} />
                    </span>
                    Large Team
                  </Nav.Link>
                </Nav.Item>
              )}
              {getVisibleTabs.includes('medium_team') && (
                <Nav.Item>
                  <Nav.Link
                    className={`custom-tab ${activeTab === 'medium_team' ? 'active' : ''}`}
                    active={activeTab === 'medium_team'}
                    onClick={() => handleTabChange('medium_team')}
                  >
                    <span className="tab-icon">
                      <FaUser style={{ fontSize: '14px', marginRight: '2px' }} />
                      <FaUser style={{ fontSize: '16px', marginRight: '2px' }} />
                      <FaUser style={{ fontSize: '14px' }} />
                    </span>
                    Medium Team
                  </Nav.Link>
                </Nav.Item>
              )}
              {getVisibleTabs.includes('small_team') && (
                <Nav.Item>
                  <Nav.Link
                    className={`custom-tab ${activeTab === 'small_team' ? 'active' : ''}`}
                    active={activeTab === 'small_team'}
                    onClick={() => handleTabChange('small_team')}
                  >
                    <span className="tab-icon">
                      <FaUser style={{ fontSize: '14px', marginRight: '2px' }} />
                      <FaUser style={{ fontSize: '16px', marginRight: '2px' }} />
                      <FaUser style={{ fontSize: '14px' }} />
                    </span>
                    Small Team
                  </Nav.Link>
                </Nav.Item>
              )}
              {getVisibleTabs.includes('soloAgents') && (
                <Nav.Item>
                  <Nav.Link 
                    className={`custom-tab ${activeTab === 'soloAgents' ? 'active' : ''}`}
                    active={activeTab === 'soloAgents'} 
                    onClick={() => handleTabChange('soloAgents')}
                  >
                    <span className="tab-icon">
                      <FaUser style={{ fontSize: '12px', marginRight: '1px' }} />
                      <span style={{ fontSize: '10px', color: '#28a745', marginRight: '1px' }}>+</span>
                      <FaUser style={{ fontSize: '10px', color: '#28a745' }} />
                    </span>
                    Solo + Assistant
                  </Nav.Link>
                </Nav.Item>
              )}
              {getVisibleTabs.includes('solos') && (
                <Nav.Item>
                  <Nav.Link 
                    className={`custom-tab ${activeTab === 'solos' ? 'active' : ''}`}
                    active={activeTab === 'solos'} 
                    onClick={() => handleTabChange('solos')}
                  >
                    <span className="tab-icon">
                      <FaUser style={{ fontSize: '16px' }} />
                    </span>
                    Solo
                  </Nav.Link>
                </Nav.Item>
              )}
              {getVisibleTabs.includes('trial') && (
                <Nav.Item>
                  <Nav.Link 
                    className={`custom-tab ${activeTab === 'trial' ? 'active' : ''}`}
                    active={activeTab === 'trial'} 
                    onClick={() => handleTabChange('trial')}
                  >
                    <span className="tab-icon">
                      <FaGift style={{ fontSize: '16px' }} />
                    </span>
                    Trial
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>

            {/* Safety check: If no tab is active yet, show loading or auto-select first visible tab */}
            {!activeTab && getVisibleTabs.length > 0 && (() => {
              // Auto-select first visible tab if none is active
              const firstTab = getVisibleTabs[0];
              console.log('[DEBUG] No active tab, auto-selecting:', firstTab);
              setTimeout(() => setActiveTab(firstTab), 0);
              return (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p style={{ marginTop: '10px' }}>Loading your calendar connections...</p>
                </div>
              );
            })()}

            {activeTab === 'small_team' && (
              <div className="tab-content-container">
                {getFilteredTeams().filter(team => team.team_type === 'small_team').length > 0 ? (
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Team / Admin</th>
                        <th>Members</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredTeams().filter(team => team.team_type === 'small_team').map((team, index) => {
                        // Debug: Log team info to help troubleshoot missing teams
                        console.log(`[DEBUG] Processing small_team ${index}:`, {
                          team_name: team.team_name,
                          team_type: team.team_type,
                          members_count: team.members?.length || 0,
                          member_roles: team.members?.map(m => ({ email: m.email, role: m.role })) || []
                        });
                        
                        const admin = team.members?.find(member => member.role === 'team_lead' || member.role === 'single_agent' || member.role === 'primary');
                        // Show all members EXCEPT the admin
                        const members = team.members?.filter(member => {
                          const isAdmin = (member.role === 'team_lead' || member.role === 'single_agent' || member.role === 'primary');
                          return !isAdmin;
                        }) || [];
                        
                        console.log(`[DEBUG] Filtered results for ${team.team_name}:`, {
                          admin_found: !!admin,
                          admin_role: admin?.role,
                          admin_email: admin?.email,
                          members_found: members.length,
                          member_emails: members.map(m => m.email),
                          all_member_roles: members.map(m => m.role)
                        });
                        return (
                          <tr key={index}>
                            <td>
                              <div>
                                <strong>{team.team_name}</strong>
                                <br />
                                <span style={{ color: '#007bff' }}>{admin ? admin.email : 'N/A'}</span>
                              </div>
                            </td>
                            <td>
                              {members.length > 0 ? (
                                <div>
                                  {members.map((member, memberIndex) => (
                                    <div key={memberIndex} style={{ marginBottom: memberIndex < members.length - 1 ? '8px' : '0' }}>
                                      <span>{member.email}</span>
                                      <br />
                                      <small style={{ color: (member.role === 'assistant' || member.role === 'secondary') ? '#28a745' : '#666', textTransform: 'capitalize', fontWeight: (member.role === 'assistant' || member.role === 'secondary') ? '500' : 'normal' }}>
                                        {member.role === 'assistant' ? '🤝 Assistant' : member.role === 'secondary' ? '🤝 Team Member' : member.role === 'team_member' ? 'Team Member' : (member.role || 'member').replace('_', ' ')} • {member.client}
                                      </small>
                                    </div>
                                  ))}
                                </div>
                              ) : 'No members'}
                            </td>
                            <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                              {team._canManageThisTeam && (
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleEditTeam(team)}
                                className="me-2"
                                style={{
                                  background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                                  border: 'none',
                                  color: '#333',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Edit
                              </Button>
                              )}
                              {team._isOwnedByCurrentUser && (
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleRemoveTeam(team)}
                                className="me-2"
                                style={{
                                  background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                                  border: 'none',
                                  color: '#333',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Remove
                              </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleViewTasks(team)}
                                style={{
                                  background: 'linear-gradient(90deg, #3a7bd5 0%, #5a9bf5 40%, #2a6bc5 100%)',
                                  border: 'none',
                                  color: '#fff',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #5a9bf5 0%, #7ab5ff 40%, #4a8be5 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #3a7bd5 0%, #5a9bf5 40%, #2a6bc5 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Tasks
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleViewCalendar(team)}
                                style={{
                                  background: 'linear-gradient(90deg, #0f9d58 0%, #34a853 40%, #0b8a4e 100%)',
                                  border: 'none',
                                  color: '#fff',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  marginLeft: '8px',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #34a853 0%, #57bb75 40%, #2a9a50 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #0f9d58 0%, #34a853 40%, #0b8a4e 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Calendar
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => openTeamUnpublishedModal(team)}
                                style={{
                                  background: 'linear-gradient(90deg, #b45309 0%, #d97706 40%, #92400e 100%)',
                                  border: 'none',
                                  color: '#fff',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  marginLeft: '8px',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #d97706 0%, #f59e0b 40%, #b45309 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #b45309 0%, #d97706 40%, #92400e 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Unshared
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center p-4">
                    <p>Click "Add Connection" to get started.</p>
                  </div>
                )}
              </div>
            )}
            {activeTab === 'medium_team' && (
              <div className="tab-content-container">
                {getFilteredTeams().filter(team => team.team_type === 'medium_team').length > 0 ? (
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Team / Admin</th>
                        <th>Members</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredTeams().filter(team => team.team_type === 'medium_team').map((team, index) => {
                        const admin = team.members?.find(member => member.role === 'team_lead' || member.role === 'single_agent' || member.role === 'primary');
                        // Show all members EXCEPT the admin
                        const members = team.members?.filter(member => {
                          const isAdmin = (member.role === 'team_lead' || member.role === 'single_agent' || member.role === 'primary');
                          return !isAdmin;
                        }) || [];

                        return (
                          <tr key={index}>
                            <td>
                              <div>
                                <div style={{ fontSize: '14px', fontWeight: '500' }}>
                                  {team.team_name || `Team ${index + 1}`}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                  Admin: {admin ? `${admin.first_name} ${admin.last_name}` : 'No admin assigned'}
                                </div>
                              </div>
                            </td>
                            <td>
                              {members.length > 0 ? (
                                <div>
                                  {members.map((member, memberIndex) => (
                                    <div key={memberIndex} style={{ marginBottom: memberIndex < members.length - 1 ? '8px' : '0' }}>
                                      <span>{member.email}</span>
                                      <br />
                                      <small style={{ color: (member.role === 'assistant' || member.role === 'secondary') ? '#28a745' : '#666', textTransform: 'capitalize', fontWeight: (member.role === 'assistant' || member.role === 'secondary') ? '500' : 'normal' }}>
                                        {member.role === 'assistant' ? '🤝 Assistant' : member.role === 'secondary' ? '🤝 Team Member' : member.role === 'team_member' ? 'Team Member' : (member.role || 'member').replace('_', ' ')} • {member.client}
                                      </small>
                                    </div>
                                  ))}
                                </div>
                              ) : 'No members'}
                            </td>
                            <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                              {team._canManageThisTeam && (
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleEditTeam(team)}
                                className="me-2"
                                style={{
                                  background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                                  border: 'none',
                                  color: '#333',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Edit
                              </Button>
                              )}
                              {team._isOwnedByCurrentUser && (
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleRemoveTeam(team)}
                                className="me-2"
                                style={{
                                  background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                                  border: 'none',
                                  color: '#333',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Remove
                              </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleViewTasks(team)}
                                style={{
                                  background: 'linear-gradient(90deg, #3a7bd5 0%, #5a9bf5 40%, #2a6bc5 100%)',
                                  border: 'none',
                                  color: '#fff',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #5a9bf5 0%, #7ab5ff 40%, #4a8be5 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #3a7bd5 0%, #5a9bf5 40%, #2a6bc5 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Tasks
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleViewCalendar(team)}
                                style={{
                                  background: 'linear-gradient(90deg, #0f9d58 0%, #34a853 40%, #0b8a4e 100%)',
                                  border: 'none',
                                  color: '#fff',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  marginLeft: '8px',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #34a853 0%, #57bb75 40%, #2a9a50 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #0f9d58 0%, #34a853 40%, #0b8a4e 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Calendar
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => openTeamUnpublishedModal(team)}
                                style={{
                                  background: 'linear-gradient(90deg, #b45309 0%, #d97706 40%, #92400e 100%)',
                                  border: 'none',
                                  color: '#fff',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  marginLeft: '8px',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #d97706 0%, #f59e0b 40%, #b45309 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #b45309 0%, #d97706 40%, #92400e 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Unshared
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center p-4">
                    <p>Click "Add Connection" to get started.</p>
                  </div>
                )}
              </div>
            )}
            {activeTab === 'large_team' && (
              <div className="tab-content-container">
                {getFilteredTeams().filter(team => team.team_type === 'large_team').length > 0 ? (
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Team / Admin</th>
                        <th>Members</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredTeams().filter(team => team.team_type === 'large_team').map((team, index) => {
                        const admin = team.members?.find(member => member.role === 'team_lead' || member.role === 'single_agent' || member.role === 'primary');
                        // Show all members EXCEPT the admin
                        const members = team.members?.filter(member => {
                          const isAdmin = (member.role === 'team_lead' || member.role === 'single_agent' || member.role === 'primary');
                          return !isAdmin;
                        }) || [];
                        return (
                          <tr key={index}>
                            <td>
                              <div>
                                <strong>{team.team_name}</strong>
                                <br />
                                <span style={{ color: '#007bff' }}>{admin ? admin.email : 'N/A'}</span>
                              </div>
                            </td>
                            <td>
                              {members.length > 0 ? (
                                <div>
                                  {members.map((member, memberIndex) => (
                                    <div key={memberIndex} style={{ marginBottom: memberIndex < members.length - 1 ? '8px' : '0' }}>
                                      <span>{member.email}</span>
                                      <br />
                                      <small style={{ color: (member.role === 'assistant' || member.role === 'secondary') ? '#28a745' : '#666', textTransform: 'capitalize', fontWeight: (member.role === 'assistant' || member.role === 'secondary') ? '500' : 'normal' }}>
                                        {member.role === 'assistant' ? '🤝 Assistant' : member.role === 'secondary' ? '🤝 Team Member' : member.role === 'team_member' ? 'Team Member' : (member.role || 'member').replace('_', ' ')} • {member.client}
                                      </small>
                                    </div>
                                  ))}
                                </div>
                              ) : 'No members'}
                            </td>
                            <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                              {team._canManageThisTeam && (
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleEditTeam(team)}
                                className="me-2"
                                style={{
                                  background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                                  border: 'none',
                                  color: '#333',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Edit
                              </Button>
                              )}
                              {team._isOwnedByCurrentUser && (
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleRemoveTeam(team)}
                                className="me-2"
                                style={{
                                  background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                                  border: 'none',
                                  color: '#333',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Remove
                              </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleViewTasks(team)}
                                style={{
                                  background: 'linear-gradient(90deg, #3a7bd5 0%, #5a9bf5 40%, #2a6bc5 100%)',
                                  border: 'none',
                                  color: '#fff',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #5a9bf5 0%, #7ab5ff 40%, #4a8be5 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #3a7bd5 0%, #5a9bf5 40%, #2a6bc5 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Tasks
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleViewCalendar(team)}
                                style={{
                                  background: 'linear-gradient(90deg, #0f9d58 0%, #34a853 40%, #0b8a4e 100%)',
                                  border: 'none',
                                  color: '#fff',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  marginLeft: '8px',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #34a853 0%, #57bb75 40%, #2a9a50 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #0f9d58 0%, #34a853 40%, #0b8a4e 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Calendar
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => openTeamUnpublishedModal(team)}
                                style={{
                                  background: 'linear-gradient(90deg, #b45309 0%, #d97706 40%, #92400e 100%)',
                                  border: 'none',
                                  color: '#fff',
                                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                  marginLeft: '8px',
                                  transition: 'all 0.2s ease-in-out',
                                  borderRadius: '4px',
                                  fontWeight: '500',
                                  padding: '4px 12px'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #d97706 0%, #f59e0b 40%, #b45309 100%)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = 'linear-gradient(90deg, #b45309 0%, #d97706 40%, #92400e 100%)';
                                  e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                Unshared
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center p-4">
                    <p>Click "Add Connection" to get started.</p>
                  </div>
                )}
              </div>
            )}

              {activeTab === 'soloAgents' && (
                <div className="tab-content-container">
                  {getFilteredSoloAgents().length > 0 ? (
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Agent / Type</th>
                          <th>Assistant</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredSoloAgents().map((agent, index) => {
                          const agentUser = agent.members.find(member => member.role === 'single_agent');
                          const assistant = agent.members.find(member => member.role === 'assistant');
                          
                          return (
                            <tr key={index}>
                              <td>
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                    <strong>{agent.agent_name}</strong>
                                    {agent.team_type === 'single_agent_with_assistant' && (
                                      <span style={{ 
                                        backgroundColor: '#28a745', 
                                        color: 'white', 
                                        fontSize: '10px', 
                                        padding: '2px 6px', 
                                        borderRadius: '10px', 
                                        marginLeft: '8px',
                                        fontWeight: '600'
                                      }}>
                                        + ASSISTANT
                                      </span>
                                    )}
                                    {agent.team_type === 'single_agent_no_assistant' && (
                                      <span style={{ 
                                        backgroundColor: '#6c757d', 
                                        color: 'white', 
                                        fontSize: '10px', 
                                        padding: '2px 6px', 
                                        borderRadius: '10px', 
                                        marginLeft: '8px',
                                        fontWeight: '600'
                                      }}>
                                        SOLO
                                      </span>
                                    )}
                                  </div>
                                  <small style={{ color: '#666' }}>
                                    {agent.team_type === 'single_agent_with_assistant' ? 'Agent + Assistant' : 'Solo Agent'} • 
                                    {agent.current_members}/{agent.max_members} members • {agent.calendars_count} calendars
                                  </small>
                                  <br />
                                  <span style={{ color: '#007bff' }}>{agentUser ? agentUser.email : 'N/A'}</span>
                                </div>
                              </td>
                              <td>
                                {assistant ? (
                                  <div>
                                    <span style={{ color: '#28a745', fontWeight: '500' }}>
                                      🤝 {assistant.email}
                                    </span>
                                    <br />
                                    <small style={{ color: '#666', textTransform: 'capitalize' }}>
                                      Assistant • {assistant.client}
                                    </small>
                                  </div>
                                ) : (
                                  <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                                    No Assistant
                                  </span>
                                )}
                              </td>
                              <td>
                                <Button 
                                  size="sm" 
                                  variant="outline-primary" 
                                  onClick={() => handleEditSoloAgent(agent)}
                                  className="me-2"
                                  style={{ 
                                    background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                                    border: 'none',
                                    color: '#333',
                                    boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                    transition: 'all 0.2s ease-in-out',
                                    borderRadius: '4px',
                                    fontWeight: '500',
                                    padding: '4px 12px'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
                                    e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline-danger" 
                                  onClick={() => handleRemoveSoloAssistant(agent)}
                                  style={{ 
                                    background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                                    border: 'none',
                                    color: '#333',
                                    boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                    transition: 'all 0.2s ease-in-out',
                                    borderRadius: '4px',
                                    fontWeight: '500',
                                    padding: '4px 12px'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
                                    e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }}
                                >
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center p-4">
                      <p>Click "Add Connection" to get started.</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'solos' && (
                <div className="tab-content-container">
                  {solos.length > 0 ? (
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Agent / Plan</th>
                          <th>Calendar Provider</th>
                          <th>Phone</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {solos
                          .filter(solo => {
                            // Only show solo connections where the current user is the owner
                            if (!user || !user.email) {
                              console.log('[DEBUG] Filtering: No user or user email');
                              return false;
                            }
                            
                            // Exclude suspended connections
                            if (solo.status === 'suspended') {
                              console.log(`[DEBUG] Excluding suspended connection: ${solo.name || 'Unnamed'}`);
                              return false;
                            }
                            
                            const userEmail = (user.email || '').toLowerCase().trim();
                            const ownerEmail = ((solo.owner && solo.owner.email) || '').toLowerCase().trim();
                            // For legacy connections without owner field, check members array
                            let matchesUser = ownerEmail === userEmail;
                            
                            if (!matchesUser && (!solo.owner || !solo.owner.email) && solo.members && solo.members.length > 0) {
                              // Fallback: check if user email matches first member (for legacy connections)
                              const firstMemberEmail = (solo.members[0].email || '').toLowerCase().trim();
                              matchesUser = firstMemberEmail === userEmail;
                              console.log('[DEBUG] Legacy connection fallback:', {
                                connectionId: solo.connection_id || solo.id,
                                firstMemberEmail,
                                userEmail,
                                legacyMatch: matchesUser
                              });
                            }
                            
                            // Handle migrated connections - check if this connection should show in solos tab
                            let shouldShowInSolosTab = true;
                            if (solo.is_migrated) {
                              const connectionType = solo.connection_type?.toLowerCase();
                              const accountType = solo.account_type?.toLowerCase();
                              // Only show in solos tab if connection_type or account_type is 'solo'
                              shouldShowInSolosTab = (connectionType === 'solo' || accountType === 'solo');
                              
                              console.log('[DEBUG] Migrated solo connection check:', {
                                connectionId: solo.connection_id || solo.id,
                                is_migrated: solo.is_migrated,
                                connection_type: connectionType,
                                account_type: accountType,
                                original_type: solo.original_type,
                                shouldShowInSolosTab
                              });
                            }
                            
                            const finalMatch = matchesUser && shouldShowInSolosTab;
                            
                            console.log('[DEBUG] Filtering solo:', {
                              connectionId: solo.connection_id || solo.id,
                              accountType: solo.account_type,
                              hasOwner: !!solo.owner,
                              ownerEmail,
                              userEmail,
                              is_migrated: solo.is_migrated,
                              shouldShowInSolosTab,
                              matchesUser,
                              finalMatch
                            });
                            return finalMatch;
                          })
                          .map((solo, index) => {
                          const member = solo.members && solo.members.length > 0 ? solo.members[0] : {};
                          return (
                            <tr key={index}>
                              <td>
                                <div>
                                  <span style={{ color: '#007bff', fontWeight: '500' }}>{member.email || ''}</span>
                                  <br />
                                  <small style={{ color: '#666' }}>
                                    {solo.account_type === 'trial' ? 'Trial User' : 'Solo Agent'} •
                                    {solo.billing_tier === 'solo_pro' ? ' Pro Plan' :
                                     solo.billing_tier === 'solo_basic' ? ' Basic Plan' :
                                     solo.account_type === 'trial' ? ' 14-Day Free Trial' : ' Trial'} •
                                    {solo.subscription_status === 'active' ? ' Active' :
                                     solo.subscription_status === 'trial' ? ' Trial' : 
                                     solo.account_type === 'trial' ? ' Active' : ' Inactive'}
                                  </small>
                                </div>
                              </td>
                              <td>
                                <div>
                                  {member.client === 'google' && 'Google'}
                                  {member.client === 'outlook' && 'Outlook'}
                                  {member.client === 'icloud' && 'iCloud'}
                                  <br />
                                  <small style={{ color: '#666' }}>
                                    {solo.calendars_count} calendar{solo.calendars_count !== 1 ? 's' : ''}
                                  </small>
                                </div>
                              </td>
                              <td>{member.phone || 'Not provided'}</td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => handleEditSolo(solo)}
                                  className="me-2"
                                  style={{
                                    background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                                    border: 'none',
                                    color: '#333',
                                    boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                    transition: 'all 0.2s ease-in-out',
                                    borderRadius: '4px',
                                    fontWeight: '500',
                                    padding: '4px 12px'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
                                    e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleRemoveSolo(solo)}
                                  style={{
                                    background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                                    border: 'none',
                                    color: '#333',
                                    boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                    transition: 'all 0.2s ease-in-out',
                                    borderRadius: '4px',
                                    fontWeight: '500',
                                    padding: '4px 12px'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
                                    e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }}
                                >
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center p-4">
                      <p>
                        Click "Add Connection" to get started.
                      </p>
                    </div>
                )}
              </div>
            )}

            {activeTab === 'trial' && (
              <div className="tab-content-container">
                {trials.length > 0 ? (
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Agent / Plan</th>
                        <th>Calendar Provider</th>
                        <th>Phone</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trials
                        .filter(trial => {
                          // Only show trial connections where the current user is the owner
                          if (!user || !user.email) {
                            console.log('[DEBUG] Filtering: No user or user email');
                            return false;
                          }
                          
                          // Exclude suspended connections
                          if (trial.status === 'suspended') {
                            console.log(`[DEBUG] Excluding suspended connection: ${trial.name || 'Unnamed'}`);
                            return false;
                          }
                          
                          const userEmail = (user.email || '').toLowerCase().trim();
                          const ownerEmail = ((trial.owner && trial.owner.email) || '').toLowerCase().trim();
                          // For legacy connections without owner field, check members array
                          let matchesUser = ownerEmail === userEmail;
                          
                          if (!matchesUser && (!trial.owner || !trial.owner.email) && trial.members && trial.members.length > 0) {
                            // Fallback: check if user email matches first member (for legacy connections)
                            const firstMemberEmail = (trial.members[0].email || '').toLowerCase().trim();
                            matchesUser = firstMemberEmail === userEmail;
                          }
                          
                          console.log(`[DEBUG] Trial filtering: userEmail=${userEmail}, ownerEmail=${ownerEmail}, matchesUser=${matchesUser}, status=${trial.status}`);
                          return matchesUser;
                        })
                        .map((trial, index) => {
                          const member = trial.members[0];
                          return (
                            <tr key={index}>
                              <td>
                                <div>
                                  <strong>{member.email}</strong>
                                  <br />
                                  <small style={{ color: '#666' }}>Trial Plan (14-day free trial)</small>
                                </div>
                              </td>
                              <td>
                                <div>
                                  {member.client === 'google' && 'Google'}
                                  {member.client === 'outlook' && 'Outlook'}
                                  {member.client === 'icloud' && 'iCloud'}
                                  <br />
                                  <small style={{ color: '#666' }}>
                                    {trial.calendars_count} calendar{trial.calendars_count !== 1 ? 's' : ''}
                                  </small>
                                </div>
                              </td>
                              <td>{member.phone || 'Not provided'}</td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => handleEditSolo(trial)}
                                  className="me-2"
                                  style={{
                                    background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                                    border: 'none',
                                    color: '#333',
                                    boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                    transition: 'all 0.2s ease-in-out',
                                    borderRadius: '4px',
                                    fontWeight: '500',
                                    padding: '4px 12px'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
                                    e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleRemoveSolo(trial)}
                                  style={{
                                    background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                                    border: 'none',
                                    color: '#333',
                                    boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                                    transition: 'all 0.2s ease-in-out',
                                    borderRadius: '4px',
                                    fontWeight: '500',
                                    padding: '4px 12px'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
                                    e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }}
                                >
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center p-4">
                    <p>
                      Click "Add Connection" to get started with your 14-day free trial.
                    </p>
                  </div>
                )}
              </div>
            )}
      </Container>      {/* Selection Modal */}
      {showSelectionModal && (
        <div className="modal-backdrop show" style={{ display: 'block' }}></div>
      )}
      <div className={`modal ${showSelectionModal ? 'show' : ''}`} style={{ display: showSelectionModal ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '600px', width: '90%' }}>
          <div className="modal-content">
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">Select Team Type</h5>
              <button type="button" className="btn-close" onClick={handleSelectionClose} aria-label="Close" style={{ filter: 'invert(1)' }}></button>
            </div>
            <div className="modal-body p-4" style={{ 
              background: '#fff',
              fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif',
              borderRadius: '0 0 8px 8px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)'
            }}>
              <div className="d-flex justify-content-center flex-wrap">
                {getAvailableConnectionTypes().includes('Solo') && (
                  <Button 
                    variant="outline-primary" 
                    size="lg" 
                    className="m-2 p-4 metallic-button"
                    onClick={() => handleTypeSelection("Solo")}
                    style={{ 
                      width: '160px', 
                      height: '120px',
                      background: 'linear-gradient(145deg, #f0f0f0 0%, #d9d9d9 50%, #c8c8c8 100%)',
                      border: '1px solid #b8b8b8',
                      borderTop: '1px solid #ffffff',
                      borderRadius: '8px',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 0 rgba(0,0,0,0.2)',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(145deg, #f8f8f8 0%, #e0e0e0 50%, #d0d0d0 100%)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.2)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(145deg, #f0f0f0 0%, #d9d9d9 50%, #c8c8c8 100%)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 0 rgba(0,0,0,0.2)';
                    }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <FaUser style={{ 
                        fontSize: '36px', 
                        color: '#4a5568',
                        marginBottom: '12px'
                      }} />
                      <span className="fw-semibold" style={{ 
                        color: '#374151', 
                        fontSize: '14px'
                      }}>Solo</span>
                    </div>
                  </Button>
                )}
                {getAvailableConnectionTypes().includes('Trial') && (
                  <Button 
                    variant="outline-primary" 
                    size="lg" 
                    className="m-2 p-4 metallic-button"
                    onClick={() => handleTypeSelection("Trial")}
                    style={{ 
                      width: '160px', 
                      height: '120px',
                      background: 'linear-gradient(145deg, #e6f3ff 0%, #b3d9ff 50%, #80bfff 100%)',
                      border: '1px solid #4d9fff',
                      borderTop: '1px solid #ffffff',
                      borderRadius: '8px',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 0 rgba(0,0,0,0.2)',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(145deg, #f0f8ff 0%, #c6e0ff 50%, #99ccff 100%)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.2)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(145deg, #e6f3ff 0%, #b3d9ff 50%, #80bfff 100%)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 0 rgba(0,0,0,0.2)';
                    }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <FaGift style={{ 
                        fontSize: '36px', 
                        color: '#0066cc',
                        marginBottom: '12px'
                      }} />
                      <span className="fw-semibold" style={{ 
                        color: '#0066cc', 
                        fontSize: '14px'
                      }}>Trial</span>
                    </div>
                  </Button>
                )}
                {getAvailableConnectionTypes().includes('Solo + Assistant') && (
                  <Button 
                    variant="outline-success" 
                    size="lg" 
                    className="m-2 p-4 metallic-button"
                    onClick={() => handleTypeSelection("Solo + Assistant")}
                    style={{ 
                      width: '160px', 
                      height: '120px',
                      background: 'linear-gradient(145deg, #f0f0f0 0%, #d9d9d9 50%, #c8c8c8 100%)',
                      border: '1px solid #b8b8b8',
                      borderTop: '1px solid #ffffff',
                      borderRadius: '8px',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 0 rgba(0,0,0,0.2)',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(145deg, #f8f8f8 0%, #e0e0e0 50%, #d0d0d0 100%)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.2)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(145deg, #f0f0f0 0%, #d9d9d9 50%, #c8c8c8 100%)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 0 rgba(0,0,0,0.2)';
                    }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <div style={{ 
                        fontSize: '28px', 
                        color: '#4a5568',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        <FaUser style={{ fontSize: '24px' }} />
                        <span style={{ fontSize: '18px', color: '#28a745' }}>+</span>
                        <FaUser style={{ fontSize: '20px', color: '#28a745' }} />
                      </div>
                      <span className="fw-semibold" style={{ 
                        color: '#374151', 
                        fontSize: '14px'
                      }}>Solo + Assistant</span>
                    </div>
                  </Button>
                )}
                {getAvailableConnectionTypes().includes('Small Team') && (
                  <Button 
                    variant="outline-info" 
                    size="lg" 
                    className="m-2 p-4 metallic-button"
                    onClick={() => handleTypeSelection("Small Team")}
                    style={{ 
                      width: '160px', 
                      height: '120px',
                      background: 'linear-gradient(145deg, #f0f0f0 0%, #d9d9d9 50%, #c8c8c8 100%)',
                      border: '1px solid #b8b8b8',
                      borderTop: '1px solid #ffffff',
                      borderRadius: '8px',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 0 rgba(0,0,0,0.2)',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <div style={{ fontSize: '36px', color: '#4a5568', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FaUser style={{ fontSize: '28px' }} />
                        <FaUser style={{ fontSize: '32px' }} />
                        <FaUser style={{ fontSize: '28px' }} />
                      </div>
                      <span className="fw-semibold" style={{ color: '#374151', fontSize: '14px' }}>Small Team</span>
                    </div>
                  </Button>
                )}
                {getAvailableConnectionTypes().includes('Medium Team') && (
                  <Button 
                    variant="outline-info" 
                    size="lg" 
                    className="m-2 p-4 metallic-button"
                    onClick={() => handleTypeSelection("Medium Team")}
                    style={{ 
                      width: '160px', 
                      height: '120px',
                      background: 'linear-gradient(145deg, #f0f0f0 0%, #d9d9d9 50%, #c8c8c8 100%)',
                      border: '1px solid #b8b8b8',
                      borderTop: '1px solid #ffffff',
                      borderRadius: '8px',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 0 rgba(0,0,0,0.2)',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <div style={{ fontSize: '36px', color: '#4a5568', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FaUser style={{ fontSize: '28px' }} />
                        <FaUser style={{ fontSize: '32px' }} />
                        <FaUser style={{ fontSize: '28px' }} />
                        <FaUser style={{ fontSize: '28px' }} />
                      </div>
                      <span className="fw-semibold" style={{ color: '#374151', fontSize: '14px' }}>Medium Team</span>
                    </div>
                  </Button>
                )}
                {getAvailableConnectionTypes().includes('Large Team') && (
                  <Button 
                    variant="outline-info" 
                    size="lg" 
                    className="m-2 p-4 metallic-button"
                    onClick={() => handleTypeSelection("Large Team")}
                    style={{ 
                      width: '160px', 
                      height: '120px',
                      background: 'linear-gradient(145deg, #f0f0f0 0%, #d9d9d9 50%, #c8c8c8 100%)',
                      border: '1px solid #b8b8b8',
                      borderTop: '1px solid #ffffff',
                      borderRadius: '8px',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 0 rgba(0,0,0,0.2)',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <div style={{ fontSize: '36px', color: '#4a5568', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FaUser style={{ fontSize: '28px' }} />
                        <FaUser style={{ fontSize: '32px' }} />
                        <FaUser style={{ fontSize: '28px' }} />
                        <FaUser style={{ fontSize: '28px' }} />
                        <FaUser style={{ fontSize: '28px' }} />
                      </div>
                      <span className="fw-semibold" style={{ color: '#374151', fontSize: '14px' }}>Large Team</span>
                    </div>
                  </Button>
                )}
              </div>
            </div>
            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              <Button 
                variant="secondary" 
                onClick={handleSelectionClose} 
                style={{ 
                  background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                  border: 'none',
                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                  transition: 'all 0.2s ease-in-out',
                  padding: '8px 20px'
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for adding new connection */}
      {showAddModal && (
        <div className="modal-backdrop show" style={{ display: 'block' }}></div>
      )}
      <div className={`modal ${showAddModal ? 'show' : ''}`} style={{ display: showAddModal ? 'block' : 'none' }}>
        <div className="modal-dialog" style={{ maxWidth: '700px', width: '85%' }}>
          <div className="modal-content">
            {/* <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', position: 'relative' }}>
              <h5 className="modal-title" style={{ color: 'white', fontWeight: 600, letterSpacing: '0.5px', textShadow: '1px 1px 2px rgba(0,0,0,0.3)', margin: 0 }}>
                {isEditMode ? 'Edit Connections' : 'Add Connections'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
                style={{ filter: 'invert(1)', position: 'absolute', top: 16, right: 16, zIndex: 10 }}
              ></button>
            </div> */}
            <div className="modal-body" style={{ borderRadius: 0 }}>
              <Auth
                key={`auth-${showAddModal}-${isEditMode}-${currentEditItem?.id || 'new'}`} // Force remount when modal opens
                ref={authRef}
                accountType={accountType}
                setAccountType={setAccountType}
                seatLimit={billingSeats} // Pass current seat limit from billing
                title={
                  isEditMode && (accountType === 'small_team' || accountType === 'medium_team' || accountType === 'large_team') ?
                    `Edit Connections (${accountType === 'large_team' ? 'Large Team' : accountType === 'medium_team' ? 'Medium Team' : 'Small Team'})` :
                  isEditMode && accountType === 'solo' ? 'Edit Connections (Solo)' :
                  isEditMode && accountType === 'individual' ? 'Edit Connections (Individual)' :
                  isEditMode && accountType === 'solo_assistant' ? 'Edit Connections (Solo + Assistant)' :
                  isEditMode && accountType === 'trial' ? 'Edit Connections (Trial)' :
                  (accountType === 'small_team' || accountType === 'medium_team' || accountType === 'large_team') ?
                    `Add Connections (${accountType === 'large_team' ? 'Large Team' : accountType === 'medium_team' ? 'Medium Team' : 'Small Team'})` :
                  accountType === 'solo' ? 'Add Connections (Solo)' :
                  accountType === 'individual' ? 'Add Connections (Individual)' :
                  accountType === 'solo_assistant' ? 'Add Connections (Solo + Assistant)' :
                  accountType === 'trial' ? 'Add Connections (Trial)' : ''
                }
                onClose={handleClose}
                members={members}
                setMembers={setMembers}
                provider={provider}
                setProvider={setProvider}
                appleId={appleId}
                setAppleId={setAppleId}
                appPassword={appPassword}
                setAppPassword={setAppPassword}
                authText={authText}
                setAuthText={setAuthText}
                authCode={authCode}
                setAuthCode={setAuthCode}
                message={message}
                setMessage={setMessage}
                loading={loading}
                setLoading={setLoading}
                showSuccess={showSuccess}
                setShowSuccess={setShowSuccess}
                onRegisterClick={handleReset}
                isEditMode={isEditMode}
                currentEditItem={currentEditItem}
                onCancel={handleClose}
                onDataUpdate={handleDataUpdate}
                onBillingClick={onBillingClick}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tasks Modal */}
      {showTasksModal && (
        <div className="modal-backdrop show" style={{ display: 'block' }}></div>
      )}
      <div className={`modal ${showTasksModal ? 'show' : ''}`} style={{ display: showTasksModal ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered" style={{
          maxWidth: tasksModalLoading || tasksDisplayRecords.length === 0
            ? '500px'
            : `min(${tasksDisplayRecords.length * 332 + 60}px, 92vw)`
        }}>
          <div className="modal-content">
            <div className="modal-header" style={{ background: '#282c34da', color: 'white', borderBottom: '1px solid #444', padding: '8px 16px' }}>
              <h5 className="modal-title" style={{ color: 'white', fontWeight: 600, fontSize: '18px', margin: 0 }}>
                Tasks — {tasksModalTeam?.team_name}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  if (Object.values(unpublishedChangesRef.current).some(v => v.count > 0)) {
                    setUnpublishedWarning({ onConfirm: () => { setUnpublishedChanges({}); setUnpublishedTaskIds({}); unpublishedChangesRef.current = {}; setShowTasksModal(false); } });
                  } else { setShowTasksModal(false); }
                }}
                aria-label="Close"
                style={{ filter: 'invert(1)' }}
              ></button>
            </div>
            <div className="modal-body" style={{ overflowX: 'auto', padding: '20px', background: '#f0f2f5' }}>
              {/* Tasks view switcher */}
              {!tasksModalLoading && (
                <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                  {canCreateTaskList && effectiveTasksTab !== 'archived' && (
                    <button
                      onClick={() => { setCreateTitle(''); setCreateAddress(''); setCreateError(''); setShowCreateModal(true); }}
                      style={{ padding: '5px 12px', borderRadius: '6px', border: 'none', background: '#1967d2', color: 'white', fontWeight: 600, fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#1558b0'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#1967d2'; }}
                    >
                      + Create Task List
                    </button>
                  )}
                  {tasksView !== 'allTime' && (
                    <>
                      <button onClick={() => setTasksViewDate(new Date())}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                        onMouseLeave={e => e.currentTarget.style.background = 'white'}
                        style={{ padding: '5px 11px', borderRadius: '6px', border: '1px solid #dadce0', background: 'white', color: '#3c4043', fontWeight: 500, fontSize: '12px', cursor: 'pointer' }}>
                        Today
                      </button>
                      <div style={{ display: 'flex' }}>
                        {[['‹', () => setTasksViewDate(d => tasksView === 'month' ? calAddMonths(d, -1) : tasksView === 'week' ? calAddDays(d, -7) : calAddDays(d, -1))],
                          ['›', () => setTasksViewDate(d => tasksView === 'month' ? calAddMonths(d, 1) : tasksView === 'week' ? calAddDays(d, 7) : calAddDays(d, 1))]
                        ].map(([arrow, fn], i) => (
                          <button key={i} onClick={fn}
                            onMouseEnter={e => e.currentTarget.style.background = '#f1f3f4'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            style={{ width: '28px', height: '28px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '18px', color: '#3c4043', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {arrow}
                          </button>
                        ))}
                      </div>
                      <span style={{ fontSize: '15px', fontWeight: 400, color: '#3c4043', flex: 1 }}>{tasksPeriodLabel}</span>
                    </>
                  )}
                  {tasksView === 'allTime' && <span style={{ flex: 1 }} />}
                  <div style={{ display: 'flex', borderRadius: '6px', border: '1px solid #dadce0', overflow: 'hidden' }}>
                    {[['allTime', 'All Time'], ['month', 'Month'], ['week', 'Week'], ['day', 'Day']].map(([v, label], i, arr) => (
                      <button key={v} onClick={() => setTasksView(v)}
                        style={{ padding: '5px 11px', border: 'none', background: tasksView === v ? '#e8f0fe' : 'white', color: tasksView === v ? '#1967d2' : '#3c4043', fontWeight: tasksView === v ? 600 : 400, fontSize: '12px', cursor: 'pointer', borderRight: i < arr.length - 1 ? '1px solid #dadce0' : 'none' }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Active / Archived tabs — owner/lead-with-permission only */}
                {tasksCanArchive && <div style={{ display: 'flex', borderBottom: '2px solid #dadce0' }}>
                  {[['active', '✓ Active'], ['archived', '🗃️ Archived']].map(([tab, label]) => (
                    <button key={tab} onClick={() => setTasksActiveTab(tab)} style={{
                      padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      background: tasksActiveTab === tab ? '#f0f2f5' : 'transparent',
                      color: tasksActiveTab === tab ? (tab === 'active' ? '#15803d' : '#1d4ed8') : '#64748b',
                      borderTop: tasksActiveTab === tab ? '1px solid #dadce0' : '1px solid transparent',
                      borderLeft: tasksActiveTab === tab ? '1px solid #dadce0' : '1px solid transparent',
                      borderRight: tasksActiveTab === tab ? '1px solid #dadce0' : '1px solid transparent',
                      borderBottom: tasksActiveTab === tab ? '2px solid #f0f2f5' : 'none',
                      borderRadius: '6px 6px 0 0',
                      marginBottom: tasksActiveTab === tab ? '-2px' : '0',
                      transition: 'all 0.15s',
                    }}>{label}</button>
                  ))}
                </div>}
              </div>
              )}
              {tasksModalLoading ? (
                <div className="text-center p-4">
                  <div className="spinner-border" role="status" style={{ color: '#3a7bd5', width: '2.5rem', height: '2.5rem' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p style={{ color: '#6c757d', marginTop: '12px', marginBottom: 0 }}>Loading tasks…</p>
                </div>
              ) : tasksModalData.length === 0 ? (
                <div className="text-center p-4">
                  <p style={{ color: '#6c757d', marginBottom: '4px' }}>No task lists for this team.</p>
                  <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>Task lists created for this team will appear here.</p>
                </div>
              ) : tasksDisplayRecords.length === 0 ? (
                <div className="text-center p-4">
                  <p style={{ color: '#6c757d' }}>
                    {tasksActiveTab === 'active' ? 'No active task lists.' : 'No archived task lists.'}
                  </p>
                </div>
              ) : !tasksHasVisible ? (
                <div className="text-center p-4">
                  <p style={{ color: '#6c757d' }}>
                    {`No tasks due in the selected ${tasksView === 'day' ? 'day' : tasksView === 'week' ? 'week' : 'month'}.`}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '18px', alignItems: 'flex-start', minWidth: 'max-content' }}>
                  {tasksDisplayRecords.map((record, idx) => {
                    const tasks = record.tasks || [];
                    const tagged = tasks.map((task, taskIdx) => ({ task, taskIdx }));
                    const inPeriod = tasksView === 'allTime' ? tagged : tagged.filter(({ task }) => tasksMatchesPeriod(task));
                    if (tasksView !== 'allTime' && inPeriod.length === 0) return null;
                    const completedCount = inPeriod.filter(({ task }) => task.status === 'completed').length;
                    const colMember = tasksModalTeam?.members?.find(m => m.email === record.id);
                    const isLead = colMember?.role === 'team_lead' || colMember?.role === 'single_agent' || colMember?.role === 'primary';
                    return (
                      <div key={idx} style={{
                        width: '320px',
                        flexShrink: 0,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.13)',
                        border: '1px solid rgba(0,0,0,0.07)'
                      }}>
                        {/* Column header */}
                        <div style={{
                          background: 'linear-gradient(145deg, #1a2540 0%, #243050 60%, #1e2d4a 100%)',
                          padding: '16px',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            position: 'absolute', top: '-24px', right: '-24px',
                            width: '90px', height: '90px', borderRadius: '50%',
                            background: 'rgba(255,255,255,0.04)', pointerEvents: 'none'
                          }} />
                          {/* Role badge — only shown for team lead */}
                          {isLead && (
                            <div style={{ marginBottom: '10px' }}>
                              <span style={{
                                display: 'inline-block', fontSize: '10px', fontWeight: 700,
                                padding: '3px 10px', borderRadius: '20px', letterSpacing: '0.6px',
                                textTransform: 'uppercase',
                                background: 'linear-gradient(90deg, #f59e0b, #d97706)',
                                color: '#1a1a1a'
                              }}>
                                Team Lead
                              </span>
                            </div>
                          )}
                          {/* Task list title */}
                          <div style={{ fontWeight: 700, fontSize: '14px', color: '#ffffff', lineHeight: '1.4', marginBottom: '4px', textAlign: 'center' }}>
                            {record.task_list_title}
                          </div>
                          <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {(() => {
                              const fromName = record.display_name || (record.record_type === 'user_projection' ? (record.source_email || record.id) : record.id);
                              const updatedAt = formatPublishedAt(record.published_at || record.last_edited_at || record.update_timestamp);
                              return (
                                <>
                                  <span style={{ fontSize: '11px', color: '#a4afbe' }}>
                                    <span style={{ color: '#7a8fa8' }}>From:</span> {fromName}
                                  </span>
                                  {updatedAt && (
                                    <span style={{ fontSize: '11px', color: '#a4afbe' }}>
                                      <span style={{ color: '#7a8fa8' }}>Last update:</span> {updatedAt}
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          {/* Task count pills + Publish button */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                              {completedCount > 0 && (
                                <span
                                  onClick={() => {
                                    setTasksCompletedOpenSet(prev => { const n = new Set(prev); n.add(record.task_list_title); return n; });
                                    setTimeout(() => completedSectionRefs.current[record.task_list_title]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(34,197,94,0.25)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                                  onMouseDown={e => { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = ''; }}
                                  onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(34,197,94,0.25)'; }}
                                  style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: 'rgba(34,197,94,0.18)', color: '#86efac', fontWeight: 600, cursor: 'pointer', transition: 'transform 0.12s ease, box-shadow 0.12s ease' }}
                                >
                                  {completedCount} completed
                                </span>
                              )}
                              {(() => {
                                const info = unpublishedChanges[record.task_list_title];
                                if (!info || info.count === 0) return null;
                                const isDiscarding = discardingTitle === record.task_list_title;
                                const disableDiscard = isDiscarding || !!record.archive;
                                return (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
                                    <button
                                      onClick={e => { e.stopPropagation(); handleFlashUnpublished(record.task_list_title); }}
                                      aria-label="Show unshared changes"
                                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(251,191,36,0.3)'; }}
                                      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                                      onMouseDown={e => { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = ''; }}
                                      onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(251,191,36,0.3)'; }}
                                      style={{
                                        fontSize: '10px', padding: '2px 8px', borderRadius: '10px',
                                        background: 'rgba(251,191,36,0.18)', color: '#fbbf24',
                                        fontWeight: 600, border: '1px solid rgba(251,191,36,0.35)',
                                        cursor: 'pointer', fontFamily: 'inherit',
                                        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
                                      }}
                                    >
                                      {info.count === 1 ? '1 unshared change' : `${info.count} unshared changes`}
                                    </button>
                                    <TeamNotificationTooltip role="discard" placement="bottom" wrapperStyle={{ display: 'inline-flex' }}>
                                      <button
                                        onClick={e => { e.stopPropagation(); handleDiscard(record); }}
                                        disabled={disableDiscard}
                                        onMouseEnter={e => { if (!disableDiscard) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(249,115,22,0.5)'; } }}
                                        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = disableDiscard ? 'none' : '0 2px 8px rgba(249,115,22,0.35)'; }}
                                        onMouseDown={e => { if (!disableDiscard) { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(249,115,22,0.3)'; } }}
                                        onMouseUp={e => { if (!disableDiscard) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(249,115,22,0.5)'; } }}
                                        style={{
                                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                          height: '24px', padding: '0 10px', borderRadius: '8px',
                                          fontSize: '11px', fontWeight: 700, border: 'none',
                                          cursor: disableDiscard ? 'default' : 'pointer', fontFamily: 'inherit',
                                          background: disableDiscard ? 'rgba(148,163,184,0.18)' : 'linear-gradient(135deg, #f97316, #ea580c)',
                                          color: disableDiscard ? '#64748b' : '#fff',
                                          letterSpacing: '0.3px', transition: 'all 0.15s ease',
                                          boxShadow: disableDiscard ? 'none' : '0 2px 8px rgba(249,115,22,0.35)',
                                        }}
                                      >
                                        {isDiscarding ? 'Discarding…' : 'Discard'}
                                      </button>
                                    </TeamNotificationTooltip>
                                  </div>
                                );
                              })()}
                            </div>
                            {/* Publish + Archive/Restore + Delete buttons — second row */}
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '8px' }}>
                              {tasksActiveTab === 'active' && (() => {
                                const isPublishing = publishingTitle === record.task_list_title;
                                return (
                                  <TeamNotificationTooltip role="publish" placement="bottom" wrapperStyle={{ display: 'inline-flex' }}>
                                    <button
                                      onClick={() => openPublishModal(record)}
                                      disabled={isPublishing}
                                      onMouseEnter={e => { if (!isPublishing) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(34,197,94,0.5)'; } }}
                                      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = isPublishing ? 'none' : '0 2px 8px rgba(34,197,94,0.35)'; }}
                                      onMouseDown={e => { if (!isPublishing) { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(34,197,94,0.3)'; } }}
                                      onMouseUp={e => { if (!isPublishing) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(34,197,94,0.5)'; } }}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        height: '24px', width: '80px',
                                        padding: '0', borderRadius: '8px', fontSize: '11px', fontWeight: 700,
                                        border: 'none', cursor: isPublishing ? 'default' : 'pointer',
                                        background: isPublishing
                                          ? 'rgba(148,163,184,0.18)'
                                          : 'linear-gradient(135deg, #22c55e, #16a34a)',
                                        color: isPublishing ? '#64748b' : '#fff',
                                        letterSpacing: '0.3px', transition: 'all 0.15s ease',
                                        boxShadow: isPublishing ? 'none' : '0 2px 8px rgba(34,197,94,0.35)',
                                      }}
                                    >
                                      {isPublishing ? '⏳ Sharing…' : '📅 Share'}
                                    </button>
                                  </TeamNotificationTooltip>
                                );
                              })()}
                              {tasksCanArchive && (tasksActiveTab === 'active' ? (() => {
                                const isArchiving = archivingTitle === record.task_list_title;
                                return (
                                  <TeamNotificationTooltip role="archive" placement="bottom" wrapperStyle={{ display: 'inline-flex', marginLeft: 'auto' }}>
                                    <button
                                      onClick={() => handleArchive(record)}
                                      disabled={isArchiving}
                                      onMouseEnter={e => { if (!isArchiving) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(59,130,246,0.5)'; } }}
                                      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = isArchiving ? 'none' : '0 2px 8px rgba(59,130,246,0.35)'; }}
                                      onMouseDown={e => { if (!isArchiving) { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(59,130,246,0.3)'; } }}
                                      onMouseUp={e => { if (!isArchiving) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(59,130,246,0.5)'; } }}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        height: '24px', minWidth: '80px',
                                        padding: '0 6px', borderRadius: '8px', fontSize: '11px', fontWeight: 700,
                                        whiteSpace: 'nowrap',
                                        border: 'none', cursor: isArchiving ? 'default' : 'pointer',
                                        background: isArchiving ? 'rgba(148,163,184,0.18)' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                        color: isArchiving ? '#64748b' : '#fff',
                                        letterSpacing: '0.3px', transition: 'all 0.15s ease',
                                        boxShadow: isArchiving ? 'none' : '0 2px 8px rgba(59,130,246,0.35)',
                                      }}
                                    >
                                      {isArchiving ? '⏳ Archiving…' : '🗃️ Archive'}
                                    </button>
                                  </TeamNotificationTooltip>
                                );
                              })() : (() => {
                                const isRestoring = restoringTitle === record.task_list_title;
                                return (
                                  <TeamNotificationTooltip role="unarchive" placement="bottom" wrapperStyle={{ display: 'inline-flex', marginLeft: 'auto' }}>
                                    <button
                                      onClick={() => handleRestore(record)}
                                      disabled={isRestoring}
                                      onMouseEnter={e => { if (!isRestoring) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(34,197,94,0.5)'; } }}
                                      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = isRestoring ? 'none' : '0 2px 8px rgba(34,197,94,0.35)'; }}
                                      onMouseDown={e => { if (!isRestoring) { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(34,197,94,0.3)'; } }}
                                      onMouseUp={e => { if (!isRestoring) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(34,197,94,0.5)'; } }}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        height: '24px', minWidth: '80px',
                                        padding: '0 6px', borderRadius: '8px', fontSize: '11px', fontWeight: 700,
                                        border: 'none', cursor: isRestoring ? 'default' : 'pointer',
                                        background: isRestoring ? 'rgba(148,163,184,0.18)' : 'linear-gradient(135deg, #22c55e, #16a34a)',
                                        color: isRestoring ? '#64748b' : '#fff',
                                        letterSpacing: '0.3px', transition: 'all 0.15s ease',
                                        boxShadow: isRestoring ? 'none' : '0 2px 8px rgba(34,197,94,0.35)',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {isRestoring ? '⏳ Unarchiving…' : '↩️ Unarchive'}
                                    </button>
                                  </TeamNotificationTooltip>
                                );
                              })())}
                              {tasksCanDelete && (() => {
                                const isDeleting = deletingTitle === record.task_list_title;
                                return (
                                  <TeamNotificationTooltip role="delete" placement="bottom" wrapperStyle={{ display: 'inline-flex' }}>
                                    <button
                                      onClick={() => handleDelete(record)}
                                      disabled={isDeleting}
                                      onMouseEnter={e => { if (!isDeleting) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(239,68,68,0.5)'; } }}
                                      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = isDeleting ? 'none' : '0 2px 8px rgba(239,68,68,0.35)'; }}
                                      onMouseDown={e => { if (!isDeleting) { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(239,68,68,0.3)'; } }}
                                      onMouseUp={e => { if (!isDeleting) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(239,68,68,0.5)'; } }}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        height: '24px', minWidth: '80px',
                                        padding: '0 6px', borderRadius: '8px', fontSize: '11px', fontWeight: 700,
                                        border: 'none', cursor: isDeleting ? 'default' : 'pointer',
                                        background: isDeleting ? 'rgba(148,163,184,0.18)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                                        color: isDeleting ? '#64748b' : '#fff',
                                        letterSpacing: '0.3px', transition: 'all 0.15s ease',
                                        boxShadow: isDeleting ? 'none' : '0 2px 8px rgba(239,68,68,0.35)',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {isDeleting ? '⏳ Deleting…' : '🗑️ Delete'}
                                    </button>
                                  </TeamNotificationTooltip>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                        {/* Task cards list */}
                        <div style={{ padding: '12px', background: '#f1f4f8', maxHeight: '62vh', overflowY: 'auto' }}>
                          {inPeriod.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '28px 0' }}>
                              <div style={{ fontSize: '28px', marginBottom: '8px', color: '#cbd5e1' }}>○</div>
                              <p style={{ color: '#94a3b8', fontSize: '13px', margin: canCreateTaskList ? '0 0 12px' : 0 }}>No tasks yet</p>
                              {canCreateTaskList && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleAddTask(record); }}
                                  style={{
                                    padding: '6px 16px', borderRadius: '6px', border: 'none',
                                    background: '#1967d2', color: 'white', fontWeight: 600, fontSize: '12px',
                                    cursor: 'pointer',
                                  }}>+ Add Task</button>
                              )}
                            </div>
                          ) : (() => {
                            const today = new Date();
                            const active = inPeriod
                              .filter(({ task }) => task.status !== 'completed')
                              .sort((a, b) => {
                                if (!a.task.due && !b.task.due) return 0;
                                if (!a.task.due) return 1;
                                if (!b.task.due) return -1;
                                return new Date(a.task.due) - new Date(b.task.due);
                              });
                            const completed = inPeriod.filter(({ task }) => task.status === 'completed');
                            const completedOpen = tasksCompletedOpenSet.has(record.task_list_title);

                            const renderCard = ({ task, taskIdx }) => {
                              const isArchived = !!record.archive;
                              const isCompleted = task.status === 'completed';
                              const isUnpublished = !!task.task_id && !!unpublishedTaskIds[record.task_list_title]?.has(task.task_id);
                              const isFlashing = isUnpublished && flashingTitles.has(record.task_list_title);
                              const statusColor = isCompleted ? '#22c55e' : '#94a3b8';
                              const statusBg    = isUnpublished ? '#fff7ed' : isCompleted ? '#f0fdf4' : '#ffffff';
                              const statusBorder = isUnpublished ? '#fed7aa' : isCompleted ? '#bbf7d0' : '#e2e8f0';
                              const leftAccent  = isUnpublished ? '#f97316' : statusColor;
                              const dueDate = task.due ? new Date(task.due) : null;
                              const daysUntilDue = dueDate
                                ? Math.round(
                                    (new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate()) -
                                     new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000
                                  )
                                : null;
                              const isOverdue = !isCompleted && daysUntilDue !== null && daysUntilDue < 0;
                              // Badge shows deadline urgency; left side shows the exact date as a quiet reference
                              const badgeText = isCompleted ? 'Completed'
                                : !dueDate ? 'No due date'
                                : isOverdue ? `! Overdue ${Math.abs(daysUntilDue)}d`
                                : daysUntilDue === 0 ? 'Due today'
                                : `Due in ${daysUntilDue}d`;
                              const badgeBg = isCompleted ? '#22c55e'
                                : !dueDate ? '#94a3b8'
                                : isOverdue ? '#ef4444'
                                : daysUntilDue === 0 ? '#f97316'
                                : '#22c55e';
                              const exactDateLabel = dueDate
                                ? dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : '';
                              const dueLineText = isCompleted
                                ? (dueDate ? `Due: ${exactDateLabel} — Completed` : 'Completed')
                                : !dueDate ? 'No due date'
                                : `Due: ${exactDateLabel} — ${badgeText}`;
                              return (
                                <div key={taskIdx}
                                  ref={isUnpublished && task.task_id ? el => {
                                    const k = `${record.task_list_title}:::${task.task_id}`;
                                    if (el) unpublishedCardRefs.current[k] = el;
                                    else delete unpublishedCardRefs.current[k];
                                  } : undefined}
                                  onClick={isArchived ? undefined : () => setEditingTask({ task: { ...task }, originalTask: { ...task }, record, recordIdx: idx, taskIdx })}
                                  onMouseEnter={isArchived ? undefined : e => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.13)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                  style={{ background: statusBg, borderRadius: '8px', padding: '9px 11px', marginBottom: '6px', border: `1px solid ${statusBorder}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderLeft: `3px solid ${leftAccent}`, cursor: isArchived ? 'default' : 'pointer', transition: 'box-shadow 0.15s ease, transform 0.15s ease', animation: isFlashing ? 'unpublishedPop 0.7s ease-out' : undefined }}>
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '5px' }}>
                                    {(() => {
                                      const circleKey = `${record.task_list_title}-${taskIdx}`;
                                      const isCircleHovered = hoveredCircle === circleKey;
                                      const ghostColor = '#d1d5db';
                                      const circleBorder = isCompleted
                                        ? (isCircleHovered ? 'rgba(34,197,94,0.6)' : statusColor)
                                        : isCircleHovered ? '#22c55e'
                                        : statusColor;
                                      const circleBg = isCompleted
                                        ? (isCircleHovered ? 'rgba(34,197,94,0.65)' : statusColor)
                                        : isCircleHovered ? 'rgba(34,197,94,0.12)' : 'transparent';
                                      const circleColor = isCompleted ? 'white'
                                        : isCircleHovered ? '#22c55e' : ghostColor;
                                      return (
                                        <div style={{ position: 'relative', flexShrink: 0 }}>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); handleToggleComplete(task, record, taskIdx); }}
                                            onMouseEnter={() => setHoveredCircle(circleKey)}
                                            onMouseLeave={() => setHoveredCircle(null)}
                                            onFocus={() => setHoveredCircle(circleKey)}
                                            onBlur={() => setHoveredCircle(null)}
                                            aria-label={isCompleted ? 'Mark task uncompleted' : 'Mark task completed'}
                                            style={{
                                              width: '20px', height: '20px', borderRadius: '4px', padding: 0,
                                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                                              fontSize: '11px', fontWeight: 800, outline: 'none',
                                              border: `2px solid ${circleBorder}`,
                                              background: circleBg,
                                              color: circleColor,
                                              cursor: 'pointer',
                                              transition: 'background 0.15s, border-color 0.15s, transform 0.15s, box-shadow 0.15s',
                                              transform: isCircleHovered ? 'scale(1.12)' : 'scale(1)',
                                              boxShadow: isCircleHovered ? '0 0 0 3px rgba(34,197,94,0.18)' : 'none',
                                            }}
                                          >
                                            ✓
                                          </button>
                                          {isCircleHovered && (
                                            <div style={{
                                              position: 'absolute', top: '1px', left: '28px',
                                              background: 'rgba(30,41,59,0.95)', color: 'white',
                                              fontSize: '11px', fontWeight: 500,
                                              padding: '4px 8px', borderRadius: '5px',
                                              whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 100,
                                              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                            }}>
                                              {isCompleted ? 'Mark uncompleted' : 'Mark completed'}
                                              <div style={{
                                                position: 'absolute', top: '50%', right: '100%',
                                                transform: 'translateY(-50%)',
                                                width: 0, height: 0,
                                                borderTop: '5px solid transparent',
                                                borderBottom: '5px solid transparent',
                                                borderRight: '5px solid rgba(30,41,59,0.95)',
                                              }} />
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                    <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b', lineHeight: '1.3', flex: 1, textAlign: 'left', textDecoration: isCompleted ? 'line-through' : 'none', opacity: isCompleted ? 0.6 : 1 }}>
                                      {task.title}
                                    </div>
                                    {canCreateTaskList && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setConfirmRemoveTask({ record, taskIdx, taskTitle: task.title || '' }); }}
                                        title="Remove task"
                                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.12)'; e.currentTarget.style.color = '#ef4444'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#f87171'; }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', fontSize: '16px', lineHeight: 1, padding: '2px 5px', borderRadius: '4px', flexShrink: 0, transition: 'background 0.15s, color 0.15s' }}
                                      >×</button>
                                    )}
                                  </div>
                                  <div style={{ textAlign: 'left', paddingLeft: '28px', marginBottom: task.action_items?.length > 0 ? '7px' : 0 }}>
                                    <span style={{ fontSize: '12px', color: badgeBg, fontWeight: 700 }}>
                                      {dueLineText}
                                    </span>
                                  </div>
                                  {task.action_items?.length > 0 && (
                                    <div style={{ borderTop: `1px solid ${statusBorder}`, paddingTop: '6px', marginTop: '2px' }}>
                                      {task.action_items.map((item, itemIdx) => (
                                        <div key={itemIdx} style={{ display: 'grid', gridTemplateColumns: '14px 1fr', gap: '4px', marginBottom: itemIdx < task.action_items.length - 1 ? '4px' : 0 }}>
                                          <span style={{ color: statusColor, fontSize: '12px', fontWeight: 700, lineHeight: '1.45', textAlign: 'center' }}>&#8250;</span>
                                          <span style={{ fontSize: '12px', color: '#475569', lineHeight: '1.45', textAlign: 'left' }}>{item}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            };

                            return (
                              <>
                                {active.map(renderCard)}
                                {completed.length > 0 && (
                                  <>
                                    <div
                                      ref={el => { completedSectionRefs.current[record.task_list_title] = el; }}
                                      onClick={() => setTasksCompletedOpenSet(prev => {
                                        const n = new Set(prev);
                                        completedOpen ? n.delete(record.task_list_title) : n.add(record.task_list_title);
                                        return n;
                                      })}
                                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 4px', cursor: 'pointer', userSelect: 'none', borderTop: '1px solid #e2e8f0', marginTop: active.length > 0 ? '4px' : 0 }}>
                                      <span style={{ fontSize: '11px', color: '#64748b' }}>{completedOpen ? '▾' : '▸'}</span>
                                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Completed ({completed.length})</span>
                                    </div>
                                    {completedOpen && completed.map(renderCard)}
                                  </>
                                )}
                                {canCreateTaskList && (
                                  <div style={{ paddingTop: '8px', borderTop: '1px solid #e2e8f0', marginTop: '6px', textAlign: 'center' }}>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleAddTask(record); }}
                                      style={{
                                        background: 'none', border: '1.5px dashed #94a3b8', borderRadius: '6px',
                                        color: '#64748b', fontSize: '12px', fontWeight: 600, padding: '4px 14px',
                                        cursor: 'pointer',
                                      }}>+ Add Task</button>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              <Button
                variant="secondary"
                onClick={() => {
                  if (Object.values(unpublishedChangesRef.current).some(v => v.count > 0)) {
                    setUnpublishedWarning({ onConfirm: () => { setUnpublishedChanges({}); setUnpublishedTaskIds({}); unpublishedChangesRef.current = {}; setShowTasksModal(false); } });
                  } else { setShowTasksModal(false); }
                }}
                style={{
                  background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                  border: 'none',
                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                  padding: '8px 20px'
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Edit Task Popup (Tasks Modal only — calendar modal has its own copy inside .modal-content) ─── */}
      {editingTask && !editingTask.isCalendarModal && (() => {
        const et = editingTask.task;
        const isCompleted  = et.status === 'completed';
        const statusColor  = isCompleted ? '#22c55e' : '#94a3b8';
        const statusBorder = isCompleted ? '#bbf7d0' : '#e2e8f0';

        const isDirty = editingTask.isNewTask
          ? (et.title?.trim().length > 0)
          : JSON.stringify(et) !== JSON.stringify(editingTask.originalTask);

        return (
          <>
            {/* Backdrop */}
            <div onClick={() => setEditingTask(null)} style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1100, backdropFilter: 'blur(2px)'
            }} />

            {/* Popup card */}
            <div style={{
              position: 'fixed', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1101, width: '420px', maxWidth: '94vw',
              backgroundColor: 'white', borderRadius: '14px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              border: `2px solid ${statusColor}`,
              display: 'flex', flexDirection: 'column', overflow: 'hidden'
            }}>

              {/* Card header — property context */}
              <div style={{
                background: 'linear-gradient(145deg, #1a2540 0%, #243050 100%)',
                padding: '14px 18px'
              }}>
                <div style={{ fontWeight: 700, fontSize: '13px', color: '#fff', textAlign: 'left' }}>
                  {editingTask.record.task_list_title}
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

                {/* Title row with completion checkbox */}
                <div style={{ display: 'flex', alignItems: editingTask.isNewTask ? 'center' : 'flex-start', gap: '10px' }}>
                  <div style={{ position: 'relative', flexShrink: 0, marginTop: editingTask.isNewTask ? '0' : '2px' }}>
                    <button
                      onClick={() => setEditingTask(prev => ({ ...prev, task: { ...prev.task, status: prev.task.status === 'completed' ? 'notStarted' : 'completed' } }))}
                      onMouseEnter={() => setHoveredCircle('__modal__')}
                      onMouseLeave={() => setHoveredCircle(null)}
                      onFocus={() => setHoveredCircle('__modal__')}
                      onBlur={() => setHoveredCircle(null)}
                      aria-label={isCompleted ? 'Mark task uncompleted' : 'Mark task completed'}
                      style={{
                        width: '22px', height: '22px', borderRadius: '4px',
                        border: `2px solid ${isCompleted ? (hoveredCircle === '__modal__' ? 'rgba(34,197,94,0.6)' : '#22c55e') : (hoveredCircle === '__modal__' ? '#22c55e' : '#94a3b8')}`,
                        background: isCompleted ? (hoveredCircle === '__modal__' ? 'rgba(34,197,94,0.65)' : '#22c55e') : (hoveredCircle === '__modal__' ? 'rgba(34,197,94,0.12)' : 'transparent'),
                        color: isCompleted ? 'white' : (hoveredCircle === '__modal__' ? '#22c55e' : '#d1d5db'),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', fontWeight: 800, cursor: 'pointer',
                        transition: 'all 0.15s', outline: 'none', padding: 0,
                        transform: hoveredCircle === '__modal__' ? 'scale(1.12)' : 'scale(1)',
                        boxShadow: hoveredCircle === '__modal__' ? '0 0 0 3px rgba(34,197,94,0.18)' : 'none',
                      }}
                    >
                      ✓
                    </button>
                    {hoveredCircle === '__modal__' && (
                      <div style={{
                        position: 'absolute', top: '1px', left: '28px',
                        background: 'rgba(30,41,59,0.95)', color: 'white',
                        fontSize: '11px', fontWeight: 500,
                        padding: '4px 8px', borderRadius: '5px',
                        whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 1200,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                      }}>
                        {isCompleted ? 'Mark uncompleted' : 'Mark completed'}
                        <div style={{
                          position: 'absolute', top: '50%', right: '100%',
                          transform: 'translateY(-50%)',
                          width: 0, height: 0,
                          borderTop: '5px solid transparent',
                          borderBottom: '5px solid transparent',
                          borderRight: '5px solid rgba(30,41,59,0.95)',
                        }} />
                      </div>
                    )}
                  </div>
                  {editingTask.isNewTask ? (
                    <input
                      autoFocus
                      placeholder="Task title"
                      value={et.title}
                      onChange={e => setEditingTask(prev => ({ ...prev, task: { ...prev.task, title: e.target.value } }))}
                      style={{
                        flex: 1, fontWeight: 700, fontSize: '16px', color: '#1e293b',
                        border: 'none', borderBottom: `2px solid ${statusColor}`,
                        outline: 'none', background: 'transparent',
                        fontFamily: 'inherit', lineHeight: '1.5',
                        textDecoration: isCompleted ? 'line-through' : 'none',
                        opacity: isCompleted ? 0.6 : 1,
                      }}
                    />
                  ) : (
                    <div style={{
                      flex: 1, fontWeight: 700, fontSize: '16px', color: '#1e293b',
                      lineHeight: '1.35',
                      textDecoration: isCompleted ? 'line-through' : 'none',
                      opacity: isCompleted ? 0.6 : 1,
                    }}>
                      {et.title}
                    </div>
                  )}
                </div>

                {/* Due date — native datetime picker */}
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>Due Date</div>
                  <input
                    type="datetime-local"
                    value={toDatetimeLocal(et.due)}
                    onClick={e => { if (pickerOpenRef.current) { e.target.blur(); } else { try { e.target.showPicker(); pickerOpenRef.current = true; } catch {} } }}
                    onBlur={() => { pickerOpenRef.current = false; }}
                    onChange={e => { setEditingTask(prev => ({ ...prev, task: { ...prev.task, due: e.target.value ? new Date(e.target.value).toISOString() : null } })); e.target.blur(); }}
                    style={{
                      width: '100%', padding: '9px 12px', borderRadius: '8px',
                      border: `1.5px solid ${statusBorder}`, background: '#f8fafc',
                      fontSize: '13px', color: '#1e293b', outline: 'none',
                      cursor: 'pointer', fontFamily: 'inherit'
                    }}
                  />
                </div>

                {/* Action items — expandable + editable */}
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>Action Items</div>
                  <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 12px', border: `1px solid ${statusBorder}`, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {(et.action_items || []).map((item, i) => {
                      const isExpanded = editTaskExpandedItems.has(i);
                      return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '14px 1fr 22px', gap: '6px', alignItems: isExpanded ? 'flex-start' : 'center' }}>
                          <span
                            onClick={() => setEditTaskExpandedItems(prev => {
                              const next = new Set(prev);
                              isExpanded ? next.delete(i) : next.add(i);
                              return next;
                            })}
                            style={{ color: statusColor, fontSize: '12px', fontWeight: 700, lineHeight: '1.5', textAlign: 'center', cursor: 'pointer', userSelect: 'none', marginTop: isExpanded ? '2px' : 0 }}
                          >{isExpanded ? '⌄' : '›'}</span>
                          {isExpanded ? (
                            <textarea
                              ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                              value={item}
                              rows={1}
                              onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                              onChange={e => setEditingTask(prev => {
                                const items = [...(prev.task.action_items || [])];
                                items[i] = e.target.value;
                                return { ...prev, task: { ...prev.task, action_items: items } };
                              })}
                              style={{ fontSize: '12px', color: '#1e293b', border: 'none', borderBottom: `1px solid ${statusBorder}`, background: 'transparent', outline: 'none', padding: '1px 2px', width: '100%', fontFamily: 'inherit', resize: 'none', overflow: 'hidden', lineHeight: '1.5', display: 'block' }}
                            />
                          ) : (
                            <span
                              onClick={() => setEditTaskExpandedItems(prev => { const next = new Set(prev); next.add(i); return next; })}
                              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                              style={{ fontSize: '12px', color: item ? '#1e293b' : '#cbd5e1', fontStyle: item ? 'normal' : 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', borderRadius: '3px', padding: '1px 2px', margin: '0 -2px' }}
                            >{item || 'click to edit'}</span>
                          )}
                          <button
                            onClick={() => {
                              setEditingTask(prev => {
                                const items = (prev.task.action_items || []).filter((_, idx) => idx !== i);
                                return { ...prev, task: { ...prev.task, action_items: items } };
                              });
                              setEditTaskExpandedItems(prev => {
                                const next = new Set();
                                for (const idx of prev) {
                                  if (idx < i) next.add(idx);
                                  else if (idx > i) next.add(idx - 1);
                                }
                                return next;
                              });
                            }}
                            title="Remove"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: '14px', lineHeight: 1, padding: 0 }}
                          >×</button>
                        </div>
                      );
                    })}
                    <button
                      onClick={() => {
                        const newIdx = (et.action_items || []).length;
                        setEditingTask(prev => ({
                          ...prev, task: { ...prev.task, action_items: [...(prev.task.action_items || []), ''] }
                        }));
                        setEditTaskExpandedItems(prev => { const next = new Set(prev); next.add(newIdx); return next; });
                      }}
                      style={{
                        marginTop: '4px', padding: '4px 10px', borderRadius: '6px',
                        border: `1.5px dashed ${statusColor}`, background: 'transparent',
                        color: statusColor, fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                        alignSelf: 'flex-start', letterSpacing: '0.3px'
                      }}>+ Add item</button>
                  </div>
                </div>
              </div>

              {/* Footer — Save / Cancel */}
              <div style={{
                display: 'flex', justifyContent: 'flex-end', gap: '10px',
                padding: '12px 20px', borderTop: '1px solid #f1f5f9',
                background: '#fafbfc'
              }}>
                <button onClick={() => setEditingTask(null)} style={{
                  padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0',
                  background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px',
                  cursor: 'pointer'
                }}>Cancel</button>
                <button onClick={handleTaskSave} disabled={!isDirty} style={{
                  padding: '8px 24px', borderRadius: '8px', border: 'none',
                  background: isDirty ? 'linear-gradient(135deg, #22c55e, #16a34a)' : '#e2e8f0',
                  color: isDirty ? 'white' : '#94a3b8', fontWeight: 700, fontSize: '13px',
                  cursor: isDirty ? 'pointer' : 'not-allowed', boxShadow: isDirty ? '0 3px 10px rgba(34,197,94,0.4)' : 'none'
                }}>Save</button>
              </div>
            </div>
          </>
        );
      })()}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="modal-backdrop show" style={{ display: 'block' }}></div>
      )}
      <div className={`modal ${showDeleteConfirmModal ? 'show' : ''}`} style={{ display: showDeleteConfirmModal ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '500px', width: '90%' }}>
          <div className="modal-content">
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">{(deleteType === 'small_team' || deleteType === 'medium_team' || deleteType === 'large_team') ? 'Delete Team' : 'Delete Connection'}</h5>
              <button type="button" className="btn-close" onClick={cancelDelete} aria-label="Close" style={{ filter: 'invert(1)' }}></button>
            </div>
            <div className="modal-body p-4" style={{ 
              background: '#fff',
              fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif',
              borderRadius: '0 0 8px 8px'
            }}>
              <p>
                {(deleteType === 'small_team' || deleteType === 'medium_team' || deleteType === 'large_team')
                  ? 'Are you sure you want to remove this team and all its members?' 
                  : deleteType === 'solo'
                  ? 'Are you sure you want to remove this solo connection?'
                  : 'Are you sure you want to remove this individual connection?'}
              </p>
            </div>
            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              <Button 
                variant="secondary" 
                onClick={cancelDelete} 
                style={{ 
                  background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                  border: 'none',
                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                  transition: 'all 0.2s ease-in-out',
                  padding: '8px 20px'
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="danger" 
                onClick={confirmDelete}
                style={{ 
                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                  transition: 'all 0.2s ease-in-out',
                  padding: '8px 20px'
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Billing modal is now rendered globally in App.js */}

      {/* Calendar Modal */}
      {showCalendarModal && (
        <div className="modal-backdrop show" style={{ display: 'block' }} />
      )}
      <div className={`modal ${showCalendarModal ? 'show' : ''}`} style={{ display: showCalendarModal ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '95vw', width: '95vw', margin: '0 auto' }}>
          <div className="modal-content" style={{ height: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif', position: 'relative' }}>

            {/* Modal header */}
            <div className="modal-header" style={{ background: '#282c34da', color: 'white', borderBottom: '1px solid #444', flexShrink: 0, padding: '8px 16px' }}>
              <h5 className="modal-title" style={{ color: 'white', fontWeight: 600, fontSize: '18px', margin: 0 }}>
                Calendar — {calendarModalTeam?.team_name}
              </h5>
              <button type="button" className="btn-close" aria-label="Close" style={{ filter: 'invert(1)' }}
                onClick={() => {
                  if (Object.values(unpublishedChangesRef.current).some(v => v.count > 0)) {
                    setCalendarCloseConfirm(true);
                  } else {
                    setShowCalendarModal(false);
                  }
                }}
              />
            </div>

            {/* Modal body */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
              {calendarModalLoading ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
                  <div className="spinner-border" role="status" style={{ color: '#1a73e8', width: '2.5rem', height: '2.5rem' }}>
                    <span className="visually-hidden">Loading…</span>
                  </div>
                  <p style={{ color: '#6c757d', margin: 0 }}>Loading calendar…</p>
                </div>
              ) : (() => {
                // ── Build property color map ──────────────────────────────
                const propColors = {};
                [...new Set(calendarModalData.map(r => r.property_address).filter(Boolean))].sort()
                  .forEach((addr, i) => { propColors[addr] = CAL_PROPERTY_COLORS[i % CAL_PROPERTY_COLORS.length]; });

                const allCalEvents = calendarModalData.flatMap((record, recordIdx) =>
                  (record.tasks || []).map((task, taskIdx) => ({
                    ...task, record, recordIdx, taskIdx,
                    date: (task.start || task.due) ? new Date(task.start || task.due) : null,
                  }))
                );
                const visibleCalEvents = allCalEvents.filter(e => !calHiddenProperties.has(e.record?.property_address));
                const eventsForDay = (day) =>
                  visibleCalEvents.filter(e => e.date && calIsSameDay(e.date, day))
                    .sort((a, b) => (a.date?.getTime() || 0) - (b.date?.getTime() || 0));
                const calChip = (ev) => {
                  const color = propColors[ev.record?.property_address] || '#94a3b8';
                  const done = ev.status === 'completed';
                  const now = new Date();
                  const overdue = !done && ev.date && ev.date < now;
                  return { color, done, overdue };
                };
                const openEdit = (ev) => {
                  const inferredType = ev.item_type || (ev.start_datetime || ev.end_datetime ? 'scheduled_event' : 'deadline');
                  const task = {
                    title: ev.title,
                    due: ev.due || ev.start || ev.start_datetime,
                    start: ev.start || ev.start_datetime || ev.due,
                    end: ev.end || ev.end_datetime || null,
                    all_day: ev.all_day || false,
                    location: ev.location || '', description: ev.description || '',
                    status: ev.status || 'notStarted', action_items: ev.action_items || [],
                    item_type: inferredType,
                    task_id: ev.task_id, update_timestamp: ev.update_timestamp,
                  };
                  setEditingTask({ task: { ...task }, originalTask: { ...task }, record: ev.record, recordIdx: ev.recordIdx, taskIdx: ev.taskIdx, isCalendarModal: true });
                };
                const allVisible = calHiddenProperties.size === 0;
                const properties = Object.keys(propColors);
                const today = new Date();

                return (
                  <>
                    {/* Sidebar */}
                    <div style={{ width: '288px', flexShrink: 0, borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflowY: 'auto', background: '#fafbfc' }}>

                      {/* Mini calendar */}
                      {(() => {
                        const year = calMiniDate.getFullYear();
                        const month = calMiniDate.getMonth();
                        const miniDays = calGetMonthGrid(year, month);
                        return (
                          <div style={{ padding: '14px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                              <button onClick={() => setCalMiniDate(d => calAddMonths(d, -1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#64748b', padding: '2px 6px', lineHeight: 1 }}>‹</button>
                              <div style={{ flex: 1, textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#334155' }}>{CAL_MONTH_NAMES[month]} {year}</div>
                              <button onClick={() => setCalMiniDate(d => calAddMonths(d, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#64748b', padding: '2px 6px', lineHeight: 1 }}>›</button>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '4px' }}>
                              {CAL_MINI_LABELS.map((d, i) => <div key={i} style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', fontWeight: 600, padding: '2px 0' }}>{d}</div>)}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                              {miniDays.map((day, i) => {
                                const isToday = calIsSameDay(day, today);
                                const isSelected = calIsSameDay(day, calCurrentDate);
                                const inMonth = calIsSameMonth(day, calMiniDate);
                                const hasEvs = visibleCalEvents.some(e => e.date && calIsSameDay(e.date, day));
                                return (
                                  <div key={i} onClick={() => { setCalCurrentDate(day); setCalMiniDate(day); }}
                                    style={{ textAlign: 'center', fontSize: '12px', padding: '4px 2px', cursor: 'pointer', borderRadius: '50%', position: 'relative', background: isSelected ? '#1a73e8' : isToday ? '#e8f0fe' : 'transparent', color: isSelected ? '#fff' : isToday ? '#1a73e8' : inMonth ? '#334155' : '#cbd5e1', fontWeight: isToday || isSelected ? 700 : 400 }}>
                                    {day.getDate()}
                                    {hasEvs && !isSelected && <div style={{ position: 'absolute', bottom: '1px', left: '50%', transform: 'translateX(-50%)', width: '4px', height: '4px', borderRadius: '50%', background: isToday ? '#1a73e8' : '#94a3b8' }} />}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Overview stats */}
                      {(() => {
                        const calTotal = visibleCalEvents.length;
                        const calCompleted = visibleCalEvents.filter(e => e.status === 'completed').length;
                        const calInProgress = visibleCalEvents.filter(e => e.status === 'inProgress').length;
                        const calOverdue = visibleCalEvents.filter(e => e.status !== 'completed' && e.date && e.date < today).length;
                        return (
                          <div style={{ padding: '10px 16px', borderTop: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Overview</div>
                            {[
                              { label: 'Total tasks', count: calTotal,      color: '#64748b' },
                              { label: 'Completed',   count: calCompleted,  color: '#22c55e' },
                              { label: 'In progress', count: calInProgress, color: '#3b82f6' },
                              { label: 'Overdue',     count: calOverdue,    color: '#ef4444' },
                            ].map(({ label, count, color }) => (
                              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                <span style={{ fontSize: '13px', color: '#475569' }}>{label}</span>
                                <span style={{ fontSize: '13px', fontWeight: 700, color }}>{count}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}

                      {/* No Due Date */}
                      {(() => {
                        const unscheduled = visibleCalEvents.filter(e => !e.date);
                        if (!unscheduled.length) return null;
                        return (
                          <div style={{ padding: '10px 16px', borderTop: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>No Due Date ({unscheduled.length})</div>
                            {unscheduled.slice(0, 4).map((ev, i) => (
                              <div key={i} onClick={() => openEdit(ev)} title={ev.title}
                                style={{ background: propColors[ev.record?.property_address] || '#94a3b8', color: '#fff', borderRadius: '4px', padding: '4px 8px', marginBottom: '4px', fontSize: '12px', fontWeight: 500, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {ev.title}
                              </div>
                            ))}
                            {unscheduled.length > 4 && <div style={{ fontSize: '12px', color: '#94a3b8' }}>+{unscheduled.length - 4} more</div>}
                          </div>
                        );
                      })()}

                      {/* My Calendars */}
                      <div style={{ padding: '10px 16px', borderTop: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>My Calendars</div>
                          <button onClick={() => setCalHiddenProperties(allVisible ? new Set(properties) : new Set())}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#1a73e8', fontWeight: 600, padding: 0 }}>
                            {allVisible ? 'Hide all' : 'Show all'}
                          </button>
                        </div>
                        {properties.map(addr => {
                          const color = propColors[addr];
                          const hidden = calHiddenProperties.has(addr);
                          const propEvs = allCalEvents.filter(e => e.record?.property_address === addr);
                          const doneCount = propEvs.filter(e => e.status === 'completed').length;
                          const overdueCount = propEvs.filter(e => e.status !== 'completed' && e.date && e.date < today).length;
                          const addrTitles = [...new Set(
                            allCalEvents
                              .filter(ev => ev.record?.property_address === addr)
                              .map(ev => ev.record?.task_list_title)
                              .filter(t => t)
                          )];
                          const unpubCount = addrTitles.reduce((sum, t) => sum + (unpublishedChanges[t]?.count || 0), 0);
                          if (Object.keys(unpublishedChanges).length > 0) {
                            console.log('[CalConn] badge render →', addr, '| addrTitles:', addrTitles, '| unpubCount:', unpubCount, '| unpublishedChanges:', unpublishedChanges);
                          }
                          const dirtyTitle = addrTitles.find(t => unpublishedChanges[t]?.count > 0);
                          const propRecord = calendarModalData.find(r => r.task_list_title === (dirtyTitle || addrTitles[0]))
                            || calendarModalData.find(r => r.property_address === addr);
                          const isPublishing = publishingTitle === propRecord?.task_list_title;
                          const isDeleting = deletingTitle === propRecord?.task_list_title;
                          const isArchiving = archivingTitle === propRecord?.task_list_title;
                          const isRestoring = restoringTitle === propRecord?.task_list_title;
                          const isArchived = propRecord?.archive === true;
                          return (
                            <div key={addr}
                              onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                              style={{ padding: '6px 4px', borderRadius: '6px' }}>
                              <div onClick={() => setCalHiddenProperties(prev => { const n = new Set(prev); hidden ? n.delete(addr) : n.add(addr); return n; })}
                                style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                                <div style={{ width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0, background: hidden ? 'transparent' : color, border: `2px solid ${color}`, transition: 'background 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {!hidden && <span style={{ color: '#fff', fontSize: '10px', fontWeight: 900, lineHeight: 1 }}>✓</span>}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '13px', color: hidden ? '#94a3b8' : '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: hidden ? 'line-through' : 'none', textAlign: 'left' }}>{propRecord?.task_list_title || addr}</div>
                                  {!hidden && (
                                    <div style={{ display: 'flex', gap: '4px', marginTop: '3px', flexWrap: 'wrap' }}>
                                      <span style={{ fontSize: '10px', color: '#64748b' }}>{propEvs.length} event{propEvs.length !== 1 ? 's' : ''}</span>
                                      {doneCount > 0 && <span style={{ fontSize: '10px', color: '#22c55e', fontWeight: 600 }}>· {doneCount} done</span>}
                                      {overdueCount > 0 && <span style={{ fontSize: '10px', color: '#ef4444', fontWeight: 600 }}>· {overdueCount} overdue</span>}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {/* Unpublished badge + Publish button */}
                              {unpubCount > 0 && propRecord && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px', paddingLeft: '26px' }}>
                                  <button
                                    type="button"
                                    onClick={e => { e.stopPropagation(); handleFlashCalUnpublished(dirtyTitle); }}
                                    onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                                    onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                                    style={{ fontSize: '10px', color: '#fbbf24', fontWeight: 600, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}
                                  >
                                    {unpubCount === 1 ? '1 unshared change' : `${unpubCount} unshared changes`}
                                  </button>
                                  <button
                                    onClick={e => { e.stopPropagation(); openPublishModal(propRecord); }}
                                    disabled={isPublishing}
                                    style={{ padding: '2px 9px', borderRadius: '6px', border: 'none', fontSize: '10px', fontWeight: 700, cursor: isPublishing ? 'not-allowed' : 'pointer', background: isPublishing ? '#e2e8f0' : 'linear-gradient(135deg, #3b82f6, #2563eb)', color: isPublishing ? '#94a3b8' : '#fff' }}>
                                    {isPublishing ? '⏳…' : 'Share'}
                                  </button>
                                </div>
                              )}
                              {/* Management buttons */}
                              {propRecord && (canManageCalendars || canArchiveTaskLists || tasksCanDelete) && (
                                <div style={{ display: 'flex', gap: '5px', marginTop: '6px', paddingLeft: '26px', flexWrap: 'wrap' }}>
                                  {!isArchived && canManageCalendars && (
                                    <button
                                      onClick={e => { e.stopPropagation(); handleAddCalEvent(propRecord, calCurrentDate); }}
                                      style={{ flex: 1, padding: '3px 6px', borderRadius: '5px', border: '1px solid #93c5fd', background: '#eff6ff', color: '#1d4ed8', fontSize: '10px', fontWeight: 600, cursor: 'pointer' }}>
                                      + Add Event
                                    </button>
                                  )}
                                  {!isArchived && canArchiveTaskLists && (
                                    <button
                                      onClick={e => { e.stopPropagation(); handleConfirmArchive(propRecord.task_list_title); }}
                                      disabled={isArchiving}
                                      style={{ padding: '3px 7px', borderRadius: '5px', border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', fontSize: '10px', fontWeight: 600, cursor: isArchiving ? 'not-allowed' : 'pointer', opacity: isArchiving ? 0.6 : 1 }}>
                                      {isArchiving ? '⏳' : 'Archive'}
                                    </button>
                                  )}
                                  {isArchived && canArchiveTaskLists && (
                                    <button
                                      onClick={e => { e.stopPropagation(); handleRestore(propRecord.task_list_title); }}
                                      disabled={isRestoring}
                                      style={{ padding: '3px 7px', borderRadius: '5px', border: '1px solid #86efac', background: '#f0fdf4', color: '#15803d', fontSize: '10px', fontWeight: 600, cursor: isRestoring ? 'not-allowed' : 'pointer', opacity: isRestoring ? 0.6 : 1 }}>
                                      {isRestoring ? '⏳' : 'Unarchive'}
                                    </button>
                                  )}
                                  {tasksCanDelete && (
                                    <button
                                      onClick={e => { e.stopPropagation(); setConfirmDeleteRecord(propRecord); }}
                                      disabled={isDeleting}
                                      style={{ padding: '3px 7px', borderRadius: '5px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#b91c1c', fontSize: '10px', fontWeight: 600, cursor: isDeleting ? 'not-allowed' : 'pointer', opacity: isDeleting ? 0.6 : 1 }}>
                                      {isDeleting ? '…' : 'Delete'}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Main calendar area */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                      {/* Toolbar */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '1px solid #e2e8f0', flexShrink: 0, background: '#fff' }}>
                        <button onClick={() => { setCalCurrentDate(new Date()); setCalMiniDate(new Date()); }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                          onMouseLeave={e => e.currentTarget.style.background = 'white'}
                          style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #dadce0', background: 'white', color: '#3c4043', fontWeight: 500, fontSize: '13px', cursor: 'pointer' }}>
                          Today
                        </button>
                        <div style={{ display: 'flex' }}>
                          {[['‹', -1], ['›', 1]].map(([arrow, dir]) => (
                            <button key={arrow} onClick={() => setCalCurrentDate(d => calView === 'month' ? calAddMonths(d, dir) : calView === 'week' ? calAddDays(d, dir * 7) : calAddDays(d, dir))}
                              onMouseEnter={e => e.currentTarget.style.background = '#f1f3f4'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                              style={{ width: '30px', height: '30px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '18px', color: '#3c4043', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                              {arrow}
                            </button>
                          ))}
                        </div>
                        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 400, color: '#3c4043', flex: 1 }}>
                          {calView === 'month' ? calFormatMonthYear(calCurrentDate) : calView === 'week' ? calFormatWeekRange(calCurrentDate) : calFormatDayFull(calCurrentDate)}
                        </h2>
                        <div style={{ display: 'flex', borderRadius: '6px', border: '1px solid #dadce0', overflow: 'hidden' }}>
                          {[['month','Month'],['week','Week'],['day','Day']].map(([v, label]) => (
                            <button key={v} onClick={() => setCalView(v)}
                              style={{ padding: '6px 12px', border: 'none', background: calView === v ? '#e8f0fe' : 'white', color: calView === v ? '#1967d2' : '#3c4043', fontWeight: calView === v ? 600 : 400, fontSize: '13px', cursor: 'pointer', borderRight: v !== 'day' ? '1px solid #dadce0' : 'none' }}>
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Month view */}
                      {calView === 'month' && (() => {
                        const days = calGetMonthGrid(calCurrentDate.getFullYear(), calCurrentDate.getMonth());
                        return (
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                              {CAL_DAY_LABELS.map(d => <div key={d} style={{ textAlign: 'center', padding: '6px 0', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{d}</div>)}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: 'repeat(6, 1fr)', flex: 1, minHeight: 0 }}>
                              {days.map((day, i) => {
                                const isToday = calIsSameDay(day, today);
                                const inMonth = calIsSameMonth(day, calCurrentDate);
                                const dayEvs = eventsForDay(day);
                                const overflow = dayEvs.length - 3;
                                return (
                                  <div key={i} onClick={() => { setCalCurrentDate(day); setCalView('day'); }}
                                    onMouseEnter={e => { if (!isToday) e.currentTarget.style.background = '#f8fafc'; }}
                                    onMouseLeave={e => { if (!isToday) e.currentTarget.style.background = '#fff'; }}
                                    style={{ border: '1px solid #f1f5f9', borderTop: '1px solid #e2e8f0', padding: '3px 5px', background: isToday ? '#eff6ff' : '#fff', cursor: 'pointer', overflow: 'hidden', minHeight: 0, transition: 'background 0.1s' }}>
                                    <div style={{ width: '24px', height: '24px', borderRadius: '50%', marginBottom: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isToday ? '#1a73e8' : 'transparent', color: isToday ? '#fff' : inMonth ? '#1e293b' : '#cbd5e1', fontSize: '12px', fontWeight: isToday ? 700 : inMonth ? 500 : 400 }}>
                                      {day.getDate()}
                                    </div>
                                    {dayEvs.slice(0, 3).map((ev, ei) => {
                                      const { color, done, overdue } = calChip(ev);
                                      const isUnpublished = !!ev.task_id && !!unpublishedTaskIds[ev.record?.task_list_title]?.has(ev.task_id);
                                      const isFlashing = isUnpublished && flashingTitles.has(ev.record?.task_list_title);
                                      return (
                                        <div key={ei}
                                          ref={isUnpublished && ev.task_id ? el => { const k = `${ev.record.task_list_title}:::${ev.task_id}`; if (el) unpublishedCardRefs.current[k] = el; else delete unpublishedCardRefs.current[k]; } : undefined}
                                          onClick={e => { e.stopPropagation(); openEdit(ev); }} title={ev.title}
                                          style={{ background: color, color: '#fff', borderRadius: '3px', padding: '1px 4px', fontSize: '10px', fontWeight: 500, marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', lineHeight: '1.6', opacity: done ? 0.45 : 1, outline: isUnpublished ? '2px solid #f97316' : overdue ? '1.5px dashed rgba(255,255,255,0.7)' : 'none', animation: isFlashing ? 'unpublishedPop 0.7s ease-out' : undefined }}>
                                          <span style={{ opacity: 0.9, marginRight: '3px', fontSize: '9px' }}>{ev.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                                          <span style={{ textDecoration: done ? 'line-through' : 'none' }}>{overdue ? '! ' : ''}{ev.title}</span>
                                        </div>
                                      );
                                    })}
                                    {overflow > 0 && (
                                      <div onClick={e => { e.stopPropagation(); setCalCurrentDate(day); setCalView('day'); }}
                                        style={{ fontSize: '10px', color: '#1a73e8', fontWeight: 600, cursor: 'pointer' }}>+{overflow} more</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Week view */}
                      {calView === 'week' && (() => {
                        const weekStart = calStartOfWeek(calCurrentDate);
                        const weekDays = Array.from({ length: 7 }, (_, i) => calAddDays(weekStart, i));
                        const CELL_H = 44;
                        return (
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '48px repeat(7, 1fr)', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                              <div />
                              {weekDays.map((day, i) => {
                                const isToday = calIsSameDay(day, today);
                                return (
                                  <div key={i} style={{ textAlign: 'center', padding: '6px 0', cursor: 'pointer' }} onClick={() => { setCalCurrentDate(day); setCalView('day'); }}>
                                    <div style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>{CAL_DAY_LABELS[day.getDay()]}</div>
                                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '50%', marginTop: '2px', background: isToday ? '#1a73e8' : 'transparent', color: isToday ? '#fff' : '#1e293b', fontSize: '15px', fontWeight: isToday ? 700 : 400 }}>{day.getDate()}</div>
                                  </div>
                                );
                              })}
                            </div>
                            <div ref={calTimeGridRef} style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '48px repeat(7, 1fr)', height: `${24 * CELL_H}px`, position: 'relative' }}>
                                <div style={{ position: 'relative' }}>
                                  {CAL_HOURS.map(h => <div key={h} style={{ position: 'absolute', top: `${h * CELL_H}px`, right: '4px', fontSize: '9px', color: '#94a3b8', transform: 'translateY(-50%)', whiteSpace: 'nowrap', lineHeight: 1 }}>{h === 0 ? '' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`}</div>)}
                                </div>
                                {weekDays.map((day, di) => {
                                  const isToday = calIsSameDay(day, today);
                                  const dayEvs = eventsForDay(day);
                                  return (
                                    <div key={di} style={{ position: 'relative', borderLeft: '1px solid #f1f5f9', background: isToday ? '#fafbff' : '#fff' }}>
                                      {CAL_HOURS.map(h => <div key={h} style={{ position: 'absolute', top: `${h * CELL_H}px`, left: 0, right: 0, borderTop: '1px solid #f1f5f9' }} />)}
                                      {isToday && (() => { const now = new Date(); const pct = (now.getHours() * 60 + now.getMinutes()) / 1440 * 100; return <div style={{ position: 'absolute', top: `${pct}%`, left: 0, right: 0, height: '2px', background: '#1a73e8', zIndex: 2 }}><div style={{ position: 'absolute', left: '-4px', top: '-4px', width: '8px', height: '8px', borderRadius: '50%', background: '#1a73e8' }} /></div>; })()}
                                      {dayEvs.map((ev, ei) => {
                                        const { color, done, overdue } = calChip(ev);
                                        const topPct = (ev.date.getHours() * 60 + ev.date.getMinutes()) / 1440 * 100;
                                        const isUnpublished = !!ev.task_id && !!unpublishedTaskIds[ev.record?.task_list_title]?.has(ev.task_id);
                                        const isFlashing = isUnpublished && flashingTitles.has(ev.record?.task_list_title);
                                        return (
                                          <div key={ei}
                                            ref={isUnpublished && ev.task_id ? el => { const k = `${ev.record.task_list_title}:::${ev.task_id}`; if (el) unpublishedCardRefs.current[k] = el; else delete unpublishedCardRefs.current[k]; } : undefined}
                                            onClick={() => openEdit(ev)} title={ev.title}
                                            style={{ position: 'absolute', top: `${topPct}%`, left: `${(ei % 2) * 48}%`, width: '46%', minHeight: '16px', background: color, color: '#fff', borderRadius: '3px', padding: '2px 4px', fontSize: '10px', fontWeight: 500, cursor: 'pointer', zIndex: 1, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.15)', lineHeight: '1.4', opacity: done ? 0.45 : 1, outline: isUnpublished ? '2px solid #f97316' : overdue ? '1.5px dashed rgba(255,255,255,0.6)' : 'none', animation: isFlashing ? 'unpublishedPop 0.7s ease-out' : undefined }}>
                                            <div style={{ fontSize: '9px', opacity: 0.85 }}>{ev.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</div>
                                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: done ? 'line-through' : 'none' }}>{overdue ? '! ' : ''}{ev.title}</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Day view */}
                      {calView === 'day' && (() => {
                        const isToday = calIsSameDay(calCurrentDate, today);
                        const dayEvs = eventsForDay(calCurrentDate);
                        const CELL_H = 56;
                        return (
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                              <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>{calCurrentDate.toLocaleDateString('en-US', { weekday: 'long' })}</div>
                              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', background: isToday ? '#1a73e8' : 'transparent', color: isToday ? '#fff' : '#1e293b', fontSize: '18px', fontWeight: 700 }}>{calCurrentDate.getDate()}</div>
                              <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#94a3b8' }}>{dayEvs.length} task{dayEvs.length !== 1 ? 's' : ''}</div>
                            </div>
                            <div ref={calTimeGridRef} style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr', height: `${24 * CELL_H}px`, position: 'relative' }}>
                                <div style={{ position: 'relative' }}>
                                  {CAL_HOURS.map(h => <div key={h} style={{ position: 'absolute', top: `${h * CELL_H}px`, right: '6px', fontSize: '10px', color: '#94a3b8', transform: 'translateY(-50%)', whiteSpace: 'nowrap', lineHeight: 1 }}>{h === 0 ? '' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`}</div>)}
                                </div>
                                <div style={{ position: 'relative', borderLeft: '1px solid #e2e8f0' }}>
                                  {CAL_HOURS.map(h => <div key={h} style={{ position: 'absolute', top: `${h * CELL_H}px`, left: 0, right: 0, borderTop: '1px solid #f1f5f9' }} />)}
                                  {isToday && (() => { const now = new Date(); const pct = (now.getHours() * 60 + now.getMinutes()) / 1440 * 100; return <div style={{ position: 'absolute', top: `${pct}%`, left: 0, right: 0, height: '2px', background: '#1a73e8', zIndex: 2 }}><div style={{ position: 'absolute', left: '-4px', top: '-4px', width: '8px', height: '8px', borderRadius: '50%', background: '#1a73e8' }} /></div>; })()}
                                  {dayEvs.map((ev, ei) => {
                                    const { color, done, overdue } = calChip(ev);
                                    const topPct = (ev.date.getHours() * 60 + ev.date.getMinutes()) / 1440 * 100;
                                    const isUnpublished = !!ev.task_id && !!unpublishedTaskIds[ev.record?.task_list_title]?.has(ev.task_id);
                                    const isFlashing = isUnpublished && flashingTitles.has(ev.record?.task_list_title);
                                    return (
                                      <div key={ei}
                                        ref={isUnpublished && ev.task_id ? el => { const k = `${ev.record.task_list_title}:::${ev.task_id}`; if (el) unpublishedCardRefs.current[k] = el; else delete unpublishedCardRefs.current[k]; } : undefined}
                                        onClick={() => openEdit(ev)}
                                        style={{ position: 'absolute', top: `${topPct}%`, left: `${ei * 4}px`, right: '8px', minHeight: '44px', background: color, color: '#fff', borderRadius: '6px', padding: '5px 9px', cursor: 'pointer', zIndex: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', borderLeft: '4px solid rgba(0,0,0,0.15)', opacity: done ? 0.5 : 1, outline: isUnpublished ? '2px solid #f97316' : overdue ? '2px dashed rgba(255,255,255,0.6)' : 'none', animation: isFlashing ? 'unpublishedPop 0.7s ease-out' : undefined }}>
                                        <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '2px', textDecoration: done ? 'line-through' : 'none' }}>{overdue ? '! ' : ''}{ev.title}</div>
                                        <div style={{ fontSize: '11px', opacity: 0.9 }}>{ev.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} · {ev.record.task_list_title}</div>
                                        {ev.action_items?.length > 0 && <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '2px' }}>{ev.action_items.length} action item{ev.action_items.length !== 1 ? 's' : ''}</div>}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* ── Edit Event Popup (Calendar Modal) — inside .modal-content so Bootstrap doesn't interfere ── */}
            {editingTask?.isCalendarModal && (() => {
              const et = editingTask.task;
              const isNew = !!editingTask.isNewEvent;
              const calAddrs = [...new Set(calendarModalData.map(r => r.property_address).filter(Boolean))].sort();
              const calPropColors = {};
              calAddrs.forEach((addr, i) => { calPropColors[addr] = CAL_PROPERTY_COLORS[i % CAL_PROPERTY_COLORS.length]; });
              const propColor = calPropColors[editingTask.record?.property_address] || '#1a73e8';
              const statuses = [
                { key: 'completed',  label: 'Done',        color: '#22c55e', bg: '#f0fdf4' },
                { key: 'inProgress', label: 'In Progress', color: '#3b82f6', bg: '#eff6ff' },
                { key: 'notStarted', label: 'Pending',     color: '#94a3b8', bg: '#f8fafc' },
              ];
              const isDirty = isNew
                ? (et.title?.trim().length > 0)
                : JSON.stringify(et) !== JSON.stringify(editingTask.originalTask);
              const upd = (patch) => setEditingTask(prev => ({ ...prev, task: { ...prev.task, ...patch } }));
              return (
                <>
                  {/* Backdrop */}
                  <div onClick={() => setEditingTask(null)} style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 20, backdropFilter: 'blur(2px)' }} />
                  {/* Popup card */}
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 21, width: '460px', maxWidth: '92%', backgroundColor: 'white', borderRadius: '14px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)', border: `2px solid ${propColor}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '88vh' }}>
                    {/* Header */}
                    <div style={{ background: 'linear-gradient(145deg,#1a2540,#243050)', padding: '14px 18px 12px', flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: '#fff', marginBottom: '2px' }}>{isNew ? 'New Event' : 'Edit Event'}</div>
                        <div style={{ fontSize: '11px', color: '#a4afbe' }}>{editingTask.record.task_list_title}</div>
                      </div>
                      <button onClick={() => setEditingTask(null)} style={{ background: 'none', border: 'none', color: '#a4afbe', fontSize: '18px', cursor: 'pointer', lineHeight: 1, padding: '2px 4px' }}>×</button>
                    </div>
                    {/* Body */}
                    <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto', flex: 1 }}>
                      {/* Item type segmented control */}
                      <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '2px' }}>
                        {[{ key: 'deadline', label: 'Deadline' }, { key: 'scheduled_event', label: 'Scheduled Event' }].map(({ key, label }) => {
                          const active = (et.item_type || 'deadline') === key;
                          return (
                            <button key={key} onClick={() => upd({ item_type: key })}
                              style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: 'none', background: active ? 'white' : 'transparent', color: active ? propColor : '#94a3b8', fontWeight: active ? 700 : 500, fontSize: '12px', cursor: 'pointer', boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none', transition: 'all 0.15s' }}>
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      {/* Helper text */}
                      <div style={{ fontSize: '12px', color: '#64748b', background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', lineHeight: '1.5' }}>
                        {(et.item_type || 'deadline') === 'deadline'
                          ? 'Use this for contract deadlines, due dates, and closing milestones.'
                          : 'Use this for appointments, meetings, inspections, showings, and scheduled calls.'}
                      </div>
                      {/* Title */}
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>Event Title *</div>
                        <input autoFocus={isNew} value={et.title || ''} onChange={e => upd({ title: e.target.value })}
                          placeholder="Enter event title"
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: `1.5px solid ${propColor}40`, background: '#f8fafc', fontSize: '14px', fontWeight: 600, color: '#1e293b', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                      </div>
                      {/* All-day toggle */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <style>{`.cal-toggle{position:relative;display:inline-block;width:36px;height:20px}.cal-toggle input{opacity:0;width:0;height:0}.cal-slider{position:absolute;cursor:pointer;inset:0;background:#cbd5e1;border-radius:20px;transition:.2s}.cal-slider:before{position:absolute;content:"";height:14px;width:14px;left:3px;bottom:3px;background:white;border-radius:50%;transition:.2s}input:checked+.cal-slider{background:#3b82f6}input:checked+.cal-slider:before{transform:translateX(16px)}`}</style>
                        <label className="cal-toggle">
                          <input type="checkbox" checked={!!et.all_day} onChange={e => upd({ all_day: e.target.checked })} />
                          <span className="cal-slider" />
                        </label>
                        <span style={{ fontSize: '13px', color: '#475569', fontWeight: 500 }}>All day</span>
                      </div>
                      {/* Deadline mode: Due Date */}
                      {(et.item_type || 'deadline') === 'deadline' && (
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>Due Date</div>
                          {et.all_day
                            ? <input type="date" value={toDateLocal(et.due || et.start)}
                                onClick={e => { try { e.target.showPicker(); } catch {} }}
                                onChange={e => { const iso = e.target.value ? new Date(e.target.value + 'T00:00:00').toISOString() : null; upd({ due: iso, start: iso }); }}
                                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px', color: '#1e293b', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }} />
                            : <input type="datetime-local" value={toDatetimeLocal(et.due || et.start)}
                                onClick={e => { try { e.target.showPicker(); } catch {} }}
                                onChange={e => { const iso = e.target.value ? new Date(e.target.value).toISOString() : null; upd({ due: iso, start: iso }); }}
                                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px', color: '#1e293b', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }} />
                          }
                        </div>
                      )}
                      {/* Scheduled Event mode: Start + End + Location */}
                      {et.item_type === 'scheduled_event' && (
                        <>
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>Start</div>
                            {et.all_day
                              ? <input type="date" value={toDateLocal(et.start || et.due)}
                                  onClick={e => { try { e.target.showPicker(); } catch {} }}
                                  onChange={e => { const iso = e.target.value ? new Date(e.target.value + 'T00:00:00').toISOString() : null; upd({ start: iso, due: iso }); }}
                                  style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px', color: '#1e293b', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }} />
                              : <input type="datetime-local" value={toDatetimeLocal(et.start || et.due)}
                                  onClick={e => { try { e.target.showPicker(); } catch {} }}
                                  onChange={e => { const iso = e.target.value ? new Date(e.target.value).toISOString() : null; upd({ start: iso, due: iso }); }}
                                  style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px', color: '#1e293b', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }} />
                            }
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>End <span style={{ color: '#cbd5e1', textTransform: 'none', fontWeight: 400, fontSize: '10px' }}>optional</span></div>
                            {et.all_day
                              ? <input type="date" value={toDateLocal(et.end)}
                                  onClick={e => { try { e.target.showPicker(); } catch {} }}
                                  onChange={e => upd({ end: e.target.value ? new Date(e.target.value + 'T00:00:00').toISOString() : null })}
                                  style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px', color: '#1e293b', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }} />
                              : <input type="datetime-local" value={toDatetimeLocal(et.end)}
                                  onClick={e => { try { e.target.showPicker(); } catch {} }}
                                  onChange={e => upd({ end: e.target.value ? new Date(e.target.value).toISOString() : null })}
                                  style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px', color: '#1e293b', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }} />
                            }
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>Location <span style={{ color: '#cbd5e1', textTransform: 'none', fontWeight: 400, fontSize: '10px' }}>optional</span></div>
                            <input placeholder="Add location" value={et.location || ''} onChange={e => upd({ location: e.target.value })}
                              style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px', color: '#1e293b', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                          </div>
                        </>
                      )}
                      {/* Action Items (both modes) */}
                      {(() => {
                        const isCompleted = et.status === 'completed';
                        const isInProgress = et.status === 'inProgress';
                        const aiColor  = isCompleted ? '#22c55e' : isInProgress ? '#3b82f6' : '#94a3b8';
                        const aiBorder = isCompleted ? '#bbf7d0' : isInProgress ? '#bfdbfe' : '#e2e8f0';
                        return (
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>Action Items</div>
                            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 12px', border: `1px solid ${aiBorder}`, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {(et.action_items || []).map((item, i) => {
                                const isExpanded = editTaskExpandedItems.has(i);
                                return (
                                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '14px 1fr 22px', gap: '6px', alignItems: isExpanded ? 'flex-start' : 'center' }}>
                                    <span
                                      onClick={() => setEditTaskExpandedItems(prev => {
                                        const next = new Set(prev);
                                        isExpanded ? next.delete(i) : next.add(i);
                                        return next;
                                      })}
                                      style={{ color: aiColor, fontSize: '12px', fontWeight: 700, lineHeight: '1.5', textAlign: 'center', cursor: 'pointer', userSelect: 'none', marginTop: isExpanded ? '2px' : 0 }}
                                    >{isExpanded ? '⌄' : '›'}</span>
                                    {isExpanded ? (
                                      <textarea
                                        ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                                        value={item}
                                        rows={1}
                                        onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                                        onChange={e => setEditingTask(prev => {
                                          const items = [...(prev.task.action_items || [])];
                                          items[i] = e.target.value;
                                          return { ...prev, task: { ...prev.task, action_items: items } };
                                        })}
                                        style={{ fontSize: '12px', color: '#1e293b', border: 'none', borderBottom: `1px solid ${aiBorder}`, background: 'transparent', outline: 'none', padding: '1px 2px', width: '100%', fontFamily: 'inherit', resize: 'none', overflow: 'hidden', lineHeight: '1.5', display: 'block' }}
                                      />
                                    ) : (
                                      <span
                                        onClick={() => setEditTaskExpandedItems(prev => { const next = new Set(prev); next.add(i); return next; })}
                                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                        style={{ fontSize: '12px', color: item ? '#1e293b' : '#cbd5e1', fontStyle: item ? 'normal' : 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', borderRadius: '3px', padding: '1px 2px', margin: '0 -2px' }}
                                      >{item || 'click to edit'}</span>
                                    )}
                                    <button
                                      onClick={() => {
                                        setEditingTask(prev => ({ ...prev, task: { ...prev.task, action_items: (prev.task.action_items || []).filter((_, idx) => idx !== i) } }));
                                        setEditTaskExpandedItems(prev => {
                                          const next = new Set();
                                          for (const idx of prev) {
                                            if (idx < i) next.add(idx);
                                            else if (idx > i) next.add(idx - 1);
                                          }
                                          return next;
                                        });
                                      }}
                                      title="Remove"
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: '14px', lineHeight: 1, padding: 0 }}>×</button>
                                  </div>
                                );
                              })}
                              <button
                                onClick={() => {
                                  const newIdx = (et.action_items || []).length;
                                  setEditingTask(prev => ({ ...prev, task: { ...prev.task, action_items: [...(prev.task.action_items || []), ''] } }));
                                  setEditTaskExpandedItems(prev => { const next = new Set(prev); next.add(newIdx); return next; });
                                }}
                                style={{ marginTop: '4px', padding: '4px 10px', borderRadius: '6px', border: `1.5px dashed ${aiColor}`, background: 'transparent', color: aiColor, fontSize: '11px', fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start', letterSpacing: '0.3px' }}>
                                + Add Item
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                      {/* Status */}
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>Status</div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {statuses.map(s => {
                            const active = et.status === s.key || (!et.status && s.key === 'notStarted');
                            return (
                              <button key={s.key} onClick={() => upd({ status: s.key })}
                                style={{ flex: 1, padding: '7px 4px', borderRadius: '8px', border: `2px solid ${active ? s.color : '#e2e8f0'}`, background: active ? s.bg : 'white', color: active ? s.color : '#94a3b8', fontWeight: active ? 700 : 500, fontSize: '12px', cursor: 'pointer', outline: 'none' }}>
                                {s.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    {/* Footer */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: isNew ? 'flex-end' : 'space-between', gap: '10px', padding: '12px 20px', borderTop: '1px solid #f1f5f9', background: '#fafbfc', flexShrink: 0 }}>
                      {!isNew && canManageCalendars && (
                        <button
                          onClick={() => setConfirmRemoveTask({ record: editingTask.record, taskIdx: editingTask.taskIdx, taskTitle: et.title })}
                          style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: 'rgba(239,68,68,0.08)', color: '#ef4444', fontWeight: 600, fontSize: '12px', cursor: 'pointer' }}>
                          Remove Event
                        </button>
                      )}
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button onClick={() => setEditingTask(null)} style={{ padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                        <button onClick={handleTaskSave} disabled={!isDirty}
                          style={{ padding: '8px 24px', borderRadius: '8px', border: 'none', background: isDirty ? 'linear-gradient(135deg,#22c55e,#16a34a)' : '#e2e8f0', color: isDirty ? 'white' : '#94a3b8', fontWeight: 700, fontSize: '13px', cursor: isDirty ? 'pointer' : 'not-allowed', boxShadow: isDirty ? '0 3px 10px rgba(34,197,94,0.4)' : 'none' }}>
                          {isNew ? 'Add Event' : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Close guard — absolute overlay inside modal-content, no z-index battle */}
            {calendarCloseConfirm && (
              <div style={{
                position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10
              }}>
                <div style={{
                  width: '400px', maxWidth: '90%', backgroundColor: 'white',
                  borderRadius: '14px', overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                }}>
                  <div style={{ height: '4px', background: '#f59e0b' }} />
                  <div style={{ padding: '24px 24px 20px' }}>
                    <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', margin: '0 0 10px' }}>
                      Unshared Calendar Changes
                    </h3>
                    <p style={{ fontSize: '13px', color: '#475569', lineHeight: '1.6', margin: '0 0 20px' }}>
                      You have unshared calendar changes. Your changes are saved for you, but they have not been shared with the team or calendars yet.
                    </p>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setCalendarCloseConfirm(false)}
                        style={{ padding: '7px 18px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                      >Cancel</button>
                      <button
                        onClick={() => { setCalendarCloseConfirm(false); setUnpublishedChanges({}); setUnpublishedTaskIds({}); unpublishedChangesRef.current = {}; setShowCalendarModal(false); }}
                        style={{ padding: '7px 18px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#ef4444,#dc2626)', color: 'white', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
                      >Leave Without Sharing</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Team Unpublished Changes modal */}
      {teamUnpublishedModal && (() => {
        const { team } = teamUnpublishedModal;
        const today = new Date();
        const etFmt = new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/New_York', year: 'numeric', month: 'short',
          day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
        });
        const fmtET = (iso) => { try { return etFmt.format(new Date(iso)) + ' ET'; } catch { return iso; } };

        // Same published_at-based filter used in PendingChanges.js — compare update_timestamp
        // against published_at so tasks published before the last publish are not counted.
        const getUnpubTasks = (record) => {
          if (!record.tasks || record.tasks.length === 0) return [];
          const publishedAt = record.published_at ? new Date(record.published_at) : null;
          return [...record.tasks]
            .filter(t => !!t.update_timestamp && (!publishedAt || new Date(t.update_timestamp) > publishedAt))
            .sort((a, b) => b.update_timestamp.localeCompare(a.update_timestamp));
        };

        // Group records by task_list_title — only include records that have real unpublished tasks.
        const groupMap = {};
        for (const record of teamUnpublishedRecords) {
          if (getUnpubTasks(record).length === 0) continue;
          const title = record.task_list_title || 'Untitled';
          if (!groupMap[title]) groupMap[title] = [];
          groupMap[title].push(record);
        }
        const latestRecTs = (rec) =>
          [...(rec.tasks || [])].map(t => t.update_timestamp || '').sort().at(-1) || rec.last_edited_at || '';
        const groups = Object.entries(groupMap)
          .map(([title, recs]) => {
            const sorted = [...recs].sort((a, b) => latestRecTs(b).localeCompare(latestRecTs(a)));
            return { title, records: sorted };
          })
          .sort((a, b) => latestRecTs(b.records[0]).localeCompare(latestRecTs(a.records[0])));

        const totalChanges = teamUnpublishedRecords.reduce((s, r) => s + getUnpubTasks(r).length, 0);
        const uniqueMemberCount = new Set(
          teamUnpublishedRecords.filter(r => getUnpubTasks(r).length > 0).map(r => (r.id || '').toLowerCase()).filter(Boolean)
        ).size;

        const toggleGroup = (title) => {
          setTeamUnpublishedCollapsed(prev => {
            const next = new Set(prev);
            if (next.has(title)) next.delete(title); else next.add(title);
            return next;
          });
        };

        return (
          <>
            <div className="modal-backdrop show" style={{ display: 'block' }} />
            <div className="modal show" style={{ display: 'block' }}>
              <div className="modal-dialog modal-dialog-centered" style={{
                maxWidth: teamUnpublishedLoading || groups.length === 0
                  ? '500px'
                  : `min(${groups.length * 316 + 60}px, 92vw)`
              }}>
                <div className="modal-content">
                  <div className="modal-header" style={{ background: '#282c34da', color: 'white', borderBottom: '1px solid #444', padding: '8px 16px' }}>
                    <h5 className="modal-title" style={{ color: 'white', fontWeight: 600, fontSize: '18px', margin: 0 }}>
                      Unshared Changes — {team.team_name}
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close"
                      style={{ filter: 'invert(1)' }}
                      onClick={() => setTeamUnpublishedModal(null)}
                    />
                  </div>
                  <div className="modal-body" style={{ background: '#f1f4f8', padding: '16px', maxHeight: '70vh', overflowY: 'auto' }}>
                    {teamUnpublishedLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                        <div className="spinner-border spinner-border-sm text-warning" role="status" style={{ marginBottom: 10 }} />
                        <p style={{ margin: 0, fontSize: 14 }}>Loading unshared changes…</p>
                      </div>
                    ) : groups.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '50px 0' }}>
                        <div style={{ fontSize: 36, marginBottom: 12, color: '#22c55e' }}>✓</div>
                        <p style={{ fontSize: 15, fontWeight: 600, margin: '0 0 6px', color: '#374151' }}>No unshared changes</p>
                        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>This team's task lists are up to date.</p>
                      </div>
                    ) : (
                      <>
                        {/* Summary bar */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '8px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8 }}>
                          <span style={{ fontSize: 16 }}>⏳</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#9a3412' }}>
                            {totalChanges} unshared change{totalChanges !== 1 ? 's' : ''} from {uniqueMemberCount} member{uniqueMemberCount !== 1 ? 's' : ''} across {groups.length} task list{groups.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        {/* Task list groups — side by side, horizontally scrollable */}
                        <div style={{ overflowX: 'auto', paddingBottom: '6px' }}>
                          <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', minWidth: 'max-content', padding: '2px 2px 4px' }}>
                          {groups.map(({ title, records: groupRecs }) => {
                            const groupTotal = groupRecs.reduce((s, r) => s + getUnpubTasks(r).length, 0);
                            if (groupTotal === 0) return null;
                            const isCollapsed = teamUnpublishedCollapsed.has(title);
                            return (
                              <div key={title} style={{ width: 300, flexShrink: 0, borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', border: '1px solid rgba(0,0,0,0.07)' }}>
                                {/* Group header */}
                                <div
                                  onClick={() => toggleGroup(title)}
                                  style={{ background: 'linear-gradient(145deg, #1a2540 0%, #243050 60%, #1e2d4a 100%)', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}
                                >
                                  <span style={{ fontWeight: 700, fontSize: 14, color: '#fff', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
                                  <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 10, fontWeight: 700, flexShrink: 0, background: 'rgba(249,115,22,0.25)', color: '#fdba74', border: '1px solid rgba(249,115,22,0.35)' }}>
                                    {groupTotal} unshared
                                  </span>
                                  <span style={{ color: '#94a3b8', fontSize: 11, flexShrink: 0, display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                                </div>
                                {/* Collapsible body */}
                                <div style={{ overflow: 'hidden', maxHeight: isCollapsed ? '0' : '4000px', transition: 'max-height 0.3s ease' }}>
                                  <div style={{ background: '#f1f4f8', padding: '12px' }}>
                                    {groupRecs.map((record, ri) => {
                                      const fromName = record.display_name || record.id || 'Unknown';
                                      const initial = (fromName[0] || '?').toUpperCase();
                                      const unpubTasks = getUnpubTasks(record);
                                      if (unpubTasks.length === 0) return null;
                                      return (
                                        <div key={ri} style={{ marginBottom: ri < groupRecs.length - 1 ? 14 : 0 }}>
                                          {/* Member row */}
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px 8px', borderBottom: '1px solid #dde3ea', marginBottom: 7 }}>
                                            <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'linear-gradient(135deg, #f97316, #ea580c)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{initial}</div>
                                            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{fromName}</span>
                                          </div>
                                          {/* Task cards */}
                                          {unpubTasks.map((task, ti) => {
                                            const dueDate = task.due ? new Date(task.due) : null;
                                            const daysUntilDue = dueDate
                                              ? Math.round((new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate()) -
                                                  new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000)
                                              : null;
                                            const isCompleted = task.status === 'completed';
                                            const isOverdue = !isCompleted && daysUntilDue !== null && daysUntilDue < 0;
                                            const urgencyText = isCompleted ? 'Completed'
                                              : !dueDate ? null
                                              : isOverdue ? `! Overdue ${Math.abs(daysUntilDue)}d`
                                              : daysUntilDue === 0 ? 'Due today'
                                              : `Due in ${daysUntilDue}d`;
                                            const exactDate = dueDate
                                              ? dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                              : '';
                                            const dueLine = isCompleted
                                              ? (dueDate ? `Due: ${exactDate} — Completed` : 'Completed')
                                              : dueDate ? `Due: ${exactDate}${urgencyText ? ` — ${urgencyText}` : ''}` : null;
                                            const dueColor = isCompleted ? '#22c55e'
                                              : !dueDate ? '#94a3b8'
                                              : isOverdue ? '#ef4444'
                                              : daysUntilDue === 0 ? '#f97316'
                                              : '#22c55e';
                                            return (
                                              <div key={ti}
                                                onClick={() => setTeamViewingTask({ task, record })}
                                                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.13)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                                style={{ background: '#fff7ed', borderRadius: 8, padding: '9px 11px', marginBottom: 6, border: '1px solid #fed7aa', borderLeft: '3px solid #f97316', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', cursor: 'pointer', transition: 'box-shadow 0.15s ease, transform 0.15s ease' }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', lineHeight: '1.35', marginBottom: 2 }}>
                                                    {task.title || 'Untitled task'}
                                                  </div>
                                                  {dueLine && (
                                                    <span style={{ fontSize: '12px', fontWeight: 700, color: dueColor }}>{dueLine}</span>
                                                  )}
                                                  {task.update_timestamp && (
                                                    <div style={{ fontSize: 11, color: '#92400e', marginTop: 4 }}>
                                                      <span style={{ color: '#b45309' }}>From:</span>{' '}
                                                      <span style={{ fontWeight: 600 }}>{fromName}</span>
                                                      {' · '}
                                                      <span style={{ color: '#b45309' }}>Updated:</span>{' '}
                                                      {fmtET(task.update_timestamp)}
                                                    </div>
                                                  )}
                                                </div>
                                                {task.action_items?.length > 0 && (
                                                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                                                    {task.action_items.filter(a => a.completed).length}/{task.action_items.length} subtasks
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="modal-footer" style={{ background: '#fafbfc', borderTop: '1px solid #f1f5f9', justifyContent: 'flex-end' }}>
                    <button onClick={() => setTeamUnpublishedModal(null)} style={{
                      padding: '8px 24px', borderRadius: '8px', border: '1.5px solid #e2e8f0',
                      background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                    }}>Close</button>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* View-only task card modal (from team unpublished modal) */}
      {teamViewingTask && (() => {
        const vt = teamViewingTask.task;
        const record = teamViewingTask.record;
        const isCompleted = vt.status === 'completed';
        const statusColor = isCompleted ? '#22c55e' : '#94a3b8';
        const statusBorder = isCompleted ? '#bbf7d0' : '#e2e8f0';
        const hasDraft = !!vt.update_timestamp;
        const dueDate = vt.due ? new Date(vt.due) : null;
        const exactDateLabel = dueDate
          ? dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : null;
        const listTitle = record?.task_list_title || '';
        const fromName = record?.display_name || record?.id || '';
        const etFmtV = new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/New_York', year: 'numeric', month: 'short',
          day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
        });
        const fmtETv = (iso) => { try { return etFmtV.format(new Date(iso)) + ' ET'; } catch { return iso; } };
        return (
          <>
            <div onClick={() => setTeamViewingTask(null)} style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
              zIndex: 1300, backdropFilter: 'blur(2px)',
            }} />
            <div style={{
              position: 'fixed', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1301, width: '420px', maxWidth: '94vw',
              backgroundColor: 'white', borderRadius: '14px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              border: `2px solid ${hasDraft ? '#f97316' : statusColor}`,
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
              {/* Header */}
              <div style={{ background: 'linear-gradient(145deg, #1a2540 0%, #243050 100%)', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontWeight: 700, fontSize: '13px', color: '#fff' }}>{listTitle}</div>
                {hasDraft && (
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(249,115,22,0.25)', color: '#fdba74', border: '1px solid rgba(249,115,22,0.35)', fontWeight: 700 }}>⏳ Draft</span>
                )}
              </div>
              {/* Body */}
              <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Title */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ width: 22, height: 22, borderRadius: 4, flexShrink: 0, marginTop: 2, border: `2px solid ${statusColor}`, background: isCompleted ? statusColor : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: isCompleted ? 'white' : '#d1d5db' }}>✓</div>
                  <div style={{ flex: 1, fontWeight: 700, fontSize: '16px', color: '#1e293b', lineHeight: '1.35', textDecoration: isCompleted ? 'line-through' : 'none', opacity: isCompleted ? 0.6 : 1 }}>{vt.title || 'Untitled task'}</div>
                </div>
                {/* Due date */}
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>Due Date</div>
                  <div style={{ padding: '9px 12px', borderRadius: '8px', border: `1.5px solid ${statusBorder}`, background: '#f8fafc', fontSize: '13px', color: exactDateLabel ? '#1e293b' : '#94a3b8' }}>{exactDateLabel || 'No due date'}</div>
                </div>
                {/* From / Updated */}
                {hasDraft && (
                  <div style={{ fontSize: 12, color: '#92400e' }}>
                    {fromName && <><span style={{ fontWeight: 600 }}>From: </span>{fromName} · </>}
                    <span style={{ fontWeight: 600 }}>Updated: </span>
                    {fmtETv(vt.update_timestamp)}
                  </div>
                )}
                {/* Action items */}
                {vt.action_items?.length > 0 && (
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>Action Items</div>
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 12px', border: `1px solid ${statusBorder}`, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {vt.action_items.map((item, i) => {
                        const expanded = expandedTeamActionItems.has(i);
                        const itemText = typeof item === 'object' ? item.text : item;
                        return (
                          <div
                            key={i}
                            onClick={() => setExpandedTeamActionItems(prev => {
                              const next = new Set(prev);
                              expanded ? next.delete(i) : next.add(i);
                              return next;
                            })}
                            style={{ display: 'grid', gridTemplateColumns: '14px 1fr', gap: '6px', alignItems: expanded ? 'flex-start' : 'center', cursor: 'pointer', borderRadius: '4px', padding: '2px 4px', margin: '0 -4px', transition: 'background 0.1s' }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            <span style={{ color: statusColor, fontSize: '12px', fontWeight: 700, textAlign: 'center', marginTop: expanded ? '2px' : 0, userSelect: 'none' }}>{expanded ? '⌄' : '›'}</span>
                            <span style={{ fontSize: '12px', color: '#1e293b', whiteSpace: expanded ? 'normal' : 'nowrap', overflow: expanded ? 'visible' : 'hidden', textOverflow: expanded ? 'clip' : 'ellipsis' }}>{itemText}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 20px', borderTop: '1px solid #f1f5f9', background: '#fafbfc' }}>
                <button onClick={() => setTeamViewingTask(null)} style={{ padding: '8px 24px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>Close</button>
              </div>
            </div>
          </>
        );
      })()}

      {/* Unpublished-changes navigation warning */}
      {unpublishedWarning && (
        <>
          <div onClick={() => setUnpublishedWarning(null)} style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1200, backdropFilter: 'blur(2px)'
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1201, width: '440px', maxWidth: '94vw',
            backgroundColor: 'white', borderRadius: '14px',
            padding: '28px 28px 24px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            border: '2px solid #f59e0b',
          }}>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}></div>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', margin: '0 0 10px' }}>
              Unshared Task Changes
            </h3>
            <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', margin: '0 0 22px' }}>
              You have unshared task changes. Your changes are saved for you, but they have not been shared with the team or calendars yet.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setUnpublishedWarning(null)}
                style={{ padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
              >Cancel</button>
              <button
                onClick={() => { const cb = unpublishedWarning.onConfirm; setUnpublishedWarning(null); cb(); }}
                style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#ef4444,#dc2626)', color: 'white', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
              >Leave Without Sharing</button>
            </div>
          </div>
        </>
      )}

      {/* Create Task List modal */}
      {showCreateModal && (
        <>
          <div onClick={() => setShowCreateModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1200 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 1201, background: 'white', borderRadius: '12px', padding: '28px', width: '420px', maxWidth: 'calc(100vw - 40px)', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#1e293b', margin: '0 0 20px' }}>Create Task List</h3>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                Task List Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={createTitle}
                onChange={e => setCreateTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateTaskList()}
                placeholder="e.g. 123 Main St – Closing Checklist"
                autoFocus
                style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                Property Address <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
              </label>
              <input
                type="text"
                value={createAddress}
                onChange={e => setCreateAddress(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateTaskList()}
                placeholder="Defaults to list name if blank"
                style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            {createError && <p style={{ color: '#ef4444', fontSize: '13px', margin: '-8px 0 14px' }}>{createError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setShowCreateModal(false)}
                style={{ padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleCreateTaskList} disabled={!createTitle.trim() || isCreating}
                style={{ padding: '8px 24px', borderRadius: '8px', border: 'none', background: createTitle.trim() && !isCreating ? 'linear-gradient(135deg,#1967d2,#1558b0)' : '#e2e8f0', color: createTitle.trim() && !isCreating ? 'white' : '#94a3b8', fontWeight: 700, fontSize: '13px', cursor: createTitle.trim() && !isCreating ? 'pointer' : 'not-allowed' }}>
                {isCreating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Publish recipient-selection modal */}
      {publishModalRecord && (() => {
        const { record, eligibleUsers } = publishModalRecord;
        const anySelected = publishSelectedEmails.size > 0;
        return (
          <>
            <div onClick={() => setPublishModalRecord(null)} style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1100, backdropFilter: 'blur(2px)'
            }} />
            <div style={{
              position: 'fixed', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1101, width: '400px', maxWidth: '94vw',
              backgroundColor: 'white', borderRadius: '14px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              border: '2px solid #3b82f6',
              display: 'flex', flexDirection: 'column', overflow: 'hidden'
            }}>
              <div style={{ background: 'linear-gradient(145deg, #1a2540 0%, #243050 100%)', padding: '14px 18px' }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#fff', marginBottom: '2px' }}>📅 Share Task List</div>
                <div style={{ fontSize: '11px', color: '#a4afbe' }}>{record.task_list_title}</div>
              </div>
              <div style={{ padding: '18px 20px' }}>
                <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 14px 0' }}>
                  Selected people will receive SmartClose task updates. Calendar sync will run only for connected calendars.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {eligibleUsers.map(member => {
                    const email = member.email?.toLowerCase();
                    const isLockedInfo = !!member.lockedInfo;
                    const isSelected = !isLockedInfo && publishSelectedEmails.has(email);
                    const displayName = [member.first_name, member.last_name].filter(Boolean).join(' ').trim() || member.display_name || member.name || email?.split('@')[0] || email;
                    const isLead = ['team_lead', 'solo_agent', 'single_agent', 'admin'].includes(member.role);
                    const isOwnerEntry = member.role === 'owner';
                    const isSelf = email === user?.email?.toLowerCase();
                    const isMember = !isSelf && !isLead && !isOwnerEntry;
                    const selfConnected = isSelf && selfCalendarStatus?.connected === true;
                    const providerLabel = selfCalendarStatus?.provider === 'google' ? 'Google'
                      : selfCalendarStatus?.provider === 'outlook' ? 'Outlook'
                      : selfCalendarStatus?.provider === 'icloud' ? 'iCloud'
                      : 'Calendar';
                    const isDisabled = isLockedInfo;

                    let rowBorder, rowBg, rowCursor;
                    if (isLockedInfo) {
                      rowBorder = '1.5px solid #bfdbfe'; rowBg = '#f0f7ff'; rowCursor = 'default';
                    } else if (selfConnected) {
                      rowBorder = `1.5px solid ${isSelected ? '#10b981' : '#d1fae5'}`; rowBg = isSelected ? '#f0fdf4' : '#fafffe'; rowCursor = 'pointer';
                    } else {
                      rowBorder = `1.5px solid ${isSelected ? '#3b82f6' : '#e2e8f0'}`; rowBg = isSelected ? '#eff6ff' : 'white'; rowCursor = 'pointer';
                    }

                    return (
                      <label key={email} style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '10px 12px', borderRadius: '8px',
                        border: rowBorder, background: rowBg, cursor: rowCursor,
                        transition: 'all 0.15s ease'
                      }}>
                        <input
                          type="checkbox"
                          checked={isLockedInfo ? true : isSelected}
                          disabled={isDisabled}
                          onChange={isDisabled ? undefined : () => setPublishSelectedEmails(prev => {
                            const next = new Set(prev);
                            isSelected ? next.delete(email) : next.add(email);
                            return next;
                          })}
                          style={{ width: '16px', height: '16px', accentColor: isLockedInfo ? '#93c5fd' : selfConnected ? '#10b981' : '#3b82f6', cursor: isDisabled ? 'default' : 'pointer' }}
                        />
                        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                            {isSelf ? 'You' : displayName}
                          </div>
                          {isSelf ? (
                            selfConnected
                              ? <div style={{ fontSize: '11px', color: '#059669' }}>{providerLabel} Calendar connected</div>
                              : <div style={{ fontSize: '11px', color: '#94a3b8' }}>No calendar connected · SmartClose update only</div>
                          ) : (
                            <div style={{ fontSize: '11px', color: '#64748b', wordBreak: 'break-all' }}>{email}</div>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                          {isSelf && (
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', whiteSpace: 'nowrap',
                              background: selfConnected ? '#d1fae5' : '#f1f5f9',
                              color: selfConnected ? '#065f46' : '#94a3b8' }}>
                              You
                            </span>
                          )}
                          {isOwnerEntry && (
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#fef3c7', color: '#92400e', whiteSpace: 'nowrap' }}>
                              Owner
                            </span>
                          )}
                          {isMember && (
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#f1f5f9', color: '#64748b', whiteSpace: 'nowrap' }}>
                              Member
                            </span>
                          )}
                          {isLead && (
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#dbeafe', color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                              Lead Agent
                            </span>
                          )}
                          {isLockedInfo && (
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#e0f2fe', color: '#0369a1', whiteSpace: 'nowrap' }}>
                              Will receive
                            </span>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '12px 20px', borderTop: '1px solid #f1f5f9', background: '#fafbfc' }}>
                <button onClick={() => setPublishModalRecord(null)} style={{
                  padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0',
                  background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer'
                }}>Cancel</button>
                <button onClick={handlePublishConfirm} disabled={!anySelected} style={{
                  padding: '8px 24px', borderRadius: '8px', border: 'none',
                  background: anySelected ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#e2e8f0',
                  color: anySelected ? 'white' : '#94a3b8', fontWeight: 700, fontSize: '13px',
                  cursor: anySelected ? 'pointer' : 'not-allowed',
                  boxShadow: anySelected ? '0 3px 10px rgba(59,130,246,0.4)' : 'none'
                }}>Share</button>
              </div>
            </div>
          </>
        );
      })()}

      {/* Discard confirmation modal */}
      {confirmDiscardRecord && (
        <div className="modal-backdrop show" style={{ display: 'block' }} />
      )}
      <div className={`modal ${confirmDiscardRecord ? 'show' : ''}`} style={{ display: confirmDiscardRecord ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px', width: '90%' }}>
          <div className="modal-content">
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">Discard unshared changes?</h5>
              <button type="button" className="btn-close" onClick={() => setConfirmDiscardRecord(null)} aria-label="Close" style={{ filter: 'invert(1)' }} />
            </div>
            <div className="modal-body" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif', padding: '24px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#334155' }}>
                <strong>"{confirmDiscardRecord?.task_list_title}"</strong> will be restored to the last shared version.
              </p>
              <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '13px', color: '#94a3b8' }}>
                This will remove your unshared edits and restore the task list to the last shared version. This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              <button onClick={() => setConfirmDiscardRecord(null)} style={{
                padding: '8px 20px', borderRadius: '6px', border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                color: '#fff', boxShadow: '0 3px 5px rgba(0,0,0,0.2)'
              }}>Cancel</button>
              <button onClick={handleConfirmDiscard} style={{
                padding: '8px 20px', borderRadius: '6px', border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: '#fff', boxShadow: '0 3px 5px rgba(239,68,68,0.3)'
              }}>Discard Changes</button>
            </div>
          </div>
        </div>
      </div>

      {/* Archive confirmation modal */}
      {confirmArchiveRecord && (
        <div className="modal-backdrop show" style={{ display: 'block' }} />
      )}
      <div className={`modal ${confirmArchiveRecord ? 'show' : ''}`} style={{ display: confirmArchiveRecord ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px', width: '90%' }}>
          <div className="modal-content">
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">Archive Task List</h5>
              <button type="button" className="btn-close" onClick={() => setConfirmArchiveRecord(null)} aria-label="Close" style={{ filter: 'invert(1)' }} />
            </div>
            <div className="modal-body" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif', padding: '24px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#334155' }}>
                Archive <strong>"{confirmArchiveRecord?.task_list_title}"</strong>?
              </p>
              <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '13px', color: '#94a3b8' }}>
                The task list will be hidden from the Active view. You can restore it at any time from the Archived tab.
              </p>
            </div>
            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              <Button variant="secondary" onClick={() => setConfirmArchiveRecord(null)} style={{
                background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', padding: '8px 20px'
              }}>Cancel</Button>
              <Button onClick={handleConfirmArchive} style={{
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                border: 'none', boxShadow: '0 3px 5px rgba(59,130,246,0.3)', padding: '8px 20px',
                color: 'white', fontWeight: 600
              }}>Archive</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {confirmDeleteRecord && (
        <div className="modal-backdrop show" style={{ display: 'block' }} />
      )}
      <div className={`modal ${confirmDeleteRecord ? 'show' : ''}`} style={{ display: confirmDeleteRecord ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px', width: '90%' }}>
          <div className="modal-content">
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">Delete Task List</h5>
              <button type="button" className="btn-close" onClick={() => setConfirmDeleteRecord(null)} aria-label="Close" style={{ filter: 'invert(1)' }} />
            </div>
            <div className="modal-body" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif', padding: '24px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#334155' }}>
                Are you sure you want to delete <strong>"{confirmDeleteRecord?.task_list_title}"</strong>?
              </p>
              <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '13px', color: '#94a3b8' }}>
                This will remove it from all calendars and cannot be undone.
              </p>
            </div>
            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              <Button variant="secondary" onClick={() => setConfirmDeleteRecord(null)} style={{
                background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', padding: '8px 20px'
              }}>Cancel</Button>
              <Button variant="danger" onClick={handleConfirmDelete} style={{
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                border: 'none', boxShadow: '0 3px 5px rgba(239,68,68,0.3)', padding: '8px 20px'
              }}>Delete</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Remove task confirmation modal */}
      {confirmRemoveTask && (
        <div className="modal-backdrop show" style={{ display: 'block' }} />
      )}
      <div className={`modal ${confirmRemoveTask ? 'show' : ''}`} style={{ display: confirmRemoveTask ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px', width: '90%' }}>
          <div className="modal-content">
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">Remove Task</h5>
              <button type="button" className="btn-close" onClick={() => setConfirmRemoveTask(null)} aria-label="Close" style={{ filter: 'invert(1)' }} />
            </div>
            <div className="modal-body" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif', padding: '24px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#334155' }}>
                Are you sure you want to remove{confirmRemoveTask?.taskTitle ? <> <strong>"{confirmRemoveTask.taskTitle}"</strong></> : ' this task'}?
              </p>
              <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '13px', color: '#94a3b8' }}>
                This will delete the task from the list. Share to update calendars.
              </p>
            </div>
            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              <Button variant="secondary" onClick={() => setConfirmRemoveTask(null)} style={{
                background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', padding: '8px 20px'
              }}>Cancel</Button>
              <Button variant="danger" onClick={() => { handleRemoveTask(confirmRemoveTask.record, confirmRemoveTask.taskIdx); setConfirmRemoveTask(null); }} style={{
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                border: 'none', boxShadow: '0 3px 5px rgba(239,68,68,0.3)', padding: '8px 20px'
              }}>Remove</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast notification */}
      {toast && (() => {
        if (!document.getElementById('cc-toast-anim')) {
          const s = document.createElement('style');
          s.id = 'cc-toast-anim';
          s.textContent = '@keyframes ccFadeInUp{from{opacity:0;transform:translate(-50%,12px)}to{opacity:1;transform:translate(-50%,0)}}';
          document.head.appendChild(s);
        }
        return (
          <div style={{
            position: 'fixed', bottom: '28px', left: '50%',
            transform: 'translateX(-50%)',
            background: '#1e293b', color: '#f8fafc',
            padding: '10px 22px', borderRadius: '10px',
            fontSize: '13px', fontWeight: 600,
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
            zIndex: 9999,
            animation: 'ccFadeInUp 0.25s ease-out',
            whiteSpace: 'nowrap',
          }}>
            {toast.message}
          </div>
        );
      })()}

    </div>
  );
};

export default CalendarConnection;
